import { useState, useEffect, useCallback, useRef } from "react";
import { householdRecordsApi, affiliateApi, vehiclesApi, vaultApi, workspacesApi, contractsApi } from "../services/api";
import ModuleHeader from "../components/shared/ModuleHeader";
import SavingNotice from "../components/shared/SavingNotice";
import CenteredModal from "../components/shared/CenteredModal";
import ConfirmModal from "../components/shared/ConfirmModal";
import { useAuth } from "../hooks/useAuth";
import useMinLoad from "../hooks/useMinLoad";
import BackButtonTrap from "../components/shared/BackButtonTrap";
import useFormPresentation from "../hooks/useFormPresentation";
import { useToastActions } from "../components/shared/Toast";

const IMG_VAULT = "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1400&q=75&auto=format&fit=crop";

// ── Presets ───────────────────────────────────────────────────────────────────

const VAULT_PRESETS = [
  { name: "Refrigerator",    category: "Appliances",   record_type: "appliance" },
  { name: "Washer",          category: "Appliances",   record_type: "appliance" },
  { name: "Dryer",           category: "Appliances",   record_type: "appliance" },
  { name: "Dishwasher",      category: "Appliances",   record_type: "appliance" },
  { name: "Microwave",       category: "Appliances",   record_type: "appliance" },
  { name: "Oven / Range",    category: "Appliances",   record_type: "appliance" },
  { name: "Air Conditioner", category: "Appliances",   record_type: "appliance" },
  { name: "HVAC System",     category: "Home Systems", record_type: "appliance" },
  { name: "Water Heater",    category: "Home Systems", record_type: "appliance" },
  { name: "Air Purifier",    category: "Appliances",   record_type: "appliance" },
  { name: "TV",              category: "Electronics",  record_type: "appliance" },
  { name: "Laptop",          category: "Electronics",  record_type: "appliance" },
  { name: "Desktop",         category: "Electronics",  record_type: "appliance" },
  { name: "Smartphone",      category: "Electronics",  record_type: "appliance" },
  { name: "Tablet",          category: "Electronics",  record_type: "appliance" },
  { name: "Game Console",    category: "Electronics",  record_type: "appliance" },
  { name: "Sound System",    category: "Electronics",  record_type: "appliance" },
  { name: "Roof",            category: "Home Systems", record_type: "manual"    },
  { name: "Windows",         category: "Home Systems", record_type: "manual"    },
  { name: "Garage Door",     category: "Home Systems", record_type: "appliance" },
  { name: "Security System", category: "Home Systems", record_type: "appliance" },
  { name: "Smoke Detectors", category: "Home Systems", record_type: "manual"    },
  { name: "Water Filter",    category: "Home Systems", record_type: "appliance" },
  { name: "Change AC filter", category: "Maintenance", record_type: "maintenance", amount: "Every 90 days", details: "Replace or clean the HVAC/AC filter." },
  { name: "Replace water filter", category: "Maintenance", record_type: "maintenance", amount: "Every 6 months", details: "Replace refrigerator or whole-home water filter." },
  { name: "Test smoke detectors", category: "Maintenance", record_type: "maintenance", amount: "Every 6 months", details: "Test alarms and replace batteries if needed." },
  { name: "Flush water heater", category: "Maintenance", record_type: "maintenance", amount: "Every 12 months", details: "Drain and flush sediment from the water heater." },
  { name: "Clean dryer vent", category: "Maintenance", record_type: "maintenance", amount: "Every 12 months", details: "Clean lint from dryer vent and duct." },
  { name: "Sofa / Couch",    category: "Furniture",    record_type: "receipt"   },
  { name: "Mattress",        category: "Furniture",    record_type: "receipt"   },
  { name: "Dining Table",    category: "Furniture",    record_type: "receipt"   },
  { name: "Office Chair",    category: "Furniture",    record_type: "receipt"   },
  { name: "Passport",        category: "Documents",    record_type: "document", details: "Save passport number, issuing country, and renewal notes." },
  { name: "Driver license",  category: "Documents",    record_type: "document", details: "Save ID number, issuing state, and renewal notes." },
  { name: "State ID",        category: "Documents",    record_type: "document", details: "Save ID number, issuing state, and renewal notes." },
  { name: "Visa / ESTA",     category: "Documents",    record_type: "document", details: "Save destination, authorization number, and renewal notes." },
  { name: "Certification",   category: "Documents",    record_type: "document", details: "Save issuer, credential number, and renewal requirements." },
];

const VAULT_TYPES = ["warranty", "receipt", "manual", "appliance", "maintenance", "document"];
const MAINTENANCE_PRESETS = VAULT_PRESETS.filter(p => p.record_type === "maintenance");
const DOCUMENT_PRESETS = VAULT_PRESETS.filter(p => p.record_type === "document");
const VAULT_PRESET_ICONS = {
  "Refrigerator": "🧊",
  "Fridge": "🧊",
  "Washer": "🫧",
  "Dryer": "🌀",
  "Dishwasher": "🍽️",
  "Microwave": "♨️",
  "Oven / Range": "🔥",
  "Air Conditioner": "❄️",
  "HVAC System": "🌬️",
  "Water Heater": "🚿",
  "Air Purifier": "🌫️",
  "TV": "📺",
  "Laptop": "💻",
  "Desktop": "🖥️",
  "Smartphone": "📱",
  "Tablet": "📲",
  "Game Console": "🎮",
  "Sound System": "🔊",
  "Roof": "🏠",
  "Windows": "🪟",
  "Garage Door": "🚪",
  "Security System": "🔒",
  "Smoke Detectors": "🚨",
  "Water Filter": "💧",
  "Sofa / Couch": "🛋️",
  "Mattress": "▰",
  "Dining Table": "▥",
  "Office Chair": "◒",
  "Change AC filter": "🔧",
  "Replace water filter": "💧",
  "Test smoke detectors": "🚨",
  "Flush water heater": "🚿",
  "Clean dryer vent": "🌀",
  "Passport": "🛂",
  "Driver license": "🪪",
  "State ID": "🪪",
  "Visa / ESTA": "🛃",
  "Certification": "🎓",
};
const VAULT_ICON_KEYWORDS = [
  [/passport/i, "🛂"],
  [/driver'?s? license|state id|real id|government id/i, "🪪"],
  [/visa|esta|eta|entry permit|travel authorization/i, "🛃"],
  [/certification|certificate|credential|license renewal/i, "🎓"],
  [/fridge|refrigerator|freezer|ice maker/i, "🧊"],
  [/washer|washing machine/i, "🫧"],
  [/dryer|drying machine/i, "🌀"],
  [/dishwasher/i, "🍽️"],
  [/microwave/i, "♨️"],
  [/oven|range|stove|cooktop/i, "🔥"],
  [/air conditioner|\bac\b|a\/c|hvac|furnace|heat pump/i, "❄️"],
  [/water heater|boiler/i, "🚿"],
  [/air purifier|humidifier|dehumidifier/i, "🌫️"],
  [/\btv\b|television|smart tv|monitor/i, "📺"],
  [/laptop|macbook|notebook/i, "💻"],
  [/desktop|computer|pc\b|imac/i, "🖥️"],
  [/phone|iphone|smartphone|android/i, "📱"],
  [/tablet|ipad/i, "📲"],
  [/game console|playstation|xbox|nintendo|switch/i, "🎮"],
  [/sound system|speaker|soundbar|receiver/i, "🔊"],
  [/roof|shingle/i, "🏠"],
  [/window/i, "🪟"],
  [/garage door|opener/i, "🚪"],
  [/security system|camera|doorbell|alarm/i, "🔒"],
  [/smoke detector|smoke alarm|detector/i, "🚨"],
  [/water filter|filter/i, "💧"],
  [/sofa|couch|loveseat/i, "🛋️"],
  [/mattress|bed frame|bed\b/i, "▰"],
  [/dining table|table/i, "▥"],
  [/office chair|chair/i, "◒"],
];
const VAULT_MOBILE_QUICK_PRESETS = VAULT_PRESETS.filter(p => ["Appliances", "Electronics", "Home Systems"].includes(p.category));
const VAULT_TYPE_TILES = [
  { key: "appliance", label: "Appliance", icon: "🏠" },
  { key: "warranty", label: "Warranty", icon: "🛡️" },
  { key: "receipt", label: "Receipt", icon: "🧾" },
  { key: "manual", label: "Manual", icon: "📘" },
  { key: "document", label: "Document", icon: "🪪" },
  { key: "maintenance", label: "Maintenance", icon: "🔧" },
];
const VAULT_CATEGORY_OPTIONS = ["Appliances", "Electronics", "Home Systems", "Maintenance", "Furniture", "Documents", "Other"];

const TYPE_CONFIG = {
  warranty:  { label: "Warranty",  color: "var(--hm-cyan)",  bg: "rgba(0,210,255,0.12)"   },
  receipt:   { label: "Receipt",   color: "var(--hm-amber)", bg: "rgba(255,209,102,0.14)" },
  manual:    { label: "Manual",    color: "#b79bff",         bg: "rgba(183,155,255,0.13)" },
  appliance: { label: "Appliance", color: "#7c3aed",         bg: "rgba(124,58,237,0.12)"  },
  maintenance: { label: "Maintenance", color: "#7c3aed", bg: "rgba(124,58,237,0.12)" },
  document: { label: "Document", color: "var(--hm-teal)", bg: "rgba(45,133,115,0.12)" },
};

const VAULT_GROUPS = [
  { id: "appliances", icon: "🏠", title: "Appliances & Home Systems", empty: "No appliances or home systems yet." },
  { id: "maintenance", icon: "🔧", title: "Maintenance", empty: "No maintenance tasks yet." },
  { id: "warranties", icon: "🛡️", title: "Warranties", empty: "No warranties tracked yet." },
  { id: "documents", icon: "🧾", title: "Receipts & Documents", empty: "No receipts or manuals yet." },
];

function vaultGroupId(record = {}) {
  const type = String(record.record_type || "").toLowerCase();
  const category = String(record.category || "").toLowerCase();
  const title = String(record.title || "").toLowerCase();
  const looksLikeTask = /^(change|replace|clean|flush|test|service|schedule|inspect|log)\b|tune-?up|cleaning|replacement|pest control|filter change|filter replacement|vent cleaning/.test(title);
  if (type === "maintenance" || category.includes("maintenance") || looksLikeTask) return "maintenance";
  if (type === "warranty" || category.includes("warranty")) return "warranties";
  if (["receipt", "manual", "document", "lease"].includes(type) || /receipt|manual|document|lease|insurance/.test(category)) return "documents";
  if (type === "appliance" || /home systems?|home_system|appliance|electronics|furniture/.test(category) || /hvac|furnace|water heater|water filter|washer|dryer|dishwasher|refrigerator|garage door|security system/.test(title)) return "appliances";
  return "documents";
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function warrantyStatus(due_date) {
  if (!due_date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(due_date + "T00:00:00");
  const days = Math.round((due - today) / 86400000);
  if (days < 0)   return { label: "Expired",      cls: "is-expired",  days };
  if (days <= 90) return { label: `${days}d left`, cls: "is-expiring", days };
  return               { label: "Active",          cls: "is-active",   days };
}

function dueStatus(due_date) {
  if (!due_date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(due_date + "T00:00:00");
  const days = Math.round((due - today) / 86400000);
  if (days < 0) return { label: "Overdue", cls: "is-expired", days };
  if (days <= 14) return { label: days === 0 ? "Due today" : `${days}d`, cls: "is-expiring", days };
  return { label: `${days}d`, cls: "is-active", days };
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function presetIcon(name) {
  const text = String(name || "").trim();
  if (VAULT_PRESET_ICONS[text]) return VAULT_PRESET_ICONS[text];
  const match = VAULT_ICON_KEYWORDS.find(([pattern]) => pattern.test(text));
  return match ? match[1] : "📦";
}

function vaultLookupQuery(record = {}) {
  return [record.title, record.model_number, record.category, record.retailer]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function vaultLookupLinks(record = {}) {
  const query = vaultLookupQuery(record) || "home appliance";
  const encoded = encodeURIComponent(query);
  return {
    manual: `https://www.google.com/search?q=${encoded}%20manual%20PDF`,
    recall: `https://www.cpsc.gov/Recalls?search_api_fulltext=${encoded}`,
  };
}

function parseRecallDetails(details) {
  if (!details) return null;
  if (typeof details === "object") return details;
  try { return JSON.parse(details); } catch { return { product_description: String(details) }; }
}

function vaultRecordPrice(record = {}) {
  const raw = record.purchase_price ?? record.amount ?? "";
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  const cleaned = String(raw).replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

const INLINE_VAULT_AI_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function readInlineVaultAiCache(cacheKey) {
  try {
    const parsed = JSON.parse(localStorage.getItem(cacheKey) || "{}") || {};
    const now = Date.now();
    let changed = false;
    const next = Object.fromEntries(Object.entries(parsed).filter(([, entry]) => {
      const time = Date.parse(entry?.cachedAt || "");
      const keep = Number.isFinite(time) && now - time <= INLINE_VAULT_AI_CACHE_TTL_MS;
      if (!keep) changed = true;
      return keep;
    }));
    if (changed) localStorage.setItem(cacheKey, JSON.stringify(next));
    return next;
  } catch {
    return {};
  }
}

const BLANK_FORM = { record_type: "warranty", title: "", category: "", purchase_date: "", retailer: "", model_number: "", due_date: "", amount: "", details: "", vehicle_id: "" };
const PORTRAIT_TABLET_QUERY = "(min-width: 769px) and (max-width: 1024px) and (orientation: portrait)";

function nextDateForInterval(interval, from = new Date()) {
  const text = String(interval || "").toLowerCase();
  const match = text.match(/[\d.]+/);
  if (!match) return "";
  const amount = Number(match[0]);
  if (!amount) return "";
  const next = new Date(from);
  if (text.includes("month") || text.includes("mo")) {
    next.setMonth(next.getMonth() + amount);
  } else if (text.includes("week")) {
    next.setDate(next.getDate() + Math.round(amount * 7));
  } else if (text.includes("year") || text.includes("yr")) {
    next.setFullYear(next.getFullYear() + amount);
  } else {
    next.setDate(next.getDate() + Math.round(amount));
  }
  return next.toISOString().slice(0, 10);
}

function automaticMaintenanceForVaultRecord(record = {}, existingRecords = []) {
  const text = `${record.title || record.name || ""} ${record.category || ""} ${record.record_type || ""}`.toLowerCase();
  const existingNames = new Set(existingRecords.map(row => String(row.title || row.name || "").trim().toLowerCase()));
  const tasks = [];
  const addTask = (title, amount, details) => {
    if (existingNames.has(title.toLowerCase())) return;
    tasks.push({
      record_type: "maintenance",
      title,
      category: "Maintenance",
      due_date: nextDateForInterval(amount),
      amount,
      details,
    });
  };

  if (/\bhvac\b|air conditioner|\bac\b|a\/c|furnace|heat pump/.test(text)) {
    addTask("Change AC filter", "Every 90 days", "Replace or clean the HVAC/AC filter.");
    addTask("HVAC annual tune-up", "Every 12 months", "Schedule a seasonal HVAC service check.");
  }
  if (/\bdryer\b/.test(text)) {
    addTask("Clean dryer vent", "Every 12 months", "Clean lint from dryer vent and duct.");
  }
  if (/water heater/.test(text)) {
    addTask("Flush water heater", "Every 12 months", "Drain and flush sediment from the water heater.");
  }
  if (/smoke detector|smoke alarm/.test(text)) {
    addTask("Test smoke detectors", "Every 6 months", "Test alarms and replace batteries if needed.");
  }
  if (/water filter/.test(text)) {
    addTask("Replace water filter", "Every 6 months", "Replace refrigerator or whole-home water filter.");
  }
  return tasks;
}

function isCompactSheetViewport() {
  if (typeof window === "undefined") return true;
  return window.innerWidth < 768 || window.matchMedia?.(PORTRAIT_TABLET_QUERY).matches;
}

function VaultMobileSheet({ open, title, children, onClose, showClose = true, className = "" }) {
  const sheetRef = useRef(null);
  const dragRef = useRef({ active: false, startY: 0, currentY: 0 });

  if (!open) return null;

  function startDrag(event) {
    if (!sheetRef.current) return;
    if (event.target?.closest?.(".hm-vault-mobile-sheet-body")) return;
    dragRef.current = { active: true, startY: event.clientY, currentY: 0 };
    sheetRef.current.style.transition = "none";
    sheetRef.current.setPointerCapture?.(event.pointerId);
  }

  function moveDrag(event) {
    if (!dragRef.current.active || !sheetRef.current) return;
    const delta = Math.max(0, event.clientY - dragRef.current.startY);
    dragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    sheetRef.current.style.transform = `translateY(${delta}px)`;
  }

  function endDrag(event) {
    if (!dragRef.current.active || !sheetRef.current) return;
    const delta = dragRef.current.currentY;
    dragRef.current = { active: false, startY: 0, currentY: 0 };
    sheetRef.current.releasePointerCapture?.(event.pointerId);
    if (delta > 86) {
      sheetRef.current.style.transition = "transform .2s cubic-bezier(0.4, 0, 1, 1)";
      sheetRef.current.style.transform = "translateY(100%)";
      window.setTimeout(onClose, 190);
      return;
    }
    sheetRef.current.style.transition = "transform .18s cubic-bezier(0.2, 0, 0, 1)";
    sheetRef.current.style.transform = "translateY(0)";
  }

  return (
    <div className="hm-vault-mobile-sheet-shell" role="dialog" aria-modal="true">
      <BackButtonTrap onClose={onClose} />
      <button type="button" className="hm-vault-mobile-sheet-scrim" onClick={onClose} aria-label="Close sheet" />
      <aside
        ref={sheetRef}
        className={`hm-vault-mobile-sheet hm-vault-mobile-sheet--teal${className ? ` ${className}` : ""}`}
        onPointerDownCapture={startDrag}
        onPointerMoveCapture={moveDrag}
        onPointerUpCapture={endDrag}
        onPointerCancelCapture={endDrag}
      >
        {showClose && (
          <div className="hm-vault-mobile-sheet-topbar">
            <div className="hm-vault-mobile-sheet-handle" />
            <button type="button" className="hm-vault-mobile-sheet-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        )}
        {!showClose && <div className="hm-vault-mobile-sheet-handle" />}
        <div className="hm-vault-mobile-sheet-head">
          <h2>{title}</h2>
        </div>
        <div className="hm-vault-mobile-sheet-body">
          {children}
        </div>
      </aside>
    </div>
  );
}

function renderInlineMarkdown(text = "") {
  return String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

function renderMarkdownText(text = "") {
  return String(text).split(/\n{2,}|\n/).map(line => line.trim()).filter(Boolean).map((line, index) => (
    <p key={index}>{renderInlineMarkdown(line)}</p>
  ));
}

function parseAiNotes(notes = "") {
  const text = String(notes || "").trim();
  if (!text) return [];
  const entries = [];
  const pattern = /\[(\d{4}-\d{2}-\d{2})\]\s*Q:\s*([\s\S]*?)(?:\s+-\s+|\n+)([\s\S]*?)(?=\n?\[\d{4}-\d{2}-\d{2}\]\s*Q:|$)/g;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    entries.push({
      date: formatDate(match[1]),
      question: match[2].trim(),
      answer: match[3].trim(),
    });
  }
  if (entries.length) return entries;
  return [{ date: "", question: "Saved AI note", answer: text }];
}

function copyVaultText(value) {
  if (!value || typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;
  navigator.clipboard.writeText(String(value)).catch(() => {});
}

function VaultCopyButton({ value, label = "Copy" }) {
  return <button type="button" className="hm-vault-copy-btn" onClick={(event) => { event.stopPropagation(); copyVaultText(value); }}>{label}</button>;
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "Unknown";
  if (typeof value === "number") return `$${Math.round(value).toLocaleString()}`;
  return String(value);
}

function addDaysToDate(iso, days) {
  if (!iso || days === null || days === undefined) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + Number(days));
  return d.toISOString().slice(0, 10);
}

function daysBetweenToday(iso) {
  if (!iso) return null;
  const target = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / 86400000);
}

function scoreColor(score = 0) {
  const value = Number(score || 0);
  const isHundredScale = value > 10;
  if (isHundredScale) {
    if (value >= 80) return "is-good";
    if (value >= 60) return "is-watch";
    return "is-risk";
  }
  if (value >= 8) return "is-good";
  if (value >= 5) return "is-watch";
  return "is-risk";
}

function VaultCrossModuleLog({ log }) {
  if (!log) return null;
  return (
    <div className="hm-vault-ai-crosslog">
      <span>↗</span>
      <div>
        <strong>{log.module || "Home Vault"}</strong>
        <p>{log.message || "Action logged"}</p>
      </div>
      {log.date && <em>{formatDate(log.date)}</em>}
    </div>
  );
}

function VaultAiActionButtons({ actions = [] }) {
  if (!actions.length) return null;
  return (
    <div className="hm-vault-ai-actions">
      {actions.map(action => (
        <button key={action.label} type="button" onClick={(event) => { event.stopPropagation(); action.onClick?.(event); }}>
          {action.label} ↗
        </button>
      ))}
    </div>
  );
}

function VaultKeepReplaceCard({ result = {}, item, onAction }) {
  const age = Number(result.age_years ?? item?.age_years ?? 0);
  const lifespan = Number(result.expected_lifespan_years || 12);
  const progress = lifespan ? Math.min(100, Math.max(0, Math.round((age / lifespan) * 100))) : 0;
  const verdict = result.verdict || "Keep";
  const score = Number(result.score || item?.health_score || 0);
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--score">
      <header className="hm-vault-ai-card-head">
        <span>{presetIcon(item?.title || item?.name)}</span>
        <div>
          <strong>{item?.title || item?.name || "Vault item"}</strong>
          <p>{[item?.retailer || item?.brand, item?.model_number || item?.model].filter(Boolean).join(" · ") || "Keep or replace analysis"}</p>
        </div>
        <em className={`hm-vault-ai-score ${scoreColor(score)}`}>{score || "?"}/10</em>
      </header>
      <section className="hm-vault-ai-progress">
        <div><span>Age vs lifespan</span><strong>{age || "?"} of {lifespan} yrs</strong></div>
        <i><b style={{ width: `${progress}%` }} /></i>
      </section>
      <section className="hm-vault-ai-cost-grid">
        <article><span>Energy/mo</span><strong>{formatMoney(result.monthly_energy_cost)}</strong></article>
        <article><span>Repair estimate</span><strong>{result.estimated_repair_cost_range || "Unknown"}</strong></article>
        <article><span>Replacement</span><strong>{result.estimated_replacement_cost_range || "Unknown"}</strong></article>
        <article><span>Break-even</span><strong>{result.break_even_months ? `${result.break_even_months} mo` : "Unknown"}</strong></article>
      </section>
      <div className={`hm-vault-ai-verdict hm-vault-ai-verdict--${String(verdict).toLowerCase()}`}>
        {verdict}
      </div>
      <p className="hm-vault-ai-explain">{result.reason || result.plain_english_reason || "No explanation returned yet."}</p>
      <VaultCrossModuleLog log={result.cross_module_log} />
      <VaultAiActionButtons actions={[
        { label: "Find service", onClick: () => onAction?.({ type: "find_service", item }) },
        { label: "Compare replacements", onClick: () => onAction?.({ type: "compare_replacements", item }) },
      ]} />
    </article>
  );
}

function VaultAuditCard({ result = {}, onAction }) {
  const score = Number(result.health_score || 0);
  const urgent = result.urgent_items || [];
  const dueSoon = result.due_within_6_months || result.items_due_within_6_months || [];
  const flags = (result.cross_module_flags || []).filter(flag => /home vault|vault/i.test(String(flag.module || flag.module_name || "")));
  const circleStyle = { "--score": score };
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--audit">
      <header className="hm-vault-ai-audit-head">
        <div className={`hm-vault-ai-ring ${scoreColor(score)}`} style={circleStyle}>
          <strong>{score || "?"}</strong>
          <span>health</span>
        </div>
        <div>
          <strong>Home health audit</strong>
          <p>{result.benchmark_text || "Benchmarked against your saved warranties, age, recall, and service data."}</p>
        </div>
      </header>
      <section className="hm-vault-ai-list">
        <h3>Urgent items</h3>
        {(urgent.length ? urgent : [{ name: "No urgent item returned", issue: "Run again after adding more vault data.", estimated_cost: "" }]).slice(0, 3).map((item, index) => (
          <article key={`${item.name}-${index}`}>
            <i className="is-red" />
            <div><strong>{item.name}</strong><p>{item.issue}</p></div>
            <em>{item.estimated_cost || item.estimated_cost_range || ""}</em>
          </article>
        ))}
      </section>
      <section className="hm-vault-ai-list">
        <h3>Due within 6 months</h3>
        {(dueSoon.length ? dueSoon : []).slice(0, 3).map((item, index) => (
          <article key={`${item.name}-${index}`}>
            <i className="is-amber" />
            <div><strong>{item.name}</strong><p>{item.issue || item.reason || "Upcoming service or warranty action."}</p></div>
            <em>{item.estimated_cost || ""}</em>
          </article>
        ))}
        {!dueSoon.length && <p className="hm-vault-ai-muted">No due-soon items returned.</p>}
      </section>
      <div className="hm-vault-ai-total"><span>Total estimated spend</span><strong>{formatMoney(result.total_estimated_spend)}</strong></div>
      {flags.length > 0 && (
        <div className="hm-vault-ai-callouts">
          {flags.map((flag, index) => (
            <article key={index}>
              <span>{flag.module || flag.module_name || "Insight"} ↗</span>
              <p>{flag.insight_text || flag.text || flag.message}</p>
            </article>
          ))}
        </div>
      )}
      <VaultAiActionButtons actions={[{ label: "Generate home sale export", onClick: () => onAction?.({ type: "generate_export" }) }]} />
    </article>
  );
}

function VaultAskApplianceCard({ result = {}, item, question, onAction }) {
  const causes = result.causes || result.most_likely_causes || [];
  const warranty = result.warranty_status || "unknown";
  const covered = String(warranty).toLowerCase().includes("active") || String(warranty).toLowerCase().includes("covered");
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--ask">
      <header className="hm-vault-ai-card-head">
        <span>{presetIcon(item?.title || item?.name)}</span>
        <div>
          <strong>{item?.title || item?.name || "Appliance"}</strong>
          <p>{[item?.retailer || item?.brand, item?.model_number || item?.model].filter(Boolean).join(" · ") || "Troubleshooting answer"}</p>
        </div>
      </header>
      <blockquote>{question}</blockquote>
      <section className="hm-vault-ai-causes">
        {causes.map((cause, index) => {
          const probability = Math.round(Number(cause.probability || 0) * 100);
          return (
            <article key={index}>
              <div>
                <strong>{index + 1}. {cause.description}</strong>
                <span>{cause.is_diy ? "DIY" : "Pro"} · {probability || "?"}% likely</span>
              </div>
              <i><b style={{ width: `${probability || 12}%` }} /></i>
            </article>
          );
        })}
      </section>
      <div className={`hm-vault-ai-warranty ${covered ? "is-good" : "is-risk"}`}>Warranty status: {warranty}</div>
      <div className="hm-vault-ai-total"><span>Estimated repair</span><strong>{result.estimated_repair_cost_range || "Unknown"}</strong></div>
      <p className="hm-vault-ai-explain">{result.call_pro_if || "Use the DIY steps only if you are comfortable and the item is safe to inspect."}</p>
      {Array.isArray(result.diy_steps) && result.diy_steps.length > 0 && (
        <ol className="hm-vault-ai-steps">
          {result.diy_steps.slice(0, 4).map((step, index) => <li key={index}>{step}</li>)}
        </ol>
      )}
      <VaultAiActionButtons actions={[
        { label: "Find repair service", onClick: () => onAction?.({ type: "find_service", item }) },
        ...(covered ? [{ label: "File claim", onClick: () => onAction?.({ type: "file_claim", item }) }] : []),
      ]} />
    </article>
  );
}

function VaultClaimCard({ result = {}, item }) {
  const summary = result.claim_summary || "";
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--claim">
      <header className="hm-vault-ai-card-head">
        <span>📄</span>
        <div>
          <strong>{item?.title || item?.name || result.item_name || "Claim summary"}</strong>
          <p>{item?.amount || item?.purchase_price ? `Original value ${item.amount || item.purchase_price}` : "Insurance-ready claim text"}</p>
        </div>
      </header>
      <section className="hm-vault-ai-copy-block">
        <VaultCopyButton value={summary} />
        <pre>{summary || "No claim summary returned yet."}</pre>
      </section>
      <section className="hm-vault-ai-checklist">
        <h3>Documentation checklist</h3>
        {(result.documentation_checklist || []).map((itemText, index) => <label key={index}><input type="checkbox" readOnly /> {itemText}</label>)}
      </section>
      <section className="hm-vault-ai-chips">
        <h3>Policy terms to reference</h3>
        {(result.policy_terms_to_reference || []).map(term => <span key={term}>{term}</span>)}
      </section>
      <button type="button" className="hm-vault-tool-primary" onClick={(event) => { event.stopPropagation(); copyVaultText(summary); }}>Copy full claim</button>
    </article>
  );
}

function VaultLeaseCard({ result = {}, lease }) {
  const pushbacks = result.pushback_counters || [];
  const leaseEnd = result.lease_end_date || lease?.lease_end || null;
  const monthlyRent = result.monthly_rent || lease?.monthly_rent || null;
  const daysUntil = result.days_until_renewal ?? daysBetweenToday(leaseEnd);
  const noticeDays = lease?.notice_days_required || 90;
  const noticeDate = addDaysToDate(leaseEnd, -noticeDays);
  const sixtyDay = addDaysToDate(leaseEnd, -60);
  const todayIso = new Date().toISOString().slice(0, 10);
  const timeline = (result.decision_timeline?.length ? result.decision_timeline : [
    { date: todayIso, title: "Start negotiation now", copy: "Landlords respond better when you reach out before the renewal number is anchored.", status: "Act now", tone: "red" },
    { date: sixtyDay, title: "Decision deadline if moving", copy: "After this point, finding, applying, and securing a new place becomes rushed.", status: "Hard deadline", tone: "amber" },
    { date: noticeDate, title: `${noticeDays}-day notice window`, copy: "This is the lease notice date to verify before deciding whether to renew or move.", status: noticeDate && daysBetweenToday(noticeDate) < 0 ? "Passed" : "Upcoming", tone: noticeDate && daysBetweenToday(noticeDate) < 0 ? "red" : "blue" },
    { date: leaseEnd, title: "Lease renewal date", copy: "New terms or month-to-month begins.", status: "Renewal", tone: "blue" },
  ]).filter(row => row.date || row.title);
  const decisionOptions = result.renew_vs_move?.length ? result.renew_vs_move : [
    {
      option: "Renew the lease",
      verdict: "Recommended",
      pros: ["No moving costs", "Known space, neighbors, and commute", "Strong tenant history is useful leverage"],
      cons: ["Rent may increase", "Locked in for another term"],
    },
    {
      option: "Move out",
      verdict: "Caution",
      pros: ["Chance to find better value or larger space", "Can reset terms if the current rent is too high"],
      cons: ["New deposit and moving costs", "Compressed timeline if the notice window is close or passed"],
    },
  ];
  const trueCost = result.true_cost?.length ? result.true_cost : [
    { label: "Moving truck + supplies", value: "-$800-1,500", tone: "red" },
    { label: "New deposit", value: lease?.deposit_amount ? `-$${Math.round(Number(lease.deposit_amount) || 0).toLocaleString()}+` : "-$2,000-5,000", tone: "red" },
    { label: "Deposit recovery", value: lease?.deposit_amount ? `+$${Math.round(Number(lease.deposit_amount) || 0).toLocaleString()} if full` : "Unknown", tone: "amber" },
    { label: "Break-even", value: "Verify with comps", tone: "amber" },
  ];
  const steps = result.negotiation_steps?.length ? result.negotiation_steps : [
    { title: "Opening - reach out this week", channel: "Email or text", script: result.opening_message || "Hi [landlord name], I am planning ahead for renewal and would love to discuss terms before the new rate is set.", why: "Contact before the renewal offer anchors the negotiation.", tone: "blue" },
    { title: "The negotiation conversation", channel: "Phone or in person", script: result.negotiation_script || "I have been a reliable tenant and want to stay, but I need the rate to be competitive with comparable units.", why: "Make them respond to market comps instead of your personal budget.", tone: "amber" },
    { title: "If price will not move", channel: "Any channel", script: (result.concession_asks || []).length ? `If the rent stays the same, would you consider ${result.concession_asks.join(" / ")}?` : "If the rent stays the same, would you consider one free month, parking, or an appliance upgrade?", why: "Concessions can be easier for landlords than reducing headline rent.", tone: "green" },
  ];
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--lease hm-vault-lease-advisor">
      <header className="hm-vault-ai-card-head hm-vault-lease-topbar">
        <span>🏢</span>
        <div>
          <strong>Lease renewal advisor</strong>
          <p>{monthlyRent ? `$${Math.round(Number(monthlyRent)).toLocaleString()}/mo` : "Rental"} · renews {leaseEnd ? formatDate(leaseEnd) : "date needed"}</p>
        </div>
        <em className={`hm-vault-lease-countdown${daysUntil !== null && daysUntil < noticeDays ? " is-urgent" : ""}`}>
          {result.countdown_badge || (daysUntil !== null ? `${Math.max(daysUntil, 0)} days` : "Set dates")}
        </em>
      </header>

      <section className="hm-vault-lease-section">
        <h3>Decision timeline</h3>
        <div className="hm-vault-lease-timeline">
          {timeline.map((node, index) => (
            <article key={`${node.title}-${index}`} className={`hm-vault-lease-node is-${node.tone || "blue"}`}>
              <i />
              <div>
                <span>{node.date ? formatDate(node.date) : "Date needed"}</span>
                <strong>{node.title}</strong>
                <p>{node.copy || node.subcopy}</p>
                {node.status && <em>{node.status}</em>}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="hm-vault-lease-section">
        <h3>Renew vs move</h3>
        <div className="hm-vault-lease-options">
          {decisionOptions.slice(0, 2).map((option, index) => (
            <article key={option.option || index}>
              <div className="hm-vault-lease-option-head">
                <strong>{option.option || option.title}</strong>
                <em className={String(option.verdict || "").toLowerCase().includes("caution") ? "is-caution" : "is-recommended"}>{option.verdict}</em>
              </div>
              {(option.pros || []).map((pro, proIndex) => <p key={`pro-${proIndex}`} className="is-pro">{pro}</p>)}
              {(option.cons || []).map((con, conIndex) => <p key={`con-${conIndex}`} className="is-con">{con}</p>)}
            </article>
          ))}
        </div>
      </section>

      <section className="hm-vault-lease-section">
        <h3>True cost of moving vs staying</h3>
        <div className="hm-vault-lease-costs">
          {trueCost.map((row, index) => (
            <div key={`${row.label}-${index}`}>
              <span>{row.label}</span>
              <strong className={`is-${row.tone || "neutral"}`}>{row.value}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="hm-vault-lease-section hm-vault-lease-script">
        <h3>Negotiation script</h3>
        {steps.map((step, index) => (
          <article key={`${step.title}-${index}`}>
            <div className="hm-vault-lease-script-head">
              <span>{index + 1}</span>
              <strong>{step.title}</strong>
              <em>{step.channel}</em>
            </div>
            <div className="hm-vault-ai-copy-block">
              <VaultCopyButton value={step.script} />
              <pre>{step.script}</pre>
            </div>
            {step.why && <p>{step.why}</p>}
          </article>
        ))}
      </section>

      <section className="hm-vault-ai-list hm-vault-lease-section">
        <h3>Pushback counters</h3>
        {pushbacks.map((row, index) => (
          <article key={index}>
            <i className="is-amber" />
            <div><strong>{row.their_line}</strong><p>{row.your_response}</p><VaultCopyButton value={row.your_response} label="Copy" /></div>
            <em>{row.tag}</em>
          </article>
        ))}
      </section>

      <section className="hm-vault-lease-section hm-vault-lease-intel">
        <h3>Negotiation intel</h3>
        <div className="hm-vault-ai-chips">
          <h3>Say these</h3>
          {(result.magic_words || []).map(word => <span key={word}>{word}</span>)}
        </div>
        <div className="hm-vault-ai-chips hm-vault-ai-chips--avoid">
          <h3>Avoid</h3>
          {(result.words_to_avoid || []).map(word => <span key={word}>{word}</span>)}
        </div>
      </section>
      <VaultCrossModuleLog log={result.cross_module_log} />
      <VaultAiActionButtons actions={[
        { label: "Find comps", onClick: () => window.dispatchEvent(new CustomEvent("hm_open_ask", { detail: { question: "Find comparable rental units and summarize what rent range I should use for this Home Vault lease negotiation.", context: { module: "vault", module_label: "Home Vault", surface: "Lease advisor" } } })) },
        { label: "Draft email", onClick: () => window.dispatchEvent(new CustomEvent("hm_open_ask", { detail: { question: "Draft a lease renewal negotiation email using my Home Vault lease advisor result.", context: { module: "vault", module_label: "Home Vault", surface: "Lease advisor" } } })) },
      ]} />
    </article>
  );
}

function VaultBudgetPlanCard({ result = {}, onSaveBudget, onAction }) {
  const items = result.budget_items || [];
  const months = Array.from(new Set(items.map(row => row.scheduled_month).filter(Boolean))).sort();
  const [activeMonth, setActiveMonth] = useState(months[0] || "");
  const visibleItems = activeMonth ? items.filter(row => row.scheduled_month === activeMonth) : items;
  const biggest = result.biggest_hit || {};
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--budget">
      <header className="hm-vault-ai-card-head">
        <span>💵</span>
        <div>
          <strong>12-month budget plan</strong>
          <p>Maintenance plan based on warranties, age, recalls, and service dates.</p>
        </div>
      </header>
      <div className="hm-vault-ai-money-range">
        <strong>{formatMoney(result.total_12mo_lo)}-{formatMoney(result.total_12mo_hi)}</strong>
        <span>next 12 months</span>
      </div>
      {(biggest.name || biggest.month) && (
        <div className="hm-vault-ai-banner is-amber">
          <strong>Biggest hit: {biggest.name || "Planned repair"}</strong>
          <span>{monthLabel(biggest.month)} · {formatMoney(biggest.cost_lo)}-{formatMoney(biggest.cost_hi)}</span>
        </div>
      )}
      <div className="hm-vault-month-strip">
        {months.map(month => (
          <button key={month} type="button" className={activeMonth === month ? "is-active" : ""} onClick={() => setActiveMonth(month)}>
            {monthLabel(month)}
          </button>
        ))}
      </div>
      <section className="hm-vault-ai-list">
        {(visibleItems.length ? visibleItems : items).slice(0, 8).map((row, index) => (
          <article key={`${row.name}-${row.scheduled_month}-${index}`}>
            <i className={row.urgency === "urgent" ? "is-red" : "is-amber"} />
            <div><strong>{row.name}</strong><p>{row.reason || monthLabel(row.scheduled_month)}</p></div>
            <em>{formatMoney(row.estimated_cost_lo)}-{formatMoney(row.estimated_cost_hi)}</em>
          </article>
        ))}
      </section>
      <VaultCrossModuleLog log={result.cross_module_log} />
      <VaultAiActionButtons actions={[
        { label: "Save to my budget", onClick: () => onSaveBudget?.(result) },
        { label: "Adjust months", onClick: () => onAction?.("Help me adjust this Home Vault 12-month maintenance budget by month and urgency.") },
      ]} />
    </article>
  );
}

function VaultImprovementRoiCard({ result = {}, improvement, onAction }) {
  const roi = Number(result.roi_pct || 0);
  const confidence = String(result.confidence || "medium").toLowerCase();
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--roi">
      <header className="hm-vault-ai-card-head">
        <span>📈</span>
        <div>
          <strong>{improvement?.name || "Improvement ROI"}</strong>
          <p>{formatMoney(improvement?.cost)} · {improvement?.completed_date ? formatDate(improvement.completed_date) : "date not saved"}</p>
        </div>
        <em className={`hm-vault-ai-score ${confidence === "high" ? "is-good" : confidence === "low" ? "is-risk" : "is-watch"}`}>{confidence}</em>
      </header>
      <div className={`hm-vault-ai-verdict hm-vault-ai-verdict--${confidence === "high" ? "keep" : confidence === "low" ? "replace" : "service"}`}>
        {roi}% estimated resale ROI
      </div>
      <section className="hm-vault-ai-progress">
        <div><span>ROI return</span><strong>{formatMoney(result.roi_return_est)}</strong></div>
        <i><b style={{ width: `${Math.min(100, Math.max(0, roi))}%`, background: "#7c3a1e" }} /></i>
      </section>
      <div className="hm-vault-ai-callouts">
        {(result.comparable_projects || []).slice(0, 2).map((row, index) => (
          <article key={index}>
            <span>{row.avg_roi_pct || "ROI"}%</span>
            <p>{row.name}</p>
          </article>
        ))}
      </div>
      <p className="hm-vault-ai-explain">{result.notes || "ROI estimates are directional and should be validated against local comps before listing."}</p>
      <VaultCrossModuleLog log={result.cross_module_log} />
      <VaultAiActionButtons actions={[{ label: "Add to home sale export", onClick: () => onAction?.({ type: "add_improvement_to_export", improvement }) }]} />
    </article>
  );
}

function VaultPresaleAuditCard({ result = {}, onAction }) {
  const score = Number(result.sale_readiness_score || 0);
  const value = result.estimated_value_add || {};
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--presale">
      <header className="hm-vault-ai-audit-head">
        <div className={`hm-vault-ai-ring ${scoreColor(score)}`} style={{ "--score": score }}>
          <strong>{score || "?"}</strong>
          <span>sale</span>
        </div>
        <div>
          <strong>Pre-sale home audit</strong>
          <p>{score >= 80 ? "Strong listing story with a few final checks." : "Tighten the items below before listing."}</p>
        </div>
      </header>
      <section className="hm-vault-ai-list">
        <h3>Fix before listing</h3>
        {(result.fix_before_listing || []).slice(0, 4).map((row, index) => (
          <article key={index}>
            <i className="is-red" />
            <div><strong>{row.name}</strong><p>{row.issue} · {row.price_impact}</p></div>
            <em>{formatMoney(row.estimated_cost)}</em>
          </article>
        ))}
      </section>
      <section className="hm-vault-ai-list">
        <h3>Must disclose</h3>
        {(result.disclose_to_buyer || []).slice(0, 4).map((row, index) => (
          <article key={index}>
            <i className="is-amber" />
            <div><strong>{row.name}</strong><p>{row.reason}</p></div>
          </article>
        ))}
      </section>
      <section className="hm-vault-ai-list">
        <h3>Selling points</h3>
        {(result.improvements_to_highlight || []).slice(0, 4).map((row, index) => (
          <article key={index}>
            <i className="is-green" />
            <div><strong>{row.name}</strong><p>{row.value_add}</p></div>
          </article>
        ))}
      </section>
      <div className="hm-vault-ai-total"><span>Estimated value add</span><strong>{formatMoney(value.lo)}-{formatMoney(value.hi)}</strong></div>
      <ol className="hm-vault-ai-steps">
        {(result.checklist || []).slice(0, 6).map((step, index) => <li key={index}>{step}</li>)}
      </ol>
      <VaultCrossModuleLog log={result.cross_module_log} />
      <VaultAiActionButtons actions={[{ label: "Generate home sale export", onClick: () => onAction?.({ type: "generate_export" }) }]} />
    </article>
  );
}

function VaultQuoteCheckCard({ result = {}, quoteContext = {}, item, onAction }) {
  const verdict = String(result.verdict || "fair").toLowerCase();
  const amount = Number(quoteContext.quote_amount || 0);
  const lo = Number(result.market_range_lo || 0);
  const hi = Number(result.market_range_hi || 0);
  const position = hi > lo ? Math.min(100, Math.max(0, ((amount - lo) / (hi - lo)) * 100)) : 50;
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--quote">
      <header className="hm-vault-ai-card-head">
        <span>🧾</span>
        <div>
          <strong>{quoteContext.service_type || "Quote check"}</strong>
          <p>{quoteContext.item_name || "General service"} · {quoteContext.quote_provider || "provider not saved"} · {formatMoney(amount)}</p>
        </div>
      </header>
      <div className={`hm-vault-ai-verdict hm-vault-ai-verdict--${verdict === "high" ? "replace" : verdict === "low" ? "service" : "keep"}`}>
        {verdict} quote · {result.recommendation || "review"}
      </div>
      <section className="hm-vault-ai-progress hm-vault-ai-quote-range">
        <div><span>Market range</span><strong>{formatMoney(lo)}-{formatMoney(hi)}</strong></div>
        <i><b style={{ width: `${position}%`, background: "#7c3a1e" }} /></i>
      </section>
      <section className="hm-vault-ai-copy-block">
        <VaultCopyButton value={result.negotiation_line || ""} />
        <pre>{result.negotiation_line || "No negotiation line returned."}</pre>
      </section>
      {(result.red_flags || []).length > 0 && (
        <section className="hm-vault-ai-list">
          <h3>Red flags</h3>
          {result.red_flags.map((flag, index) => (
            <article key={index}><i className="is-red" /><div><strong>{flag}</strong></div></article>
          ))}
        </section>
      )}
      <VaultCrossModuleLog log={result.cross_module_log} />
      <VaultAiActionButtons actions={[{ label: "Find alternatives", onClick: () => onAction?.({ type: "find_service", item, query: [quoteContext.service_type, quoteContext.item_name].filter(Boolean).join(" ") }) }]} />
    </article>
  );
}

function VaultAiResultCard({ result, item, question, lease, onAction, onSaveBudget, improvement }) {
  if (!result?.type || !result?.data) return null;
  if (result.type === "score") return <VaultKeepReplaceCard result={result.data} item={item} onAction={onAction} />;
  if (result.type === "audit") return <VaultAuditCard result={result.data} onAction={onAction} />;
  if (result.type === "ask") return <VaultAskApplianceCard result={result.data} item={item} question={question} onAction={onAction} />;
  if (result.type === "claim") return <VaultClaimCard result={result.data} item={item} />;
  if (result.type === "lease") return <VaultLeaseCard result={result.data} lease={lease} />;
  if (result.type === "budget_plan") return <VaultBudgetPlanCard result={result.data} onSaveBudget={onSaveBudget} onAction={onAction} />;
  if (result.type === "improvement_roi") return <VaultImprovementRoiCard result={result.data} improvement={improvement} onAction={onAction} />;
  if (result.type === "presale_audit") return <VaultPresaleAuditCard result={result.data} onAction={onAction} />;
  if (result.type === "quote_check") return <VaultQuoteCheckCard result={result.data} quoteContext={result.data.quote_context || {}} item={item} onAction={onAction} />;
  return null;
}

function vaultAiSheetTitle(type, item) {
  const titleByType = {
    score: "Keep / replace",
    audit: "Home health audit",
    ask: "Appliance answer",
    claim: "Claim summary",
    lease: "Lease advisor",
    budget_plan: "Maintenance budget",
    improvement_roi: "Improvement ROI",
    presale_audit: "Pre-sale audit",
    quote_check: "Quote check",
  };
  const label = titleByType[type] || "Home Vault AI";
  return item?.title && ["score", "ask", "claim"].includes(type) ? `${item.title} · ${label}` : label;
}

function VaultRenterGuideCard({ type, lease, insuranceRecord, moveLog }) {
  const insurance = parseInsuranceDetails(insuranceRecord);
  if (type === "insurance") {
    const hasPolicy = !!insuranceRecord;
    return (
      <article className="hm-vault-ai-card hm-vault-ai-card--renter-guide">
        <header className="hm-vault-ai-card-head">
          <span>▣</span>
          <div>
            <strong>Renter insurance explanation</strong>
            <p>{hasPolicy ? `${insurance.provider || insuranceRecord.title} · ${insurance.coverage || "coverage not saved"}` : "Add policy metadata to unlock stronger explanations."}</p>
          </div>
        </header>
        <div className={`hm-vault-ai-verdict hm-vault-ai-verdict--${hasPolicy ? "keep" : "service"}`}>
          {hasPolicy ? "Policy metadata ready" : "Policy details needed"}
        </div>
        <section className="hm-vault-ai-cost-grid">
          <article><span>Provider</span><strong>{insurance.provider || "Not saved"}</strong></article>
          <article><span>Property coverage</span><strong>{insurance.coverage || "Unknown"}</strong></article>
          <article><span>Liability</span><strong>{insurance.liability || "Not saved"}</strong></article>
          <article><span>Deductible</span><strong>{insurance.deductible || "Not saved"}</strong></article>
        </section>
        <p className="hm-vault-ai-explain">
          Use this sheet as a plain-English policy checkpoint before replacing device coverage or starting a claim. Verify exclusions, high-value item limits, roommates, and deductible math against the carrier policy.
        </p>
        <ol className="hm-vault-ai-steps">
          <li>Confirm the covered property amount and deductible.</li>
          <li>Check whether electronics, theft, water damage, and off-premise loss are limited.</li>
          <li>Save claim contact details and renewal date in Home Vault.</li>
        </ol>
      </article>
    );
  }

  const deposit = lease?.deposit_amount ? `$${Math.round(Number(lease.deposit_amount) || 0).toLocaleString()}` : "Unknown";
  const photos = Number(moveLog?.photos || 0);
  const damage = Number(moveLog?.damage || 0);
  const needsPhotos = photos < 5;
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--renter-guide">
      <header className="hm-vault-ai-card-head">
        <span>▤</span>
        <div>
          <strong>Move-in and deposit guidance</strong>
          <p>{deposit} deposit · {photos} photo metadata item{photos === 1 ? "" : "s"} logged</p>
        </div>
      </header>
      <div className={`hm-vault-ai-verdict hm-vault-ai-verdict--${needsPhotos ? "service" : "keep"}`}>
        {needsPhotos ? "Add more evidence metadata" : "Deposit record looks ready"}
      </div>
      <section className="hm-vault-ai-cost-grid">
        <article><span>Deposit at risk</span><strong>{deposit}</strong></article>
        <article><span>Photo entries</span><strong>{photos}</strong></article>
        <article><span>Damage items</span><strong>{damage}</strong></article>
        <article><span>Rooms noted</span><strong>{moveLog?.entries ? "Saved" : "Not saved"}</strong></article>
      </section>
      <p className="hm-vault-ai-explain">
        HouseMind stores metadata only. Capture clear room-by-room notes, dates, and counts so you can reconstruct the move-in condition without storing raw files in the vault.
      </p>
      <ol className="hm-vault-ai-steps">
        <li>Log every room, appliance, floor, wall, window, and fixture issue.</li>
        <li>Keep original photos outside Home Vault and record counts or filenames here.</li>
        <li>Send a dated summary to the landlord or property manager.</li>
      </ol>
    </article>
  );
}

function VaultWarrantyInlineCard({ result = {}, item, warrantyState }) {
  const status = warrantyState?.label || result.warranty_status || (item?.due_date ? formatDate(item.due_date) : "Not tracked");
  const covered = !/expired|not covered|unknown/i.test(String(status));
  return (
    <article className="hm-vault-ai-card hm-vault-ai-card--warranty">
      <header className="hm-vault-ai-card-head">
        <span>▣</span>
        <div>
          <strong>{item?.title || item?.name || "Warranty coverage"}</strong>
          <p>{[item?.purchase_date ? `Purchased ${formatDate(item.purchase_date)}` : "", item?.due_date ? `Expires ${formatDate(item.due_date)}` : ""].filter(Boolean).join(" · ") || "Coverage review"}</p>
        </div>
      </header>
      <div className={`hm-vault-ai-verdict hm-vault-ai-verdict--${covered ? "keep" : "replace"}`}>
        {covered ? "Coverage action available" : "Coverage needs review"}
      </div>
      <section className="hm-vault-ai-cost-grid">
        <article><span>Purchase date</span><strong>{item?.purchase_date ? formatDate(item.purchase_date) : "Not saved"}</strong></article>
        <article><span>Warranty expiry</span><strong>{item?.due_date ? formatDate(item.due_date) : "Not saved"}</strong></article>
        <article><span>Status</span><strong>{status}</strong></article>
        <article><span>Item value</span><strong>{item?.amount || "Unknown"}</strong></article>
      </section>
      <p className="hm-vault-ai-explain">{result.call_pro_if || result.reason || result.answer || "Review your saved receipt, warranty terms, and purchase date before contacting the provider."}</p>
      {Array.isArray(result.diy_steps) && result.diy_steps.length > 0 && (
        <ol className="hm-vault-ai-steps">
          {result.diy_steps.slice(0, 4).map((step, index) => <li key={index}>{step}</li>)}
        </ol>
      )}
    </article>
  );
}

function VaultActionSheetContent({ state, record, warrantyState, question, onRegenerate, onKeepCurrent, onQuoteChange, onQuoteSubmit, onAction }) {
  if (!state?.type) return null;
  const saved = ["score", "claim"].includes(state.type);
  const canRegenerate = !!state.data && !state.busy;
  const stop = (event, handler) => {
    event.stopPropagation();
    handler?.(event);
  };
  return (
    <section className="hm-vault-action-sheet-result" onClick={event => event.stopPropagation()}>
      {state.type === "quote_check" && state.showForm && (
        <form className="hm-vault-action-quote-form" onClick={event => event.stopPropagation()} onSubmit={(event) => stop(event, onQuoteSubmit)}>
          <label><span>Provider name</span><input value={state.quoteDraft?.provider || ""} onClick={event => event.stopPropagation()} onChange={event => onQuoteChange("provider", event.target.value)} placeholder="Company or pro" /></label>
          <label><span>Quote amount</span><input type="number" min="0" step="1" value={state.quoteDraft?.quote_amount || ""} onClick={event => event.stopPropagation()} onChange={event => onQuoteChange("quote_amount", event.target.value)} placeholder="650" /></label>
          <button type="submit" onClick={event => event.stopPropagation()} disabled={state.busy}>{state.busy ? "Checking..." : "Check quote"}</button>
        </form>
      )}
      {state.error && <div className="hm-inline-error">{state.error}</div>}
      {state.busy && !state.data && <div className="hm-vault-action-loading">Generating this Home Vault card...</div>}
      {state.data && (
        state.type === "warranty" ? (
          <VaultWarrantyInlineCard result={state.data} item={record} warrantyState={warrantyState} />
        ) : (
          <VaultAiResultCard result={{ type: state.type, data: state.data }} item={record} question={question} onAction={onAction} />
        )
      )}
      {state.data && (
        <footer className="hm-vault-action-result-foot">
          <span>{state.confirm ? "Saved report already exists. Re-running will replace it." : saved ? "Saved to vault · generated today" : "Last result kept on this device"}</span>
          {state.confirm ? (
            <>
              <button type="button" onClick={(event) => stop(event, onKeepCurrent)}>Keep current</button>
              <button type="button" className="is-primary" onClick={(event) => stop(event, onRegenerate)}>Replace report</button>
            </>
          ) : (
            canRegenerate && <button type="button" onClick={(event) => stop(event, onRegenerate)}>{state.type === "quote_check" ? "Check another" : "Re-run"}</button>
          )}
        </footer>
      )}
    </section>
  );
}

function vaultRecordHasQuoteContext(record = {}, groupId = "") {
  if (groupId === "maintenance" || record.record_type === "maintenance") return true;
  const fields = [
    record.title,
    record.category,
    record.details,
    record.notes,
    record.service_type,
    record.provider,
    record.quote_amount,
    record.estimate_amount,
    record.estimated_cost,
  ].filter(Boolean).join(" ");
  return /quote|estimate|bid|repair|service|contractor|install|replacement/i.test(fields);
}

function recordAgeText(record = {}) {
  if (!record.purchase_date) return "Unknown age";
  const years = Math.max(0, Math.round(((Date.now() - new Date(`${record.purchase_date}T00:00:00`).getTime()) / 31536000000) * 10) / 10);
  if (years < 1) return "Under 1 yr old";
  return `${years} yr${years === 1 ? "" : "s"} old`;
}

function VaultDetailGrid({ rows }) {
  return (
    <div className="hm-vault-expanded-grid">
      {rows.filter(row => row.value !== undefined && row.value !== null && row.value !== "").map(row => (
        <article key={row.label}>
          <span>{row.label}</span>
          <strong className={row.tone ? `is-${row.tone}` : ""}>{row.value}</strong>
        </article>
      ))}
    </div>
  );
}

function VaultItemAffiliateSection({ record, groupId = vaultGroupId(record) }) {
  const [links, setLinks] = useState(null);
  const price = vaultRecordPrice(record);
  const query = vaultLookupQuery(record);
  const showWarranty = ["appliances", "warranties"].includes(groupId) && !(price !== null && price < 50);
  const showRepair = groupId === "appliances" || groupId === "maintenance";
  const showParts = groupId === "appliances" || groupId === "maintenance";

  useEffect(() => {
    let active = true;
    affiliateApi.vaultLinks(query).then(data => {
      if (active) setLinks(data);
    }).catch(() => {
      if (active) setLinks(null);
    });
    return () => { active = false; };
  }, [query]);

  if (!links || (!showWarranty && !showRepair && !showParts)) return null;

  const groups = [
    {
      key: "warranty",
      title: "Warranty protection",
      show: showWarranty,
      options: [
        { key: "upsie", label: "Upsie", url: links.upsie, icon: "◇" },
        { key: "asurion", label: "Asurion", url: links.asurion, icon: "◇" },
      ],
    },
    {
      key: "repair",
      title: "Find a repair pro",
      show: showRepair,
      options: [
        { key: "taskrabbit", label: "TaskRabbit", url: links.taskrabbit, icon: "⌕" },
        ...(groupId === "maintenance" ? [] : [
          { key: "angi", label: "Angi", url: links.angi, icon: "⌕" },
          { key: "thumbtack", label: "Thumbtack", url: links.thumbtack, icon: "⌕" },
        ]),
      ],
    },
    {
      key: "parts",
      title: "Buy parts or replacement",
      show: showParts,
      options: [
        { key: "home_depot", label: "Home Depot", url: links.home_depot, icon: "▤" },
        { key: "lowes", label: "Lowe's", url: links.lowes, icon: "▤" },
      ],
    },
  ].map(group => ({
    ...group,
    options: group.options.filter(option => option.url),
  })).filter(group => group.show && group.options.length > 0);

  if (groups.length === 0) return null;

  return (
    <section className="hm-vault-item-affiliates" aria-label="Service and protection">
      <div className="hm-vault-item-affiliates-head">
        <strong>Service &amp; protection</strong>
        <span>affiliate links · we may earn a commission</span>
      </div>
      {groups.map(group => (
        <div className="hm-vault-item-affiliate-group" key={group.key}>
          <span>{group.title}</span>
          <div>
            {group.options.map(option => (
              <a
                key={option.key}
                href={option.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  event.stopPropagation();
                  affiliateApi.track(option.key, "home-vault", option.url).catch(() => {});
                }}
              >
                <i>{option.icon}</i>
                <strong>{option.label}</strong>
                <em>↗</em>
              </a>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function VaultExpandedDetails({ record, groupId, propertyType, lease, warrantyState, inlineState, isLoggingMaintenance = false, onScore, onClaim, onQuote, onLogDone, onBuyParts, onAttachReceipt, onEdit, onDelete, onGlobalAsk }) {
  const isLease = record.record_type === "lease" || record.category === "lease" || /lease/i.test(record.title || "");
  const isInsurance = record.category === "insurance" || /insurance|policy/i.test(`${record.title || ""} ${record.details || ""}`);
  const warrantyLabel = warrantyState?.label || (record.due_date ? formatDate(record.due_date) : "Not tracked");
  const canQuoteCheck = vaultRecordHasQuoteContext(record, groupId);
  const actionLabel = (type, label) => inlineState?.type === type && inlineState?.data ? "View result" : label;
  const stop = (event, handler) => {
    event.preventDefault();
    event.stopPropagation();
    handler?.();
  };

  if (propertyType === "renter" && isLease) {
    return (
      <div className="hm-vault-expanded-details hm-vault-expanded-details--renter">
        <VaultDetailGrid rows={[
          { label: "Rent", value: lease?.monthly_rent ? `$${Math.round(lease.monthly_rent).toLocaleString()}/mo` : record.amount || "Unknown" },
          { label: "Deposit", value: lease?.deposit_amount ? `$${Math.round(lease.deposit_amount).toLocaleString()}` : "Unknown" },
          { label: "Landlord", value: lease?.landlord_name || "Not saved" },
          { label: "Phone", value: lease?.landlord_phone || "Not saved" },
          { label: "Renews", value: lease?.lease_end ? formatDate(lease.lease_end) : record.due_date ? formatDate(record.due_date) : "Not set" },
          { label: "Notice", value: lease?.notice_days_required ? `${lease.notice_days_required} days` : "Unknown" },
          { label: "Pets", value: lease?.pets_allowed ? "Allowed" : "No pets / unknown" },
          { label: "Parking", value: lease?.parking_included ? "Included" : "Unknown" },
        ]} />
        <div className="hm-vault-expanded-actions">
          <button type="button" onClick={(event) => stop(event, onGlobalAsk)}>Negotiate ↗</button>
          <button type="button" onClick={(event) => stop(event, () => onGlobalAsk("Explain my tenant rights and renewal options from Home Vault."))}>Know rights ↗</button>
        </div>
      </div>
    );
  }

  if (propertyType === "renter" && isInsurance) {
    const insurance = parseInsuranceDetails(record);
    return (
      <div className="hm-vault-expanded-details hm-vault-expanded-details--renter">
        <VaultDetailGrid rows={[
          { label: "Provider", value: insurance.provider || "Not saved" },
          { label: "Policy", value: insurance.policyNumber || "Not saved" },
          { label: "Coverage", value: insurance.coverage || "Unknown" },
          { label: "Liability", value: insurance.liability || "Not saved" },
          { label: "Deductible", value: insurance.deductible || "Not saved" },
          { label: "Agent", value: insurance.agent || "Not saved" },
          { label: "Renews", value: insurance.renewalDate ? formatDate(insurance.renewalDate) : "Not set" },
          { label: "Status", value: warrantyLabel, tone: warrantyState?.cls === "is-expired" ? "risk" : "good" },
          { label: "Notes", value: insurance.notes || "No coverage notes saved" },
        ]} />
        <div className="hm-vault-expanded-actions">
          <button type="button" onClick={(event) => stop(event, () => onGlobalAsk("Explain my renter insurance coverage using this Home Vault policy."))}>Explain coverage ↗</button>
          <button type="button" onClick={(event) => stop(event, onClaim)}>Claim summary ↗</button>
        </div>
      </div>
    );
  }

  return (
    <div className="hm-vault-expanded-details">
      {record.has_open_recall && (
        <div className="hm-vault-expanded-recall">
          <strong>Recall open</strong>
          <p>{record.recall_details || "Review possible recall details before using or servicing this item."}</p>
        </div>
      )}
      <VaultDetailGrid rows={groupId === "maintenance" ? [
        { label: "Next due", value: warrantyLabel, tone: warrantyState?.cls === "is-expired" ? "risk" : warrantyState?.cls === "is-expiring" ? "watch" : "good" },
        { label: "Interval", value: record.amount || "Not set" },
        { label: "Last done", value: record.last_service_date ? formatDate(record.last_service_date) : "Never logged" },
        { label: "Notes", value: record.details || "None" },
      ] : [
        { label: "Purchased", value: record.purchase_date ? formatDate(record.purchase_date) : "Unknown" },
        { label: "Warranty", value: warrantyLabel, tone: warrantyState?.cls === "is-expired" ? "risk" : warrantyState?.cls === "is-expiring" ? "watch" : "good" },
        { label: "Purchase price", value: record.amount || "Unknown" },
        { label: "Age", value: recordAgeText(record) },
        { label: "Store / brand", value: record.retailer || "Unknown" },
        { label: "Model", value: record.model_number || "Not saved" },
      ]} />
      <VaultItemAffiliateSection record={record} groupId={groupId} />
      <div className={`hm-vault-expanded-action-strip${groupId === "maintenance" ? " hm-vault-maintenance-actions" : ""}`}>
        {groupId === "maintenance" ? (
          <>
            <button type="button" className="is-primary-action hm-vault-maintenance-done" onClick={(event) => stop(event, onLogDone)} disabled={isLoggingMaintenance}>
              <span>✓</span>
              <strong>{isLoggingMaintenance ? "Logging maintenance..." : "Mark maintenance done"}</strong>
            </button>
            <div className="hm-vault-maintenance-secondary">
              <button type="button" onClick={(event) => stop(event, onEdit)}><span>✎</span><strong>Set interval</strong></button>
              <button type="button" onClick={(event) => stop(event, onBuyParts)}><span>▤</span><strong>Buy parts</strong></button>
              {canQuoteCheck && <button type="button" className={inlineState?.type === "quote_check" ? "is-active-action" : ""} onClick={(event) => stop(event, onQuote)}><span>▤</span><strong>{actionLabel("quote_check", "Quote check")}</strong></button>}
            </div>
          </>
        ) : groupId === "warranties" ? (
          <>
            <button type="button" className={inlineState?.type === "claim" ? "is-active-action" : ""} onClick={(event) => stop(event, onClaim)}><span>▧</span><strong>{actionLabel("claim", "Claim")}</strong></button>
            <button type="button" onClick={(event) => stop(event, onAttachReceipt)}><span>⛶</span><strong>Attach item</strong></button>
            {canQuoteCheck && <button type="button" className={inlineState?.type === "quote_check" ? "is-active-action" : ""} onClick={(event) => stop(event, onQuote)}><span>▤</span><strong>{actionLabel("quote_check", "Quote check")}</strong></button>}
          </>
        ) : groupId === "documents" ? (
          <>
            <button type="button" className="is-primary-action" onClick={(event) => stop(event, onEdit)}><span>⌕</span><strong>Edit details</strong></button>
            <button type="button" onClick={(event) => stop(event, onDelete)}><span>▢</span><strong>Remove</strong></button>
            {canQuoteCheck && <button type="button" className={inlineState?.type === "quote_check" ? "is-active-action" : ""} onClick={(event) => stop(event, onQuote)}><span>▤</span><strong>{actionLabel("quote_check", "Quote check")}</strong></button>}
          </>
        ) : (
          <>
            <button type="button" className={inlineState?.type === "score" ? "is-active-action" : ""} onClick={(event) => stop(event, onScore)}><span>⌘</span><strong>{actionLabel("score", "Keep/replace")}</strong></button>
            {canQuoteCheck && <button type="button" className={inlineState?.type === "quote_check" ? "is-active-action" : ""} onClick={(event) => stop(event, onQuote)}><span>▤</span><strong>{actionLabel("quote_check", "Quote check")}</strong></button>}
            <button type="button" className={inlineState?.type === "claim" ? "is-active-action" : ""} onClick={(event) => stop(event, onClaim)}><span>▧</span><strong>{actionLabel("claim", "Claim")}</strong></button>
          </>
        )}
      </div>
    </div>
  );
}

function parseMoveInLog(record) {
  const text = `${record?.details || ""} ${record?.notes || ""}`;
  const photos = Number((text.match(/photos logged:\s*(\d+)/i) || text.match(/(\d+)\s+photo/i) || [])[1] || 0);
  const damage = Number((text.match(/damage items:\s*(\d+)/i) || text.match(/(\d+)\s+(?:damage|issue|item)/i) || [])[1] || 0);
  const entriesMatch = text.match(/entries:\s*([\s\S]*?)(?:\nnotes:|$)/i);
  const notesMatch = text.match(/notes:\s*([\s\S]*)/i);
  return {
    photos,
    damage,
    entries: entriesMatch ? entriesMatch[1].trim() : "",
    notes: notesMatch ? notesMatch[1].trim() : record?.details || "",
  };
}

function findRenterInsuranceRecord(records = []) {
  return records.find(record => record.category === "insurance" || /insurance|policy/i.test(`${record.title || ""} ${record.details || ""}`));
}

function parseInsuranceDetails(record) {
  const lines = String(record?.details || "").split(/\r?\n|·/).map(line => line.trim()).filter(Boolean);
  const data = {};
  lines.forEach(line => {
    const match = line.match(/^([^:]+):\s*(.+)$/);
    if (match) data[match[1].trim().toLowerCase()] = match[2].trim();
  });
  return {
    provider: record?.retailer || data.provider || "",
    policyNumber: record?.model_number || data.policy || data["policy number"] || "",
    coverage: record?.amount || data.coverage || data["personal property"] || "",
    liability: data.liability || "",
    deductible: data.deductible || "",
    agent: data.agent || data.contact || "",
    renewalDate: record?.due_date || "",
    notes: data.notes || "",
  };
}

function formatInsuranceDetails(draft) {
  return [
    `Provider: ${draft.provider || "Not saved"}`,
    `Policy: ${draft.policy_number || "Not saved"}`,
    `Coverage: ${draft.coverage_amount || "Not saved"}`,
    `Liability: ${draft.liability_amount || "Not saved"}`,
    `Deductible: ${draft.deductible || "Not saved"}`,
    `Agent: ${draft.agent_contact || "Not saved"}`,
    `Notes: ${draft.notes || "No coverage notes saved."}`,
  ].join("\n");
}

function RenterVaultDashboard({ records, lease, daysToRenewal, onEditLease, onEditInsurance, onMoveInLog, onLeaseAdvisor, onRenterGuide, onGlobalAsk }) {
  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("hm_vault_renter_open_sections") || "{}");
      return saved && typeof saved === "object" ? saved : {};
    } catch {
      return {};
    }
  });
  const insuranceRecord = findRenterInsuranceRecord(records);
  const moveInRecord = records.find(record => /move[-\s]?in|photo log|damage/i.test(`${record.title || ""} ${record.details || ""}`));
  const electronicsRecord = records.find(record => record.category === "electronics" || /macbook|laptop|phone|tablet|tv|computer/i.test(record.title || ""));
  const moveLog = parseMoveInLog(moveInRecord);
  const leaseEnd = lease?.lease_end ? formatDate(lease.lease_end) : "Add lease";
  const noticeDays = lease?.notice_days_required || 90;
  const noticeWindowOpen = daysToRenewal !== null && daysToRenewal <= noticeDays;
  const photoIncomplete = moveLog.photos < 5;
  const coverageAmount = insuranceRecord?.amount || "$45k";

  const alertRows = [
    {
      tone: "amber",
      icon: "⌛",
      title: noticeWindowOpen ? "Lease renewal decision window open" : "Lease renewal date needed",
      body: lease?.lease_end ? `Renews ${leaseEnd} · ${noticeDays}-day notice required.` : "Add lease end and notice requirement to track your decision window.",
      action: "Decide",
      onClick: onLeaseAdvisor,
    },
    {
      tone: photoIncomplete ? "red" : "green",
      icon: "📷",
      title: photoIncomplete ? "Move-in photos incomplete" : "Move-in photos logged",
      body: photoIncomplete ? `${Math.max(0, 5 - moveLog.photos)} more photo metadata items recommended to protect your deposit.` : `${moveLog.photos} photos and ${moveLog.damage} damage notes logged.`,
      action: photoIncomplete ? "Guide" : "Review",
      onClick: onMoveInLog,
    },
    {
      tone: insuranceRecord ? "blue" : "amber",
      icon: "🛡",
      title: insuranceRecord ? "Renter's insurance active" : "Renter's insurance not tracked",
      body: insuranceRecord ? `${insuranceRecord.title} · ${coverageAmount} property coverage.` : "Add your renter policy to compare device insurance and claim coverage.",
      action: insuranceRecord ? "Explain" : "Add",
      onClick: () => insuranceRecord ? onRenterGuide("insurance") : onEditInsurance(),
    },
  ];

  return (
    <section className="hm-vault-renter-dashboard">
      <VaultSectionCard
        sectionKey="renter_glance"
        storageKey="hm_vault_renter_open_sections"
        label="Rental at a glance"
        title="Lease and deposit snapshot"
        badge={daysToRenewal !== null ? `${Math.max(0, daysToRenewal)} days` : "Set dates"}
        openSections={openSections}
        setOpenSections={setOpenSections}
        className="hm-vault-renter-section-card"
      >
        <div className="hm-vault-renter-stat-grid">
          <button type="button" onClick={onEditLease}><span>Monthly rent</span><strong>{lease?.monthly_rent ? `$${Math.round(lease.monthly_rent).toLocaleString()}` : "Add rent"}</strong><p>{lease?.property_address || "Lease details"}</p></button>
          <button type="button" onClick={onMoveInLog}><span>Deposit at risk</span><strong>{lease?.deposit_amount ? `$${Math.round(lease.deposit_amount).toLocaleString()}` : "Unknown"}</strong><p>{photoIncomplete ? "photos incomplete" : "photo log ready"}</p></button>
          <button type="button" onClick={onEditInsurance}><span>Property covered</span><strong>{insuranceRecord ? coverageAmount : "Add policy"}</strong><p>{insuranceRecord ? "renter's insurance" : "not tracked"}</p></button>
          <button type="button" onClick={onEditLease}><span>Days to renewal</span><strong>{daysToRenewal !== null ? `${Math.max(0, daysToRenewal)} days` : "Set date"}</strong><p>{noticeDays}-day notice req.</p></button>
        </div>
      </VaultSectionCard>

      <VaultSectionCard
        sectionKey="renter_alerts"
        storageKey="hm_vault_renter_open_sections"
        label="Alerts"
        title="Lease, photos, and insurance"
        badge={`${alertRows.length} alerts`}
        openSections={openSections}
        setOpenSections={setOpenSections}
        className="hm-vault-renter-section-card"
      >
        <div className="hm-vault-renter-alerts">
          {alertRows.map(alert => (
            <article key={alert.title} className={`is-${alert.tone}`}>
              <span>{alert.icon}</span>
              <div><strong>{alert.title}</strong><p>{alert.body}</p></div>
              <button type="button" onClick={alert.onClick}>{alert.action} ↗</button>
            </article>
          ))}
        </div>
      </VaultSectionCard>

      <VaultSectionCard
        sectionKey="renter_insights"
        storageKey="hm_vault_renter_open_sections"
        label="Insights"
        title="Renter-specific checks"
        badge="3 checks"
        openSections={openSections}
        setOpenSections={setOpenSections}
        className="hm-vault-renter-section-card"
      >
        <div className="hm-vault-renter-insights">
          <article>
            <span>🔑</span>
            <div>
              <strong>Lease renewal {noticeDays} days out</strong>
              <p>{lease?.lease_end ? `Your lease renews ${leaseEnd}. Build the negotiation script before the notice window anchors your decision.` : "Add your lease dates to unlock renewal timing."}</p>
              <em>Decision point: renew or negotiate</em>
            </div>
            <button type="button" onClick={onLeaseAdvisor}>Lease advisor ↗</button>
          </article>
          <article>
            <span>🛡</span>
            <div>
              <strong>Renter's insurance coverage check</strong>
              <p>{insuranceRecord && electronicsRecord ? `${insuranceRecord.title} may cover ${electronicsRecord.title}; review your saved policy terms before filing or replacing coverage.` : "Add renter insurance and electronics to review coverage from saved Vault records."}</p>
              <em>{insuranceRecord && electronicsRecord ? "Vault coverage review" : "Coverage gap check"}</em>
            </div>
            <button type="button" onClick={() => onRenterGuide("insurance")}>Explain coverage ↗</button>
          </article>
          <article>
            <span>📷</span>
            <div>
              <strong>Move-in photo log {photoIncomplete ? "incomplete" : "ready"}</strong>
              <p>{photoIncomplete ? "Log at least 5 metadata entries for pre-existing damage and room photos. No raw files are stored." : "Your deposit protection metadata is recorded."}</p>
              <em>{photoIncomplete ? "Protects deposit" : "Deposit evidence ready"}</em>
            </div>
            <button type="button" onClick={() => onRenterGuide("move_in")}>Move-in guide ↗</button>
          </article>
        </div>
      </VaultSectionCard>

      <VaultSectionCard
        sectionKey="renter_ai"
        storageKey="hm_vault_renter_open_sections"
        label="AI helper"
        title="Landlord communication"
        badge="1 action"
        openSections={openSections}
        setOpenSections={setOpenSections}
        className="hm-vault-renter-section-card"
      >
        <section className="hm-vault-renter-ai-panel">
          <header>
            <div><span>✦</span><strong>Maintenance request writer</strong></div>
            <em>AI</em>
          </header>
          <div className="hm-vault-renter-ai-row">
            <strong>Use lease context</strong>
            <span>Drafts a clear request using saved lease, item, and issue context.</span>
            <em>Ready</em>
          </div>
          <button type="button" className="hm-vault-renter-ai-primary" onClick={() => onGlobalAsk("Write a landlord maintenance request using my Home Vault lease context.")}>Write landlord request ↗</button>
        </section>
      </VaultSectionCard>

      <VaultSectionCard
        sectionKey="renter_edit"
        storageKey="hm_vault_renter_open_sections"
        label="Manage"
        title="Lease, insurance, and photos"
        badge="3 actions"
        openSections={openSections}
        setOpenSections={setOpenSections}
        className="hm-vault-renter-section-card"
      >
        <div className="hm-vault-renter-dashboard-actions">
          <button type="button" onClick={onEditLease}>Edit lease details</button>
          <button type="button" onClick={onEditInsurance}>Edit insurance</button>
          <button type="button" onClick={onMoveInLog}>Log move-in photos</button>
        </div>
      </VaultSectionCard>
    </section>
  );
}

const SEASONS = ["spring", "summer", "fall", "winter"];

function monthLabel(iso) {
  if (!iso) return "Unscheduled";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function rowHighEstimate(row) {
  return Number(row.estimated_cost_hi || row.estimated_cost_lo || 0);
}

function VaultSectionCard({ sectionKey, label, title, badge, openSections, setOpenSections, wide = false, storageKey = "hm_vault_open_sections", className = "", children }) {
  const isOpen = !!openSections[sectionKey];
  function toggle() {
    setOpenSections(current => {
      const next = { ...current, [sectionKey]: !current[sectionKey] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  }
  return (
    <article className={`hm-vault-owner-card${wide ? " hm-vault-owner-card--wide" : ""}${className ? ` ${className}` : ""}${isOpen ? " is-open" : " is-collapsed"}`}>
      <button type="button" className="hm-vault-section-toggle" onClick={toggle} aria-expanded={isOpen}>
        <span>{label}</span>
        <strong>{title}</strong>
        {badge && <em>{badge}</em>}
        <b>{isOpen ? "⌃" : "⌄"}</b>
      </button>
      {isOpen && children}
    </article>
  );
}

function OwnerEnhancementPanel({
  records,
  budgetItems,
  improvements,
  contractors,
  suggestions,
  seasonalTasks,
  exportSummary,
  activeSeason,
  setActiveSeason,
  onGenerateBudget,
  onAddBudget,
  onEditBudget,
  onMarkBudgetDone,
  onSeasonalTaskDone,
  onPlanSeasonalTask,
  onEditImprovement,
  onEditContractor,
  onExport,
  onPresaleAudit,
  onClaim,
  claimItemId,
  setClaimItemId,
  claimDamage,
  setClaimDamage,
  claimOtherDraft,
  setClaimOtherDraft,
}) {
  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("hm_vault_open_sections") || "{}");
      return saved && typeof saved === "object" ? saved : {};
    } catch {
      return {};
    }
  });
  const [activeBudgetMonth, setActiveBudgetMonth] = useState("");
  const [dismissedImprovementSuggestions, setDismissedImprovementSuggestions] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("hm_vault_dismissed_improvement_suggestions") || "[]");
      return new Set(Array.isArray(saved) ? saved : []);
    } catch {
      return new Set();
    }
  });
  const monthGroups = budgetItems.reduce((acc, item) => {
    const key = item.scheduled_month || "unscheduled";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  const monthKeys = Object.keys(monthGroups).sort();
  useEffect(() => {
    if (!monthKeys.length) {
      setActiveBudgetMonth("");
      return;
    }
    if (!activeBudgetMonth || !monthGroups[activeBudgetMonth]) setActiveBudgetMonth(monthKeys[0]);
  }, [activeBudgetMonth, monthGroups, monthKeys]);
  const visibleBudgetItems = activeBudgetMonth ? (monthGroups[activeBudgetMonth] || []) : [];
  const activeMonthSubtotal = visibleBudgetItems.reduce((sum, item) => sum + rowHighEstimate(item), 0);
  const budgetTotal = budgetItems.reduce((sum, item) => sum + rowHighEstimate(item), 0);
  const improvementTotal = improvements.reduce((sum, row) => sum + Number(row.cost || 0), 0);
  const roiTotal = improvements.reduce((sum, row) => sum + Number(row.roi_return_est || 0), 0);
  const improvementNames = new Set(improvements.map(row => String(row.name || "").trim().toLowerCase()));
  const improvementCategories = ["hvac", "roof", "flooring", "kitchen", "bathroom", "electrical", "plumbing", "windows/doors", "windows", "doors"];
  const localImprovementSuggestions = records
    .filter(record => {
      const text = `${record.title || ""} ${record.category || ""} ${record.record_type || ""} ${record.details || ""}`.toLowerCase();
      const cost = Number(String(record.amount || record.purchase_price || "").replace(/[$,]/g, "")) || 0;
      if (improvementNames.has(String(record.title || "").trim().toLowerCase())) return false;
      return cost >= 1000 || improvementCategories.some(category => text.includes(category));
    })
    .slice(0, 3)
    .map(record => ({
      name: record.title,
      category: record.category || record.record_type || "home",
      completed_date: record.purchase_date || "",
      cost: record.amount || record.purchase_price || "",
      contractor: record.retailer || "",
      roi_pct: "",
      roi_return_est: "",
      notes: `Suggested from Home Vault record: ${record.title} (vault_item:${record.id})`,
      include_in_export: true,
      source_record_id: record.id,
    }));
  const contractorNames = new Set(contractors.map(row => String(row.name || "").trim().toLowerCase()));
  const localContractorSuggestions = improvements
    .filter(row => row.contractor && !contractorNames.has(String(row.contractor).trim().toLowerCase()))
    .slice(0, 3)
    .map(row => ({
      name: row.contractor,
      trade: row.category || "general",
      phone: "",
      email: "",
      rate: "",
      rating: "",
      times_used: 1,
      last_used_date: row.completed_date || "",
      notes: `Suggested from improvement: ${row.name}`,
    }));
  const improvementSuggestions = (suggestions?.improvements?.length ? suggestions.improvements : localImprovementSuggestions)
    .filter(row => {
      const key = String(row.source_record_id || row.name || "").trim().toLowerCase();
      return !dismissedImprovementSuggestions.has(key) && !improvementNames.has(String(row.name || "").trim().toLowerCase());
    })
    .slice(0, 3);
  const contractorSuggestions = (suggestions?.contractors?.length ? suggestions.contractors : localContractorSuggestions).filter(row => !contractorNames.has(String(row.name || "").trim().toLowerCase())).slice(0, 3);
  const claimIsOther = claimItemId === "__other__";
  const selectedClaimItem = claimIsOther ? null : (records.find(record => String(record.id) === String(claimItemId)) || records[0] || null);
  const claimCanGenerate = claimIsOther ? !!claimOtherDraft?.name?.trim() : !!selectedClaimItem;
  const sourceLabel = task => task.source === "budget" ? "Budgeted" : task.source === "item" ? "Service log" : "Suggested";
  const seasonalActionLabel = task => {
    if (task.status === "done") return "Done";
    if (task.source === "seasonal_rule") return "Plan";
    return "Log done";
  };
  const appendClaimDamage = text => {
    setClaimDamage(current => {
      const trimmed = (current || "").trim();
      return trimmed ? `${trimmed}; ${text}` : text;
    });
  };
  const dismissImprovementSuggestion = row => {
    const key = String(row.source_record_id || row.name || "").trim().toLowerCase();
    if (!key) return;
    setDismissedImprovementSuggestions(current => {
      const next = new Set(current);
      next.add(key);
      try { localStorage.setItem("hm_vault_dismissed_improvement_suggestions", JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };

  return (
    <section className="hm-vault-owner-enhancements">
      <div className="hm-vault-owner-grid">
        <VaultSectionCard sectionKey="budget" label="12-month budget" title="Maintenance budget planner" badge={`${budgetItems.length} tasks`} wide openSections={openSections} setOpenSections={setOpenSections}>
          <div className="hm-vault-owner-budget-total">
            <strong>{formatMoney(budgetTotal)}</strong>
            <span>estimated high-side maintenance over the next 12 months</span>
          </div>
          <div className="hm-vault-month-strip">
            {monthKeys.slice(0, 12).map(month => (
              <button key={month} type="button" className={activeBudgetMonth === month ? "is-active" : ""} onClick={() => setActiveBudgetMonth(month)}>
                {monthLabel(month)}
              </button>
            ))}
          </div>
          <div className="hm-vault-owner-list">
                {visibleBudgetItems.length > 0 && (
                  <div className="hm-vault-owner-subtotal">
                    <span>{monthLabel(activeBudgetMonth)} subtotal</span>
                    <strong>{formatMoney(activeMonthSubtotal)}</strong>
                  </div>
                )}
                {visibleBudgetItems.map(item => (
                  <div
                    key={item.id}
                    className="hm-vault-owner-row is-clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => onEditBudget(item)}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onEditBudget(item);
                      }
                    }}
                  >
                    <i className={`is-${item.status}`} />
                    <div><strong>{item.name}</strong><span>{monthLabel(item.scheduled_month)} · {formatMoney(item.estimated_cost_lo)}-{formatMoney(item.estimated_cost_hi)}</span></div>
                    <button type="button" onClick={event => { event.stopPropagation(); onMarkBudgetDone(item); }}>{item.status === "done" ? "Done" : "Mark done"}</button>
                  </div>
                ))}
                {budgetItems.length === 0 && <p className="hm-vault-owner-empty">No budget items yet. HouseMind will build this from saved systems, maintenance, warranties, and recalls.</p>}
                {budgetItems.length > 0 && visibleBudgetItems.length === 0 && <p className="hm-vault-owner-empty">No tasks in this month.</p>}
          </div>
          <button type="button" className="hm-vault-owner-secondary" onClick={onGenerateBudget}>Refresh plan</button>
          <button type="button" className="hm-vault-owner-secondary" onClick={onAddBudget}>+ Add budget item</button>
        </VaultSectionCard>

        <VaultSectionCard sectionKey="calendar" label="Seasonal" title="Maintenance calendar" badge={`${seasonalTasks.length} tasks`} wide openSections={openSections} setOpenSections={setOpenSections}>
          <div className="hm-vault-season-tabs">
            {SEASONS.map(season => (
              <button key={season} type="button" className={activeSeason === season ? "is-active" : ""} onClick={() => setActiveSeason(season)}>
                {season}
              </button>
            ))}
          </div>
          <div className="hm-vault-owner-list">
            {seasonalTasks.slice(0, 8).map(task => (
              <div key={task.id} className="hm-vault-owner-row hm-vault-seasonal-row">
                <i className={`is-${task.status}`} />
                <div>
                  <strong>{task.name}</strong>
                  <span>{sourceLabel(task)} · {task.status}{task.scheduled_month ? ` · ${monthLabel(task.scheduled_month)}` : ""}{task.estimated_cost_hi !== undefined && task.estimated_cost_hi !== null ? ` · ${formatMoney(task.estimated_cost_lo)}-${formatMoney(task.estimated_cost_hi)}` : ""}</span>
                </div>
                <button
                  type="button"
                  disabled={task.status === "done"}
                  onClick={() => task.source === "seasonal_rule" ? onPlanSeasonalTask(task) : onSeasonalTaskDone(task)}
                >
                  {seasonalActionLabel(task)}
                </button>
              </div>
            ))}
            {seasonalTasks.length === 0 && <p className="hm-vault-owner-empty">No seasonal tasks found for {activeSeason}.</p>}
          </div>
        </VaultSectionCard>

        <VaultSectionCard sectionKey="roi" label="ROI" title="Improvement tracker" badge={`${improvements.length} projects`} openSections={openSections} setOpenSections={setOpenSections}>
          <div className="hm-vault-owner-budget-total">
            <strong>{formatMoney(improvementTotal)}</strong>
            <span>{formatMoney(roiTotal)} estimated return at sale</span>
          </div>
          <div className="hm-vault-owner-list">
            {improvements.slice(0, 4).map(row => (
              <button key={row.id} type="button" className="hm-vault-owner-row is-clickable" onClick={() => onEditImprovement(row)}>
                <i />
                <div><strong>{row.name}</strong><span>{row.category || "project"} · ROI {row.roi_pct || 0}%</span></div>
                <em>{formatMoney(row.cost)}</em>
              </button>
            ))}
            {improvementSuggestions.map(row => (
              <div key={`suggested-${row.source_record_id || row.name}`} className="hm-vault-owner-row hm-vault-suggestion-row">
                <i className="is-upcoming" />
                <div><strong>{row.name}</strong><span>Suggested from vault · Add as improvement?</span></div>
                <em>{formatMoney(row.cost)}</em>
                <div className="hm-vault-suggestion-actions">
                  <button type="button" onClick={() => onEditImprovement(row)}>Add</button>
                  <button type="button" aria-label={`Dismiss ${row.name}`} onClick={() => dismissImprovementSuggestion(row)}>×</button>
                </div>
              </div>
            ))}
            {improvements.length === 0 && improvementSuggestions.length === 0 && <p className="hm-vault-owner-empty">Log upgrades to build your resale story.</p>}
          </div>
          <button type="button" className="hm-vault-owner-secondary" onClick={() => onEditImprovement(null)}>+ Add improvement</button>
        </VaultSectionCard>

        <VaultSectionCard sectionKey="export" label="Export" title="Home sale resume" badge={`${exportSummary?.item_count ?? records.length} items`} openSections={openSections} setOpenSections={setOpenSections}>
          <div className="hm-vault-export-preview">
            <span><b>{exportSummary?.item_count ?? records.length}</b> items</span>
            <span><b>{exportSummary?.service_record_count ?? 0}</b> service logs</span>
            <span><b>{exportSummary?.active_warranty_count ?? 0}</b> warranties</span>
            <span><b>{exportSummary?.improvement_count ?? improvements.length}</b> improvements</span>
          </div>
          <button type="button" className="hm-vault-owner-primary" onClick={onExport}>Generate export ↗</button>
          <button type="button" className="hm-vault-owner-secondary" onClick={onPresaleAudit} disabled={records.length === 0}>Pre-sale checklist ↗</button>
        </VaultSectionCard>

        <VaultSectionCard sectionKey="contractors" label="Pros" title="Trusted contractors" badge={`${contractors.length} saved`} openSections={openSections} setOpenSections={setOpenSections}>
          <div className="hm-vault-owner-list">
            {contractors.slice(0, 5).map(row => (
              <button key={row.id} type="button" className="hm-vault-owner-row is-clickable" onClick={() => onEditContractor(row)}>
                <i />
                <div><strong>{row.name}</strong><span>{row.trade || "service"} · {row.rate || "rate not saved"}</span></div>
                <em>{"★".repeat(row.rating || 0) || "No rating"}</em>
              </button>
            ))}
            {contractorSuggestions.map(row => (
              <button key={`suggested-${row.name}`} type="button" className="hm-vault-owner-row is-clickable" onClick={() => onEditContractor(row)}>
                <i className="is-upcoming" />
                <div><strong>{row.name}</strong><span>Suggested from improvement · tap to save</span></div>
                <em>New</em>
              </button>
            ))}
            {contractors.length === 0 && contractorSuggestions.length === 0 && <p className="hm-vault-owner-empty">Save providers you trust after service visits.</p>}
          </div>
          <button type="button" className="hm-vault-owner-secondary" onClick={() => onEditContractor(null)}>+ Add contractor</button>
        </VaultSectionCard>

        <VaultSectionCard sectionKey="claim" label="Emergency" title="Claim mode" badge="AI" openSections={openSections} setOpenSections={setOpenSections} className="hm-vault-owner-card--claim">
          <div className="hm-vault-claim-item">
            <div>
              <span>Item</span>
              <strong>{claimIsOther ? (claimOtherDraft?.name || "Other claim item") : (selectedClaimItem?.title || "Select an item")}</strong>
              <p>{claimIsOther ? "Not saved in Home Vault. Add enough detail for the claim summary." : selectedClaimItem ? `${selectedClaimItem.category || selectedClaimItem.record_type || "Vault item"} · ${selectedClaimItem.amount || selectedClaimItem.purchase_price || "value not saved"}` : "Choose the damaged item first."}</p>
            </div>
            <select className="hm-auth-input" value={claimItemId || selectedClaimItem?.id || ""} onChange={event => setClaimItemId(event.target.value)}>
              {records.map(record => <option key={record.id} value={record.id}>{record.title}</option>)}
              <option value="__other__">Other / not in vault</option>
            </select>
          </div>
          {claimIsOther && (
            <div className="hm-vault-claim-other-grid">
              <label><span>Item name</span><input className="hm-auth-input" value={claimOtherDraft.name} onChange={event => setClaimOtherDraft(draft => ({ ...draft, name: event.target.value }))} placeholder="Dining table, laptop, window..." /></label>
              <label><span>Type</span><input className="hm-auth-input" value={claimOtherDraft.category} onChange={event => setClaimOtherDraft(draft => ({ ...draft, category: event.target.value }))} placeholder="Furniture, electronics..." /></label>
              <label><span>Value</span><input className="hm-auth-input" value={claimOtherDraft.estimated_value} onChange={event => setClaimOtherDraft(draft => ({ ...draft, estimated_value: event.target.value }))} placeholder="Optional" /></label>
            </div>
          )}
          {selectedClaimItem?.has_open_recall && <p className="hm-vault-claim-recall">Recall open: include this in the claim context.</p>}
          <label className="hm-vault-claim-damage">
            <span>Damage</span>
            <textarea className="hm-auth-input" rows={2} value={claimDamage} onChange={event => setClaimDamage(event.target.value)} placeholder="What happened, when, and what was damaged?" />
          </label>
          <div className="hm-vault-claim-presets">
            {["Water leak", "Power surge", "Storm damage", "Fire/smoke", "Theft", "Accidental breakage"].map(label => (
              <button key={label} type="button" onClick={() => appendClaimDamage(label)}>{label}</button>
            ))}
          </div>
          <div className="hm-vault-claim-docs">
            <span>Documentation</span>
            <div>
              <strong>Receipt</strong>
              <strong>Warranty</strong>
              <strong>Service history</strong>
            </div>
            <p>No raw files stored. Claim summary uses saved metadata only.</p>
          </div>
          <button type="button" className="hm-vault-owner-primary hm-vault-claim-submit" onClick={() => onClaim(selectedClaimItem, claimDamage, claimIsOther ? claimOtherDraft : null)} disabled={!claimCanGenerate}>
            Generate claim summary ↗
          </button>
        </VaultSectionCard>
      </div>
    </section>
  );
}

function VaultAddDrawer({ open, form, busy, error, vehicles = [], setField, applyPreset, onClose, onSubmit }) {
  const [mobileMode, setMobileMode] = useState("quick");
  const [mobileStep, setMobileStep] = useState(1);
  const [attemptedMobileSave, setAttemptedMobileSave] = useState(false);
  const presentation = useFormPresentation();

  useEffect(() => {
    if (open) {
      setMobileMode("quick");
      setMobileStep(1);
      setAttemptedMobileSave(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || presentation === "modal") return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [open, presentation]);

  if (!open) return null;

  const typeLabel = TYPE_CONFIG[form.record_type]?.label || "Record";
  function chooseDesktopType(type) {
    const category = type === "maintenance" ? "Maintenance" : type === "appliance" ? "Appliances" : type === "document" ? "Documents" : form.category;
    setField("record_type", type);
    if (category) setField("category", category);
  }

  function chooseMobileType(type) {
    setAttemptedMobileSave(false);
    chooseDesktopType(type);
    if (type === "receipt") {
      setField("category", "");
      setField("model_number", "");
      setField("due_date", "");
    } else if (type === "manual") {
      setField("category", "");
      setField("purchase_date", "");
      setField("due_date", "");
      setField("amount", "");
    } else if (type === "maintenance") {
      setField("category", "Maintenance");
      setField("retailer", "");
      setField("model_number", "");
      setField("purchase_date", "");
      setField("amount", "Every 90 days");
      setField("due_date", nextDateForInterval("Every 90 days"));
    } else if (type === "document") {
      setField("category", "Documents");
      setField("amount", "");
    }
  }

  function openManual() {
    setMobileMode("manual");
    setMobileStep(1);
  }

  function titleLabel() {
    if (form.record_type === "maintenance") return "Task name";
    if (form.record_type === "document") return "Document name";
    if (form.record_type === "receipt" || form.record_type === "manual") return "Item name";
    return "Name";
  }

  function titlePlaceholder() {
    if (form.record_type === "maintenance") return "e.g. Change AC filter";
    if (form.record_type === "document") return "e.g. Passport, driver license, certification";
    if (form.record_type === "manual") return "e.g. Refrigerator manual";
    if (form.record_type === "receipt") return "e.g. Air purifier";
    return "e.g. Refrigerator, Samsung TV, AC filter...";
  }

  function partyLabel() {
    if (form.record_type === "document") return "Issuer";
    if (form.record_type === "appliance") return "Brand";
    return "Retailer";
  }

  function partyPlaceholder() {
    if (form.record_type === "document") return "State Dept., DMV, issuer...";
    if (form.record_type === "appliance") return "Samsung, LG, GE...";
    return "Best Buy, Lowe's...";
  }

  function identifierLabel() {
    return form.record_type === "document" ? "Document / ID number" : "Model number";
  }

  function identifierPlaceholder() {
    return form.record_type === "document" ? "Passport, ID, or credential number" : "Model or serial";
  }

  function startDateLabel() {
    return form.record_type === "document" ? "Issue date" : "Purchase date";
  }

  function endDateLabel() {
    if (form.record_type === "maintenance") return "Next reminder";
    if (form.record_type === "document") return "Expiry date";
    if (form.record_type === "receipt") return "Return or coverage date";
    return "Warranty end date";
  }

  function notesPlaceholder() {
    if (form.record_type === "maintenance") return "Filter size, interval, service notes...";
    if (form.record_type === "document") return "Renewal notes, issuing country/state, restrictions...";
    return "Warranty terms, serial number, service notes...";
  }

  function handleMobileSubmit(event) {
    setAttemptedMobileSave(true);
    if (form.record_type === "warranty" && !form.due_date) {
      event.preventDefault();
      return;
    }
    onSubmit(event);
  }

  function setMaintenanceInterval(value) {
    setField("amount", value);
    if (value) setField("due_date", nextDateForInterval(value));
  }

  if (presentation === "modal") {
    return (
      <CenteredModal title="Add to vault" onClose={onClose}>
          <p style={{ marginTop: -8, marginBottom: 16, color: "var(--text-muted)" }}>Save an appliance, manual, receipt, warranty, or reminder.</p>
          {error && <div className="hm-inline-error hm-vault-add-error">{error}</div>}

          <form id="hm-vault-add-drawer-form" onSubmit={onSubmit} className="hm-vault-add-desktop-form">
            <section className="hm-vault-add-desktop-types" aria-label="Record type">
              <div>
                <strong>Record type</strong>
                <span>Choose what you are saving.</span>
              </div>
              <div className="hm-vault-add-type-picker hm-vault-add-type-picker--desktop">
                {VAULT_TYPE_TILES.map(tile => (
                  <button key={tile.key} type="button" className={form.record_type === tile.key ? "is-selected" : ""} onClick={() => chooseDesktopType(tile.key)}>
                    <span>{tile.icon}</span>
                    <strong>{tile.label}</strong>
                  </button>
                ))}
              </div>
            </section>

            <section className="hm-vault-add-desktop-fields">
              <div className="hm-util-form-section hm-util-ledger-span">
                <strong>{typeLabel} details</strong>
                <span>Add what you know now. You can edit the record later.</span>
              </div>
              <label className="hm-util-ledger-span">
                <span>{titleLabel()}</span>
                <input className="hm-auth-input" placeholder={titlePlaceholder()} value={form.title} onChange={e => setField("title", e.target.value)} autoFocus />
              </label>
              <label>
                <span>Category</span>
                <select className="hm-auth-input" value={form.category} onChange={e => setField("category", e.target.value)}>
                  <option value="">Choose category</option>
                  {VAULT_CATEGORY_OPTIONS.map(category => <option key={category} value={category}>{category}</option>)}
                </select>
              </label>
              {vehicles.length > 0 && (
                <label>
                  <span>Linked vehicle</span>
                  <select className="hm-auth-input" value={form.vehicle_id || ""} onChange={e => setField("vehicle_id", e.target.value)}>
                    <option value="">No vehicle link</option>
                    {vehicles.map(vehicle => <option key={vehicle.id} value={vehicle.id}>{vehicle.display_name}</option>)}
                  </select>
                </label>
              )}
              {form.record_type !== "maintenance" && (
                <>
                  <label>
                    <span>{partyLabel()}</span>
                    <input className="hm-auth-input" placeholder={partyPlaceholder()} value={form.retailer} onChange={e => setField("retailer", e.target.value)} />
                  </label>
                  <label>
                    <span>{identifierLabel()}</span>
                    <input className="hm-auth-input" placeholder={identifierPlaceholder()} value={form.model_number} onChange={e => setField("model_number", e.target.value)} />
                  </label>
                  <label>
                    <span>{startDateLabel()}</span>
                    <input type="date" className="hm-auth-input" value={form.purchase_date} onChange={e => setField("purchase_date", e.target.value)} />
                  </label>
                  <label>
                    <span>{endDateLabel()}</span>
                    <input type="date" className="hm-auth-input" value={form.due_date} onChange={e => setField("due_date", e.target.value)} />
                  </label>
                  {form.record_type !== "document" && (
                    <label>
                      <span>Amount paid</span>
                      <input className="hm-auth-input" placeholder="$499" value={form.amount} onChange={e => setField("amount", e.target.value)} />
                    </label>
                  )}
                </>
              )}
              {form.record_type === "maintenance" && (
                <>
                  <label>
                    <span>Service interval</span>
                    <select className="hm-auth-input" value={form.amount} onChange={e => setMaintenanceInterval(e.target.value)}>
                      <option value="">One-time / no repeat</option>
                      <option value="Every 30 days">Monthly (30 days)</option>
                      <option value="Every 90 days">Every 3 months</option>
                      <option value="Every 6 months">Every 6 months</option>
                      <option value="Every 12 months">Yearly</option>
                    </select>
                  </label>
                  <label>
                    <span>Next reminder</span>
                    <input type="date" className="hm-auth-input" value={form.due_date} onChange={e => setField("due_date", e.target.value)} />
                  </label>
                </>
              )}
              <label className="hm-util-ledger-span">
                <span>Notes</span>
                <textarea className="hm-auth-input" rows={4} placeholder={notesPlaceholder()} value={form.details} onChange={e => setField("details", e.target.value)} />
              </label>
            </section>
          </form>

          <div className="hm-util-drawer-footer">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" form="hm-vault-add-drawer-form" className="is-primary" disabled={busy || !form.title.trim()}>{busy ? "Saving..." : "Save record"}</button>
          </div>
      </CenteredModal>
    );
  }

  return (
    <div className="hm-vault-add-shell" role="dialog" aria-modal="true" aria-label="Add to vault">
      <BackButtonTrap onClose={onClose} />
      <button className="hm-vault-add-scrim" type="button" onClick={onClose} aria-label="Close add record" />
      <aside
        className="hm-vault-add-drawer"
      >
        <div className="hm-sheet-handle hm-vault-add-handle" aria-hidden="true" />
        <div className="hm-util-drawer-head hm-util-drawer-head--compact hm-vault-mobile-add-head">
          <div>
            <h2>Add to vault</h2>
            <p>{mobileMode === "manual" ? (mobileStep === 1 ? "Choose record type" : `Add ${typeLabel.toLowerCase()} details`) : "Quick add common home records"}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close add record">×</button>
        </div>

        <div className="hm-vault-add-tabs" role="tablist">
          <button type="button" className={mobileMode === "quick" ? "is-active" : ""} onClick={() => setMobileMode("quick")}>Quick add</button>
          <button type="button" className={mobileMode === "manual" ? "is-active" : ""} onClick={openManual}>Manual entry</button>
        </div>

        <div className="hm-vault-add-body">
          {error && <div className="hm-inline-error hm-vault-add-error">{error}</div>}

          {mobileMode === "quick" && (
            <div className="hm-vault-mobile-quick">
              <strong>Common items</strong>
              <div className="hm-vault-mobile-chip-grid">
                {VAULT_MOBILE_QUICK_PRESETS.map(p => (
                  <button key={p.name} type="button" onClick={() => { applyPreset(p); setMobileMode("manual"); setMobileStep(2); }}>
                    <span>{presetIcon(p.name)}</span>{p.name}
                  </button>
                ))}
              </div>
              <strong>Maintenance reminders</strong>
              <div className="hm-vault-mobile-chip-grid">
                {MAINTENANCE_PRESETS.map(p => (
                  <button key={p.name} type="button" onClick={() => { applyPreset(p); setMobileMode("manual"); setMobileStep(2); }}>
                    <span>{presetIcon(p.name)}</span>{p.name}
                  </button>
                ))}
              </div>
              <strong>Documents & IDs</strong>
              <div className="hm-vault-mobile-chip-grid">
                {DOCUMENT_PRESETS.map(p => (
                  <button key={p.name} type="button" onClick={() => { applyPreset(p); setMobileMode("manual"); setMobileStep(2); }}>
                    <span>{presetIcon(p.name)}</span>{p.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mobileMode === "manual" && mobileStep === 1 && (
            <form className="hm-vault-add-form-compact hm-vault-add-form-type" onSubmit={event => { event.preventDefault(); setMobileStep(2); }}>
              <div className="hm-vault-mobile-type-step">
                <div className="hm-vault-step-mini">
                  <span className="is-active" />
                  <em>Type</em>
                </div>
                <p>What are you saving? You can add more details after.</p>
                <div className="hm-vault-type-step-grid">
                  {[
                    { key: "warranty", label: "Warranty", icon: "🔐" },
                    { key: "appliance", label: "Appliance", icon: "🏠" },
                    { key: "receipt", label: "Receipt", icon: "🧾" },
                    { key: "manual", label: "Manual", icon: "📖" },
                    { key: "document", label: "Document / ID", icon: "🪪" },
                    { key: "maintenance", label: "Maintenance reminder", icon: "🔧" },
                  ].map(tile => (
                    <button key={tile.key} type="button" className={form.record_type === tile.key ? "is-selected" : ""} onClick={() => chooseMobileType(tile.key)}>
                      <span>{tile.icon}</span>
                      <strong>{tile.label}</strong>
                    </button>
                  ))}
                </div>
              </div>
              <div className="hm-vault-add-inline-footer">
                <button type="button" className="hm-vault-add-secondary" onClick={() => setMobileMode("quick")}>← Back</button>
                <button type="submit" className="hm-vault-add-primary">Next — Fill details →</button>
              </div>
            </form>
          )}

          {mobileMode === "manual" && mobileStep === 2 && (
            <form id="hm-vault-add-drawer-form" onSubmit={handleMobileSubmit} className="hm-vault-add-form-compact hm-vault-add-form-details">
              <div className="hm-vault-add-fields">
                <div className="hm-vault-selected-type" aria-label="Selected record type">
                  <span>Saving as</span>
                  <button type="button" onClick={() => setMobileStep(1)}>
                    {TYPE_CONFIG[form.record_type]?.label || typeLabel} · change →
                  </button>
                </div>
                <label className="hm-util-ledger-span">
                  <span>{titleLabel()} *</span>
                  <input className="hm-auth-input" placeholder={titlePlaceholder()} value={form.title} onChange={e => setField("title", e.target.value)} autoFocus />
                </label>
                {form.record_type === "warranty" && (
                  <>
                    <label className="hm-vault-date-field">
                      <span>Purchase date</span>
                      <input type="date" className="hm-auth-input" value={form.purchase_date} onChange={e => setField("purchase_date", e.target.value)} />
                    </label>
                    <label className={`hm-vault-date-field hm-vault-warranty-end${attemptedMobileSave && !form.due_date ? " is-missing" : ""}`}>
                      <span>⚠ Warranty end date *</span>
                      <input type="date" className="hm-auth-input" value={form.due_date} onChange={e => setField("due_date", e.target.value)} />
                    </label>
                    <label className="hm-util-ledger-span">
                      <span>Category</span>
                      <select className="hm-auth-input" value={form.category} onChange={e => setField("category", e.target.value)}>
                        <option value="">Choose category</option>
                        {VAULT_CATEGORY_OPTIONS.map(category => <option key={category} value={category}>{category}</option>)}
                      </select>
                    </label>
                    <label>
                      <span>Retailer</span>
                      <input className="hm-auth-input" placeholder="Best Buy, Lowe's..." value={form.retailer} onChange={e => setField("retailer", e.target.value)} />
                    </label>
                    <label>
                      <span>Model number</span>
                      <input className="hm-auth-input" placeholder="Model or serial" value={form.model_number} onChange={e => setField("model_number", e.target.value)} />
                    </label>
                    <label className="hm-util-ledger-span">
                      <span>Amount paid</span>
                      <input className="hm-auth-input" placeholder="$499" value={form.amount} onChange={e => setField("amount", e.target.value)} />
                    </label>
                  </>
                )}
                {form.record_type === "appliance" && (
                  <>
                    <label>
                      <span>Brand</span>
                      <input className="hm-auth-input" placeholder="Samsung, LG, GE..." value={form.retailer} onChange={e => setField("retailer", e.target.value)} />
                    </label>
                    <label>
                      <span>Model number</span>
                      <input className="hm-auth-input" placeholder="Model or serial" value={form.model_number} onChange={e => setField("model_number", e.target.value)} />
                    </label>
                    <label className="hm-vault-date-field">
                      <span>Purchase date</span>
                      <input type="date" className="hm-auth-input" value={form.purchase_date} onChange={e => setField("purchase_date", e.target.value)} />
                    </label>
                    <label className="hm-vault-date-field hm-vault-warranty-end">
                      <span>⚠ Warranty end date</span>
                      <input type="date" className="hm-auth-input" value={form.due_date} onChange={e => setField("due_date", e.target.value)} />
                    </label>
                    <label className="hm-util-ledger-span">
                      <span>Purchase price</span>
                      <input className="hm-auth-input" placeholder="$0" value={form.amount} onChange={e => setField("amount", e.target.value)} />
                    </label>
                  </>
                )}
                {form.record_type === "receipt" && (
                  <>
                    <label>
                      <span>Retailer</span>
                      <input className="hm-auth-input" placeholder="Target, Home Depot..." value={form.retailer} onChange={e => setField("retailer", e.target.value)} />
                    </label>
                    <label>
                      <span>Amount</span>
                      <input className="hm-auth-input" placeholder="$0" value={form.amount} onChange={e => setField("amount", e.target.value)} />
                    </label>
                    <label className="hm-util-ledger-span">
                      <span>Purchase date</span>
                      <input type="date" className="hm-auth-input" value={form.purchase_date} onChange={e => setField("purchase_date", e.target.value)} />
                    </label>
                  </>
                )}
                {form.record_type === "manual" && (
                  <>
                    <label>
                      <span>Brand</span>
                      <input className="hm-auth-input" placeholder="Samsung, Carrier..." value={form.retailer} onChange={e => setField("retailer", e.target.value)} />
                    </label>
                    <label>
                      <span>Model number</span>
                      <input className="hm-auth-input" placeholder="#GSX..." value={form.model_number} onChange={e => setField("model_number", e.target.value)} />
                    </label>
                  </>
                )}
                {form.record_type === "document" && (
                  <>
                    <label>
                      <span>Issuer</span>
                      <input className="hm-auth-input" placeholder="State Dept., DMV, issuer..." value={form.retailer} onChange={e => setField("retailer", e.target.value)} />
                    </label>
                    <label>
                      <span>Document / ID number</span>
                      <input className="hm-auth-input" placeholder="Passport, ID, or credential number" value={form.model_number} onChange={e => setField("model_number", e.target.value)} />
                    </label>
                    <label className="hm-vault-date-field">
                      <span>Issue date</span>
                      <input type="date" className="hm-auth-input" value={form.purchase_date} onChange={e => setField("purchase_date", e.target.value)} />
                    </label>
                    <label className="hm-vault-date-field hm-vault-warranty-end">
                      <span>Expiry date</span>
                      <input type="date" className="hm-auth-input" value={form.due_date} onChange={e => setField("due_date", e.target.value)} />
                    </label>
                  </>
                )}
                {form.record_type === "maintenance" && (
                  <>
                    <label>
                      <span>Service interval</span>
                      <select className="hm-auth-input" value={form.amount} onChange={e => setMaintenanceInterval(e.target.value)}>
                        <option value="">One-time / no repeat</option>
                        <option value="Every 30 days">Monthly (30 days)</option>
                        <option value="Every 90 days">Every 3 months</option>
                        <option value="Every 6 months">Every 6 months</option>
                        <option value="Every 12 months">Yearly</option>
                      </select>
                    </label>
                    <label>
                      <span>Next due date</span>
                      <input type="date" className="hm-auth-input" value={form.due_date} onChange={e => setField("due_date", e.target.value)} />
                    </label>
                  </>
                )}
                <label className="hm-util-ledger-span">
                  <span>Notes</span>
                  <textarea className="hm-auth-input" rows={3} placeholder={notesPlaceholder()} value={form.details} onChange={e => setField("details", e.target.value)} />
                </label>
              </div>
              <div className="hm-vault-add-inline-footer">
                <button type="button" className="hm-vault-add-secondary" onClick={() => setMobileStep(1)}>← Back</button>
                <button type="submit" className="hm-vault-add-primary" disabled={busy || !form.title.trim()}>{busy ? "Saving..." : "Save to vault"}</button>
              </div>
            </form>
          )}
        </div>
      </aside>
    </div>
  );
}

// ── Vault Scan Section ────────────────────────────────────────────────────────

function VaultInsightStrip({ records, expiring, expired }) {
  const appliances = records.filter(r => r.record_type === "appliance" || r.category === "Home Systems").length;
  const docs = records.filter(r => ["receipt", "manual", "warranty", "document"].includes(r.record_type)).length;
  const maintenanceDue = records.filter(r => r.record_type === "maintenance" && dueStatus(r.due_date)?.days <= 14).length;
  return (
    <div className="hm-vault-intel-row">
      <article>
        <span>Warranty watch</span>
        <strong>{expiring ? `${expiring} expiring soon` : expired ? `${expired} expired` : "Covered items calm"}</strong>
        <p>Add warranty end dates to catch renewals before they slip.</p>
      </article>
      <article>
        <span>Document memory</span>
        <strong>{docs} saved record{docs === 1 ? "" : "s"}</strong>
        <p>Receipts, manuals, and warranty cards stay tied to the item.</p>
      </article>
      <article>
        <span>Ask your home</span>
        <strong>AI home knowledge</strong>
        <p>Ask across warranties, manuals, receipts, models, and saved notes.</p>
      </article>
      <article>
        <span>Maintenance</span>
        <strong>{maintenanceDue ? `${maintenanceDue} due soon` : "Home tasks calm"}</strong>
        <p>AC filters, smoke detectors, dryer vents, and water filters can be tracked here.</p>
      </article>
      <article>
        <span>Service-ready</span>
        <strong>{appliances} home system{appliances === 1 ? "" : "s"}</strong>
        <p>Repair, parts, and protection links show where they are useful.</p>
      </article>
    </div>
  );
}

function VaultScanSection({ onSaved, onAiUsage, compact = false, records = [], attachRecord = null }) {
  const [scanType, setScanType]       = useState("receipt");
  const [previewUrl, setPreviewUrl]   = useState("");
  const [scanFileName, setScanFileName] = useState("");
  const [scanFileType, setScanFileType] = useState("");
  const [scanFileSize, setScanFileSize] = useState(null);
  const [candidates, setCandidates]   = useState([]);
  const [scanning, setScanning]       = useState(false);
  const [saving, setSaving]           = useState(false);
  const [message, setMessage]         = useState("");
  const [error, setError]             = useState("");
  const [aiActive, setAiActive]       = useState(null); // null=unknown, true, false
  const [scanConsent, setScanConsent] = useState(false);
  const showScanning = useMinLoad(scanning, 700);
  const showSaving = useMinLoad(saving, 700);
  const scanUploadRef = useRef(null);
  const scanCameraRef = useRef(null);
  const detectedCount = candidates.length;
  const hasDetectedItems = detectedCount > 0;
  const scanDropTitle = scanType === "receipt"
    ? "Upload receipt or invoice"
    : scanType === "warranty"
      ? "Upload warranty card"
      : "Upload manual or guide";
  const scanDropHelp = scanType === "receipt"
    ? "HouseMind extracts item, retailer, price, model, and warranty clues for your vault."
    : scanType === "warranty"
      ? "Save warranty coverage, purchase dates, model numbers, and expiration details."
      : "Capture manual names, model numbers, and home system details for later questions.";
  const uploadButtonLabel = scanType === "receipt" ? "Upload receipt" : scanType === "warranty" ? "Upload warranty" : "Upload manual";
  const cameraButtonLabel = scanType === "receipt" ? "Camera receipt" : "Use camera";
  const detectedTitle = scanType === "receipt" ? "Detected receipt records" : scanType === "warranty" ? "Detected warranty records" : "Detected manual records";
  const documentType = scanType === "warranty" ? "warranty_card" : scanType === "manual" ? "manual" : "receipt";

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!scanConsent) {
      setError("Confirm the scan notice before uploading a document.");
      e.target.value = "";
      return;
    }
    setError(""); setMessage("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(file.type === "application/pdf" ? "" : URL.createObjectURL(file));
    setScanFileName(file.name || "");
    setScanFileType(file.type || "");
    setScanFileSize(file.size || null);
    setCandidates([]);
    setScanning(true);

    try {
      const result = await householdRecordsApi.scan(file, scanType);
      onAiUsage?.(result?.ai_usage);

      if (result?.ai_configured === false) {
        setAiActive(false);
        setMessage(result.message || "AI scanning is not configured yet. Add details manually or try again after AI keys are connected.");
        setCandidates([]);
      } else {
        setAiActive(true);
        const items = (result?.items || []).map((item, i) => ({
          ...item, id: `${Date.now()}-${i}`, selected: true, attach_to_id: attachRecord?.id ? String(attachRecord.id) : "",
        }));
        if (items.length === 0) {
          setMessage("No items detected in this document. Try a clearer image or add items manually below.");
        } else {
          setMessage(`${items.length} item${items.length === 1 ? "" : "s"} detected — review and confirm before saving.`);
        }
        setCandidates(items);
      }
    } catch (err) {
      setError(err.message || "Could not scan document");
    } finally {
      setScanning(false);
    }
  }

  function chooseScanType(value) {
    setScanType(value);
    setCandidates([]);
    setPreviewUrl("");
    setScanFileName("");
    setScanFileType("");
    setScanFileSize(null);
    setMessage("");
    setError("");
  }

  function updateCandidate(id, patch) {
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  }

  function removeCandidate(id) {
    setCandidates(prev => prev.filter(c => c.id !== id));
  }

  async function saveConfirmed() {
    const selected = candidates.filter(c => c.selected && c.title.trim());
    if (!selected.length) { setError("Select at least one item to save."); return; }
    setSaving(true); setError(""); setMessage("");
    try {
      for (const item of selected) {
        const attachId = attachRecord?.id || item.attach_to_id;
        if (attachId) {
          await vaultApi.addDocument(attachId, {
            document_type: documentType,
            file_name: scanFileName || null,
            file_size_bytes: scanFileSize || null,
            ocr_extracted_data: {
              scan_type: scanType,
              detected_title: item.title?.trim() || null,
              record_type: item.record_type || scanType,
              category: item.category || null,
              retailer: item.retailer || null,
              model_number: item.model_number || null,
              amount: item.amount || null,
              purchase_date: item.purchase_date || null,
              due_date: item.due_date || null,
            },
          });
        } else {
          await householdRecordsApi.add({
            record_type:   item.record_type,
            title:         item.title.trim(),
            category:      item.category || null,
            retailer:      item.retailer || null,
            model_number:  item.model_number || null,
            amount:        item.amount || null,
            purchase_date: item.purchase_date || null,
            due_date:      item.due_date || null,
          });
        }
      }
      onSaved?.();
      setCandidates([]);
      setScanFileName("");
      setScanFileType("");
      setScanFileSize(null);
      setPreviewUrl("");
      setMessage(attachRecord ? `${selected.length} document metadata record${selected.length === 1 ? "" : "s"} attached to ${attachRecord.title}.` : `${selected.length} item${selected.length === 1 ? "" : "s"} saved to vault.`);
    } catch (err) {
      setError(err.message || "Could not save scanned items");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={`hm-panel hm-vault-scan-panel hm-vault-doc-scan-panel${compact ? " hm-vault-scan-panel--compact fd-food-scan-panel" : ""}`}>
      {showScanning && <SavingNotice title="Scanning" message="Reading this home document..." />}
      {showSaving && <SavingNotice title="Saving" message="Adding confirmed items to Home Vault..." />}
      {!compact && <div className="hm-panel-head">
        <div>
          <p>Document import</p>
          <h2>Scan receipt or manual</h2>
        </div>
      </div>}

      {!hasDetectedItems && (
        <div className="hm-vault-scan-note">
          {attachRecord ? `Scan a receipt, warranty, or manual for ${attachRecord.title}. HouseMind saves extracted metadata only; no raw file is stored.` : "Pick Receipt, Warranty, or Manual. HouseMind reads the home document and shows a review screen before anything is saved to Home Vault."}
          {aiActive === false && <><br /><em>AI scanning is not configured yet. No sample records are created; add details manually for now.</em></>}
          {aiActive === true  && <><br /><em>AI-powered extraction active — vault records detected from your document.</em></>}
        </div>
      )}
      {hasDetectedItems && (
        <div className="fd-food-scan-detected-bar hm-vault-scan-detected-bar">
          <span>
            <strong>{detectedCount}</strong> vault record{detectedCount === 1 ? "" : "s"} detected · Review before saving
          </span>
          <button type="button" onClick={() => { setCandidates([]); setPreviewUrl(""); setScanFileName(""); setScanFileType(""); setScanFileSize(null); }}>Rescan</button>
        </div>
      )}

      {error   && <div className="hm-inline-error"  >{error}</div>}
      {message && <div className="hm-inline-success">{message}</div>}

      <div className="hm-vault-scan-grid">

        {/* Controls */}
        {!hasDetectedItems && <div className="hm-vault-scan-controls">
          <div className="hm-segmented fd-food-scan-type-picker hm-vault-doc-type-picker">
            {[["receipt","Receipt"], ["warranty","Warranty"], ["manual","Manual"]].map(([val, lbl]) => (
              <button
                key={val} type="button"
                className={scanType === val ? "is-selected" : ""}
                onClick={() => chooseScanType(val)}
              >{lbl}</button>
            ))}
          </div>

          <label className="hm-vault-scan-consent">
            <input type="checkbox" checked={scanConsent} onChange={e => setScanConsent(e.target.checked)} />
            <span>HouseMind will read this document only to extract Home Vault details. Review everything before saving.</span>
          </label>

          <div className={`fd-food-scan-drop hm-vault-doc-drop${!scanConsent ? " is-disabled" : ""}`}>
            <input ref={scanUploadRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,image/*"
              disabled={!scanConsent}
              onChange={e => { handleFile(e); e.target.value = ""; }} />
            <input ref={scanCameraRef} type="file" accept="image/*" capture="environment"
              disabled={!scanConsent}
              onChange={e => { handleFile(e); e.target.value = ""; }} />
            <span className="hm-vault-scan-camera">🏠</span>
            <strong>{scanDropTitle}</strong>
            <span>{scanDropHelp}</span>
            <div className="fd-food-scan-drop-actions">
              <button type="button" onClick={() => scanUploadRef.current?.click()} disabled={!scanConsent || scanning}>{uploadButtonLabel}</button>
              <button type="button" onClick={() => scanCameraRef.current?.click()} disabled={!scanConsent || scanning}>{cameraButtonLabel}</button>
            </div>
          </div>

          {previewUrl && <img className="hm-scan-preview" src={previewUrl} alt="Scan preview" />}
          {!previewUrl && scanFileType === "application/pdf" && (
            <div className="hm-vault-empty" style={{ marginTop: 0 }}>
              PDF selected: {scanFileName}
            </div>
          )}
        </div>}

        {/* Review */}
        <div className="hm-vault-scan-review">
          <h3>{detectedTitle}</h3>

          {scanning ? (
            <div className="hm-vault-empty hm-vault-scanning" style={{ marginTop: 0 }}>
              <span className="hm-vault-scan-spinner" />
              Analyzing document…
            </div>
          ) : candidates.length === 0 ? (
            <div className="hm-vault-empty" style={{ marginTop: 0 }}>
              Upload a Home Vault document to see detected records here.
            </div>
          ) : (
            <>
              <div className="hm-vault-scan-candidates">
                {candidates.map(item => (
                  <article key={item.id} className={`hm-vault-scan-item fd-food-scan-item hm-vault-doc-scan-item${item.selected ? " is-selected" : ""}`}>
                    <div className="hm-vault-scan-item-check">
                      <input type="checkbox" checked={item.selected}
                        onChange={e => updateCandidate(item.id, { selected: e.target.checked })} />
                    </div>
                    <div className="hm-vault-scan-item-fields">
                      <div className="fd-food-scan-item-top">
                        <input className="hm-auth-input" value={item.title}
                          placeholder="Item name"
                          onChange={e => updateCandidate(item.id, { title: e.target.value })} />
                        <button type="button" className="hm-vault-scan-remove"
                          onClick={() => removeCandidate(item.id)} title="Remove">×</button>
                      </div>
                      <div className="fd-food-scan-item-meta">
                        <select className="hm-auth-input" value={item.record_type}
                          onChange={e => updateCandidate(item.id, { record_type: e.target.value })}>
                          {VAULT_TYPES.map(t => (
                            <option key={t} value={t}>{TYPE_CONFIG[t].label}</option>
                          ))}
                        </select>
                        <input className="hm-auth-input" value={item.retailer}
                          placeholder="Retailer"
                          onChange={e => updateCandidate(item.id, { retailer: e.target.value })} />
                      </div>
                      <div className="fd-food-scan-item-detail">
                        <input className="hm-auth-input" value={item.amount}
                          placeholder="Price"
                          onChange={e => updateCandidate(item.id, { amount: e.target.value })} />
                        <input className="hm-auth-input" value={item.model_number}
                          placeholder="Model #"
                          onChange={e => updateCandidate(item.id, { model_number: e.target.value })} />
                      </div>
                      {!attachRecord && records.length > 0 && (
                        <div className="fd-food-scan-item-detail hm-vault-scan-destination">
                          <label>
                            <span>Save destination</span>
                            <select className="hm-auth-input" value={item.attach_to_id || ""} onChange={e => updateCandidate(item.id, { attach_to_id: e.target.value })}>
                              <option value="">Create new vault item</option>
                              {records.map(record => <option key={record.id} value={record.id}>Attach to {record.title}</option>)}
                            </select>
                          </label>
                        </div>
                      )}
                      {attachRecord && (
                        <div className="hm-vault-scan-attach-target">
                          <span>Attaching metadata to</span>
                          <strong>{attachRecord.title}</strong>
                        </div>
                      )}
                      {item.record_type === "warranty" && (
                        <div className="fd-food-scan-item-detail">
                          <label style={{ display: "grid", gap: 3 }}>
                            <span style={{ fontSize: 11, color: "var(--hm-muted)", fontWeight: 800 }}>Purchase date</span>
                            <input type="date" className="hm-auth-input" value={item.purchase_date}
                              onChange={e => updateCandidate(item.id, { purchase_date: e.target.value })} />
                          </label>
                          <label style={{ display: "grid", gap: 3 }}>
                            <span style={{ fontSize: 11, color: "var(--hm-muted)", fontWeight: 800 }}>Warranty end</span>
                            <input type="date" className="hm-auth-input" value={item.due_date}
                              onChange={e => updateCandidate(item.id, { due_date: e.target.value })} />
                          </label>
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
              <div className="fd-food-scan-actions">
                <button className="hm-btn hm-btn--ghost" type="button" onClick={() => { setCandidates([]); setPreviewUrl(""); setScanFileName(""); setScanFileType(""); setScanFileSize(null); }}>
                  Rescan
                </button>
                <button type="button" className="hm-vault-scan-save"
                  onClick={saveConfirmed} disabled={saving || !candidates.some(c => c.selected)}>
                  {saving ? "Saving…" : attachRecord ? `Attach ${candidates.filter(c => c.selected).length} metadata record${candidates.filter(c => c.selected).length === 1 ? "" : "s"}` : `Save ${candidates.filter(c => c.selected).length} record${candidates.filter(c => c.selected).length === 1 ? "" : "s"} to vault`}
                </button>
              </div>
            </>
          )}
        </div>

      </div>
    </section>
  );
}

function HomeVaultAskBar() {
  const prompts = ["What warranties expire soon?", "What should I maintain?", "What should I add?"];

  function openVaultAsk(question = "") {
    const clean = String(question || "").trim();
    const contextualQuestion = clean
      ? `${clean} Focus on Home Vault.`
      : "Ask about my Home Vault.";
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: contextualQuestion,
        context: {
          module: "vault",
          module_label: "Home Vault",
          surface: "Home Vault",
        },
      },
    }));
  }

  return (
    <div className="fd-food-askbar hm-vault-askbar">
      <input
        className="fd-food-askbar__input"
        placeholder="✦ Ask about your Home Vault..."
        onFocus={() => openVaultAsk("")}
        readOnly
      />
      <div className="fd-food-askbar__chips">
        {prompts.map(prompt => (
          <button
            key={prompt}
            type="button"
            className="fd-food-askbar__chip"
            onClick={() => openVaultAsk(prompt)}
          >
            {prompt}
          </button>
        ))}
      </div>
      <button type="button" className="fd-food-askbar__button" onClick={() => openVaultAsk(prompts[0])}>
        ✦ Ask
      </button>
    </div>
  );
}

// ── Vault Affiliate Card ──────────────────────────────────────────────────────

function VaultAffiliateCard({ mode = "all", query = "" }) {
  const [links, setLinks] = useState(null);

  useEffect(() => {
    affiliateApi.vaultLinks(query).then(setLinks).catch(() => {});
  }, [query]);

  if (!links) return null;

  const options = [
    { key: "taskrabbit", label: "TaskRabbit",  url: links.taskrabbit,  icon: "🔧", desc: "Hire a local tasker" },
    { key: "angi",       label: "Angi",         url: links.angi,       icon: "🏠", desc: "Home service pros" },
    { key: "thumbtack",  label: "Thumbtack",    url: links.thumbtack,  icon: "📌", desc: "Local home pros" },
    { key: "home_depot", label: "Home Depot",   url: links.home_depot, icon: "🛠️", desc: "Parts & tools" },
    { key: "lowes",      label: "Lowe's",       url: links.lowes,      icon: "🪚", desc: "Parts & tools" },
    { key: "upsie",      label: "Upsie",        url: links.upsie,      icon: "🛡️", desc: "Extended warranties" },
    { key: "asurion",    label: "Asurion",      url: links.asurion,    icon: "🛡️", desc: "Protection plans" },
  ].filter(o => o.url);

  if (options.length === 0) return null;
  const warrantyOptions = mode === "shops" || mode === "services" ? [] : options.filter(opt => ["upsie", "asurion"].includes(opt.key));
  const serviceOptions = mode === "shops"
    ? options.filter(opt => ["home_depot", "lowes"].includes(opt.key))
    : mode === "services"
      ? options.filter(opt => ["taskrabbit", "angi", "thumbtack"].includes(opt.key))
    : options.filter(opt => !["upsie", "asurion"].includes(opt.key));
  const renderOptions = items => items.map(opt => (
    <a
      key={opt.key}
      href={opt.url}
      target="_blank"
      rel="noopener noreferrer"
      className="affiliate-btn btn btn-secondary btn-sm"
      title={opt.desc}
      onClick={() => affiliateApi.track(opt.key, "home-vault", opt.url).catch(() => {})}
    >
      <span>{opt.icon}</span>
      <span>{opt.label}</span>
      <span>↗</span>
    </a>
  ));

  return (
    <div className="hm-vault-tip hm-vault-services-grouped">
      {warrantyOptions.length > 0 && (
        <section>
          <strong>Warranty protection</strong>
          <div>{renderOptions(warrantyOptions)}</div>
        </section>
      )}
      {serviceOptions.length > 0 && <section>
        <strong>{mode === "shops" ? "Parts & shops" : mode === "services" ? "Repair services" : "Repair & parts"}</strong>
        <div>{renderOptions(serviceOptions)}</div>
      </section>}
      <p className="affiliate-disclosure" style={{ marginTop: 8, fontSize: 10 }}>
        Some links may be affiliate links. We may earn a small commission at no extra cost to you.
      </p>
    </div>
  );
}

// ── Contracts ─────────────────────────────────────────────────────────────────

const DOC_TYPE_LABEL = {
  lease: "Lease",
  service_contract: "Service Contract",
  hoa: "HOA Agreement",
  employment: "Employment",
  insurance: "Insurance",
  warranty_extended: "Extended Warranty",
  nda: "NDA",
  loan: "Loan",
  purchase: "Purchase Agreement",
  other: "Contract",
};

const SEVERITY_COLOR = { high: "#dc2626", medium: "#d97706", low: "#2563eb" };
const FLAG_COLOR     = { red: "#dc2626", amber: "#d97706", info: "var(--hm-mint)" };

function ContractDetailSheet({ contract, onClose, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!contract) return;
    const prev = { overflow: document.body.style.overflow, position: document.body.style.position,
                   top: document.body.style.top, width: document.body.style.width };
    const scrollY = window.scrollY || 0;
    document.body.style.overflow  = "hidden";
    document.body.style.position  = "fixed";
    document.body.style.top       = `-${scrollY}px`;
    document.body.style.width     = "100%";
    return () => {
      document.body.style.overflow  = prev.overflow  || "";
      document.body.style.position  = prev.position  || "";
      document.body.style.top       = prev.top       || "";
      document.body.style.width     = prev.width     || "";
      window.scrollTo(0, scrollY);
    };
  }, [contract]);

  if (!contract) return null;

  const highFlags  = (contract.red_flags || []).filter(f => f.severity === "high");
  const otherFlags = (contract.red_flags || []).filter(f => f.severity !== "high");
  const totalCost  = (contract.hidden_costs || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);

  async function handleDelete() {
    if (!window.confirm("Remove this contract scan? This cannot be undone.")) return;
    setDeleting(true);
    try { await contractsApi.remove(contract.id); onDelete(contract.id); }
    catch { setDeleting(false); }
  }

  return (
    <div className="hm-contract-sheet-shell" role="dialog" aria-modal="true" aria-label="Contract details">
      <button type="button" className="hm-contract-sheet-scrim" onClick={onClose} aria-label="Close" />
      <section className="hm-contract-sheet">
        {/* Header */}
        <div className="hm-contract-sheet-head">
          <div>
            <span className="hm-contract-sheet-type">{DOC_TYPE_LABEL[contract.document_type] || "Contract"}</span>
            <h2 className="hm-contract-sheet-title">{contract.title}</h2>
          </div>
          <button type="button" className="hm-contract-sheet-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Scrollable body */}
        <div className="hm-contract-sheet-body" ref={bodyRef}>

          {/* AI summary */}
          {contract.ai_summary && (
            <div className="hm-contract-summary">
              <span className="hm-contract-summary-icon">✦</span>
              <p>{contract.ai_summary}</p>
            </div>
          )}

          {/* Red flags — high severity first */}
          {(contract.red_flags || []).length > 0 && (
            <div className="hm-contract-section">
              <div className="hm-contract-section-head">
                <span style={{ color: "#dc2626" }}>⚑</span>
                <strong>Red flags & warnings</strong>
                <em>{contract.red_flag_count} found</em>
              </div>
              {[...highFlags, ...otherFlags].map((flag, i) => (
                <div key={i} className="hm-contract-flag" style={{ "--flag-color": SEVERITY_COLOR[flag.severity] || "#555" }}>
                  <div className="hm-contract-flag-head">
                    <span className="hm-contract-flag-sev">{flag.severity?.toUpperCase()}</span>
                    {flag.section && <span className="hm-contract-flag-section">{flag.section}</span>}
                  </div>
                  {flag.text && <blockquote className="hm-contract-flag-quote">"{flag.text}"</blockquote>}
                  <p className="hm-contract-flag-explain">{flag.explanation}</p>
                </div>
              ))}
            </div>
          )}

          {/* Hidden costs */}
          {(contract.hidden_costs || []).length > 0 && (
            <div className="hm-contract-section">
              <div className="hm-contract-section-head">
                <span>💸</span>
                <strong>Hidden costs</strong>
                {totalCost > 0 && <em>${totalCost.toLocaleString()} total</em>}
              </div>
              {(contract.hidden_costs || []).map((cost, i) => (
                <div key={i} className="hm-contract-row">
                  <span>{cost.label}</span>
                  <strong>${Number(cost.amount || 0).toLocaleString()}{cost.frequency ? ` / ${cost.frequency}` : ""}</strong>
                  {cost.note && <small>{cost.note}</small>}
                </div>
              ))}
            </div>
          )}

          {/* Key amounts */}
          {(contract.key_amounts || []).length > 0 && (
            <div className="hm-contract-section">
              <div className="hm-contract-section-head"><span>💰</span><strong>Key amounts</strong></div>
              {(contract.key_amounts || []).map((amt, i) => (
                <div key={i} className="hm-contract-row">
                  <span>{amt.label}</span>
                  <strong>${Number(amt.amount || 0).toLocaleString()}{amt.frequency ? ` / ${amt.frequency}` : ""}</strong>
                  {amt.note && <small>{amt.note}</small>}
                </div>
              ))}
            </div>
          )}

          {/* Key dates */}
          {(contract.expiry_date || contract.effective_date || contract.auto_renews != null) && (
            <div className="hm-contract-section">
              <div className="hm-contract-section-head"><span>📅</span><strong>Key dates</strong></div>
              {contract.effective_date && (
                <div className="hm-contract-row"><span>Effective</span><strong>{contract.effective_date}</strong></div>
              )}
              {contract.expiry_date && (
                <div className="hm-contract-row">
                  <span>Expires</span>
                  <strong style={{ color: contract.days_until_expiry != null && contract.days_until_expiry < 60 ? "#dc2626" : undefined }}>
                    {contract.expiry_date}
                    {contract.days_until_expiry != null && ` (${contract.days_until_expiry > 0 ? `${contract.days_until_expiry}d left` : "expired"})`}
                  </strong>
                </div>
              )}
              {contract.auto_renews != null && (
                <div className="hm-contract-row">
                  <span>Auto-renews</span>
                  <strong style={{ color: contract.auto_renews ? "#d97706" : "var(--hm-mint)" }}>
                    {contract.auto_renews ? `Yes — give ${contract.renewal_notice_days || "?"} days notice to cancel` : "No"}
                  </strong>
                </div>
              )}
              {contract.renewal_deadline && (
                <div className="hm-contract-row" style={{ background: "rgba(217,119,6,0.08)", borderRadius: 8, padding: "6px 10px" }}>
                  <span>⚠ Cancel-by deadline</span>
                  <strong style={{ color: "#d97706" }}>{contract.renewal_deadline}</strong>
                </div>
              )}
            </div>
          )}

          {/* Parties */}
          {(contract.parties || []).length > 0 && (
            <div className="hm-contract-section">
              <div className="hm-contract-section-head"><span>👤</span><strong>Parties</strong></div>
              {(contract.parties || []).map((p, i) => (
                <div key={i} className="hm-contract-row">
                  <span style={{ textTransform: "capitalize" }}>{p.role}</span>
                  <strong>{p.name}{p.contact ? ` · ${p.contact}` : ""}</strong>
                </div>
              ))}
            </div>
          )}

          {/* Obligations */}
          {(contract.obligations || []).length > 0 && (
            <div className="hm-contract-section">
              <div className="hm-contract-section-head"><span>✅</span><strong>Your obligations</strong></div>
              {(contract.obligations || []).map((ob, i) => (
                <div key={i} className="hm-contract-obligation">
                  <span className="hm-contract-obligation-party">{ob.party}</span>
                  <p>{ob.obligation}</p>
                  {ob.deadline && ob.deadline !== "ongoing" && <small>Deadline: {ob.deadline}</small>}
                  {ob.recurring && <small>Recurring</small>}
                </div>
              ))}
            </div>
          )}

          {/* Important sections */}
          {(contract.important_sections || []).length > 0 && (
            <div className="hm-contract-section">
              <div className="hm-contract-section-head"><span>📋</span><strong>Sections to read carefully</strong></div>
              {(contract.important_sections || []).map((sec, i) => (
                <div key={i} className="hm-contract-isection" style={{ "--flag-color": FLAG_COLOR[sec.flag_level] || "var(--hm-muted)" }}>
                  <div className="hm-contract-isection-head">
                    <span className="hm-contract-isection-dot" />
                    <strong>{sec.title}</strong>
                    {sec.page && <em>p.{sec.page}</em>}
                  </div>
                  <p>{sec.summary}</p>
                </div>
              ))}
            </div>
          )}

          {/* Delete */}
          <div style={{ paddingTop: 12, borderTop: "1px solid var(--hm-line)", marginTop: 8 }}>
            <button type="button" className="hm-contract-delete-btn" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Removing…" : "Remove this scan"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContractsSection() {
  const toast = useToastActions();
  const fileRef = useRef(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [scanning, setScanning]   = useState(false);
  const [selected, setSelected]   = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [scanError, setScanError] = useState("");

  useEffect(() => {
    contractsApi.list().then(setContracts).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setScanError("");
    setScanning(true);
    const tid = toast?.addToast?.("Scanning contract…", "loading", { persist: true });
    try {
      const result = await contractsApi.scan(file);
      setContracts(prev => [result, ...prev]);
      setSelected(result);
      toast?.updateToast?.(tid, "Contract analyzed ✓", "success");
    } catch (err) {
      setScanError(err.message || "Scan failed. Try a clearer PDF or image.");
      toast?.updateToast?.(tid, "Scan failed", "error");
    } finally {
      setScanning(false);
    }
  }

  const highFlagTotal = contracts.reduce((s, c) => s + (c.high_flag_count || 0), 0);

  return (
    <div id="contracts" className={`hm-contracts-section${collapsed ? " is-collapsed" : ""}`}>
      {/* Section header */}
      <div className="hm-contracts-head">
        <button type="button" className="hm-contracts-head-toggle" onClick={() => setCollapsed(c => !c)}>
          <span className="hm-contracts-head-icon">📄</span>
          <div>
            <strong>Contracts & Agreements</strong>
            <span>{contracts.length} scanned{highFlagTotal > 0 ? ` · ${highFlagTotal} high-risk flag${highFlagTotal > 1 ? "s" : ""}` : ""}</span>
          </div>
          <em className="hm-contracts-head-chevron">{collapsed ? "⌄" : "⌃"}</em>
        </button>
        <button
          type="button"
          className="hm-contracts-scan-btn"
          onClick={() => fileRef.current?.click()}
          disabled={scanning}
          title="Upload a PDF, Word doc, or photo of a contract for AI analysis"
        >
          {scanning ? <span className="hm-contracts-scan-spinner" /> : "+ Scan contract"}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,image/*" style={{ display: "none" }} onChange={handleFile} />
      </div>

      {!collapsed && (
        <>
          {scanError && (
            <div className="hm-contracts-error">{scanError} <button type="button" onClick={() => setScanError("")}>×</button></div>
          )}

          {loading ? (
            <div className="hm-contracts-empty">Checking contracts…</div>
          ) : contracts.length === 0 ? (
            <div className="hm-contracts-empty">
              <p>No contracts scanned yet.</p>
              <p>Upload a lease, service contract, HOA agreement, or any legal document — AI will highlight hidden costs, auto-renewal traps, and sections to read carefully before you sign.</p>
              <button type="button" className="hm-contracts-scan-btn is-primary" onClick={() => fileRef.current?.click()} disabled={scanning}>
                {scanning ? "Scanning…" : "Upload first contract"}
              </button>
            </div>
          ) : (
            <div className="hm-contracts-grid">
              {contracts.map(c => {
                const urgentExpiry = c.days_until_expiry != null && c.days_until_expiry >= 0 && c.days_until_expiry < 60;
                const autoRenewWarning = c.auto_renews && c.renewal_deadline;
                return (
                  <button key={c.id} type="button" className="hm-contract-card" onClick={() => setSelected(c)}>
                    <div className="hm-contract-card-top">
                      <span className="hm-contract-card-type">{DOC_TYPE_LABEL[c.document_type] || "Contract"}</span>
                      {c.high_flag_count > 0 && (
                        <span className="hm-contract-card-badge is-high">{c.high_flag_count} high risk</span>
                      )}
                      {c.high_flag_count === 0 && c.red_flag_count > 0 && (
                        <span className="hm-contract-card-badge is-med">{c.red_flag_count} flag{c.red_flag_count > 1 ? "s" : ""}</span>
                      )}
                    </div>
                    <h3 className="hm-contract-card-title">{c.title}</h3>
                    {c.ai_summary && <p className="hm-contract-card-summary">{c.ai_summary.slice(0, 110)}{c.ai_summary.length > 110 ? "…" : ""}</p>}
                    <div className="hm-contract-card-meta">
                      {c.expiry_date && (
                        <span style={{ color: urgentExpiry ? "#dc2626" : undefined }}>
                          Expires {c.expiry_date}{urgentExpiry ? " ⚠" : ""}
                        </span>
                      )}
                      {autoRenewWarning && (
                        <span style={{ color: "#d97706" }}>Cancel by {c.renewal_deadline}</span>
                      )}
                      {(contract => contract.hidden_costs?.length > 0 ? `${contract.hidden_costs.length} hidden cost${contract.hidden_costs.length > 1 ? "s" : ""}` : null)(c) && (
                        <span>💸 {c.hidden_costs.length} hidden cost{c.hidden_costs.length > 1 ? "s" : ""}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {selected && (
        <ContractDetailSheet
          contract={selected}
          onClose={() => setSelected(null)}
          onDelete={id => { setContracts(prev => prev.filter(c => c.id !== id)); setSelected(null); }}
        />
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function HomeVaultPage() {
  const { user, applyAiUsage } = useAuth();
  const toast = useToastActions();
  const [records, setRecords]       = useState([]);
  const [vehicles, setVehicles]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [activeTab, setActiveTab]   = useState("all");
  const [editingId, setEditingId]   = useState(null);
  const [editDraft, setEditDraft]   = useState({});
  const [editBusy, setEditBusy]     = useState(false);
  const [intervalRecordId, setIntervalRecordId] = useState(null);
  const [intervalDraft, setIntervalDraft] = useState({ amount: "", due_date: "" });
  const [intervalBusy, setIntervalBusy] = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [form, setForm]             = useState(BLANK_FORM);
  const [busy, setBusy]             = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingBudgetDelete, setPendingBudgetDelete] = useState(null);
  const [askRecordId, setAskRecordId] = useState(null);
  const [askQuestion, setAskQuestion] = useState("");
  const [askAnswer, setAskAnswer] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [homeQuestion, setHomeQuestion] = useState("");
  const [homeAnswer, setHomeAnswer] = useState("");
  const [showHomeAsk, setShowHomeAsk] = useState(false);
  const [homeAskHistory, setHomeAskHistory] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("hm_vault_home_ai_history") || "[]");
      return Array.isArray(saved) ? saved.slice(0, 5) : [];
    } catch {
      return [];
    }
  });
  const [homeAskBusy, setHomeAskBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [mobileSheet, setMobileSheet] = useState(null);
  const [vaultToolsMode, setVaultToolsMode] = useState("all");
  const [vaultToolsQuery, setVaultToolsQuery] = useState("");
  const [recordMenuId, setRecordMenuId] = useState(null);
  const [expandedRecordId, setExpandedRecordId] = useState(null);
  const [vaultGroupOpen, setVaultGroupOpen] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("hm_vault_group_open") || "{}");
      return { appliances: true, maintenance: false, warranties: true, documents: false, ...saved };
    } catch {
      return { appliances: true, maintenance: false, warranties: true, documents: false };
    }
  });
  const [detailRecordId, setDetailRecordId] = useState(null);
  const [scanAttachRecordId, setScanAttachRecordId] = useState(null);
  const [manualSheet, setManualSheet] = useState({ open: false, record: null, docs: [], busy: false, error: "" });
  const [recallSheet, setRecallSheet] = useState({ open: false, record: null, result: null, busy: false, error: "" });
  const [vaultAiOpen, setVaultAiOpen] = useState(() => {
    try { return localStorage.getItem("hm_vault_ai_workbench_open") === "true"; } catch { return false; }
  });
  const [showLeaseSheet, setShowLeaseSheet] = useState(false);
  const [leaseDraft, setLeaseDraft] = useState({
    landlord_name: "",
    landlord_phone: "",
    landlord_email: "",
    property_address: "",
    monthly_rent: "",
    deposit_amount: "",
    lease_start: "",
    lease_end: "",
    notice_days_required: "90",
    autopay: false,
    pets_allowed: false,
    parking_included: false,
    notes: "",
  });
  const [leaseBusy, setLeaseBusy] = useState(false);
  const [showInsuranceSheet, setShowInsuranceSheet] = useState(false);
  const [insuranceDraft, setInsuranceDraft] = useState({
    provider: "",
    policy_number: "",
    coverage_amount: "",
    liability_amount: "",
    deductible: "",
    agent_contact: "",
    renewal_date: "",
    notes: "",
  });
  const [insuranceBusy, setInsuranceBusy] = useState(false);
  const [showMoveInSheet, setShowMoveInSheet] = useState(false);
  const [moveInDraft, setMoveInDraft] = useState({ photos: "0", damage: "0", entries: "", notes: "" });
  const [moveInBusy, setMoveInBusy] = useState(false);
  const activeWorkspace = user?.workspaces?.find(workspace => workspace.id === user?.active_workspace_id) || user?.workspace || null;
  const [propertyType, setPropertyType] = useState(activeWorkspace?.property_type || "owner");
  const [healthSummary, setHealthSummary] = useState(null);
  const [lease, setLease] = useState(null);
  const [ownerBudget, setOwnerBudget] = useState([]);
  const [ownerImprovements, setOwnerImprovements] = useState([]);
  const [ownerContractors, setOwnerContractors] = useState([]);
  const [ownerSuggestions, setOwnerSuggestions] = useState({ improvements: [], contractors: [] });
  const [ownerSeasonal, setOwnerSeasonal] = useState([]);
  const [ownerSeason, setOwnerSeason] = useState("spring");
  const [ownerExportSummary, setOwnerExportSummary] = useState(null);
  const [showImprovementSheet, setShowImprovementSheet] = useState(false);
  const [improvementDraft, setImprovementDraft] = useState(null);
  const [pendingImprovementDelete, setPendingImprovementDelete] = useState(null);
  const [showContractorSheet, setShowContractorSheet] = useState(false);
  const [contractorDraft, setContractorDraft] = useState(null);
  const [pendingContractorDelete, setPendingContractorDelete] = useState(null);
  const [showBudgetSheet, setShowBudgetSheet] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState({ name: "", scheduled_month: "", estimated_cost_lo: "", estimated_cost_hi: "", actual_cost: "", status: "upcoming", notes: "" });
  const [ownerEnhancementBusy, setOwnerEnhancementBusy] = useState(false);
  const [claimItemId, setClaimItemId] = useState("");
  const [claimDamage, setClaimDamage] = useState("");
  const [claimOtherDraft, setClaimOtherDraft] = useState({ name: "", category: "", estimated_value: "" });
  const [vaultAiItemId, setVaultAiItemId] = useState("");
  const [vaultAiQuestion, setVaultAiQuestion] = useState("What is the most likely issue and should I try a DIY fix?");
  const [vaultAiResult, setVaultAiResult] = useState(null);
  const [vaultAiResultItem, setVaultAiResultItem] = useState(null);
  const [vaultAiResultSheetOpen, setVaultAiResultSheetOpen] = useState(false);
  const [vaultAiBusy, setVaultAiBusy] = useState("");
  const [vaultAiConfirm, setVaultAiConfirm] = useState(null);
  const [vaultAiError, setVaultAiError] = useState("");
  const [renterGuideSheet, setRenterGuideSheet] = useState("");
  const inlineVaultCacheKey = `hm_vault_inline_ai:${activeWorkspace?.id || "default"}`;
  const [inlineVaultActions, setInlineVaultActions] = useState(() => {
    return readInlineVaultAiCache(inlineVaultCacheKey);
  });
  const [vaultActionSheetRecordId, setVaultActionSheetRecordId] = useState(null);
  const [vaultActionNotice, setVaultActionNotice] = useState("");
  const [maintenanceLogBusyId, setMaintenanceLogBusyId] = useState(null);
  const [selectedImprovementId] = useState("");
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteDraft, setQuoteDraft] = useState({ vault_item_id: "", service_type: "", provider: "", quote_amount: "" });
  const showMaintenanceLogging = useMinLoad(!!maintenanceLogBusyId, 700);
  const showSaving = useMinLoad(busy || editBusy || !!deleteBusyId || exportBusy || leaseBusy || insuranceBusy || moveInBusy || ownerEnhancementBusy, 700);
  const inlineVaultBusy = Object.values(inlineVaultActions).some(action => action?.busy);
  const showAsking = useMinLoad(askBusy || homeAskBusy || inlineVaultBusy || !!vaultActionNotice, 700);
  const blankBudgetDraft = () => ({ name: "", scheduled_month: new Date().toISOString().slice(0, 10), estimated_cost_lo: "", estimated_cost_hi: "", actual_cost: "", status: "upcoming", notes: "" });

  useEffect(() => {
    setInlineVaultActions(readInlineVaultAiCache(inlineVaultCacheKey));
  }, [inlineVaultCacheKey]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await householdRecordsApi.listVault();
      setRecords(data || []);
      setError("");
    } catch (err) {
      setError(err.message || "Could not load Home Vault records");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  useEffect(() => {
    if (loading || window.location.hash !== "#contracts") return;
    const timer = window.setTimeout(() => document.getElementById("contracts")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    return () => window.clearTimeout(timer);
  }, [loading]);

  // Auto-open maintenance group if any record is due or overdue — keep closed otherwise
  useEffect(() => {
    if (!records.length) return;
    const today = new Date().toISOString().slice(0, 10);
    const hasUrgent = records.some(r => {
      if (r.record_type !== "maintenance") return false;
      if (!r.due_date) return false;
      return r.due_date <= today;
    });
    if (hasUrgent) {
      setVaultGroupOpen(prev => prev.maintenance ? prev : { ...prev, maintenance: true });
    }
  }, [records]);

  useEffect(() => {
    if (!recordMenuId) return undefined;

    const closeRecordMenu = event => {
      const target = event.target;
      if (target?.closest?.(".hm-vault-mobile-record-menu, .hm-vault-record-menu-popover")) return;
      setRecordMenuId(null);
    };
    const closeOnEscape = event => {
      if (event.key === "Escape") setRecordMenuId(null);
    };

    document.addEventListener("pointerdown", closeRecordMenu, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeRecordMenu, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [recordMenuId]);

  useEffect(() => {
    let ignore = false;
    workspacesApi.current()
      .then(data => {
        if (!ignore && data?.workspace?.property_type) setPropertyType(data.workspace.property_type);
      })
      .catch(() => {});
    vaultApi.healthScore()
      .then(data => { if (!ignore) setHealthSummary(data); })
      .catch(() => {});
    vaultApi.getLease()
      .then(data => { if (!ignore) setLease(data); })
      .catch(() => {});
    return () => { ignore = true; };
  }, [loadRecords]);

  const loadOwnerEnhancements = useCallback(async (season = ownerSeason) => {
    try {
      const [budget, improvements, contractors, seasonal] = await Promise.all([
        vaultApi.budget(),
        vaultApi.improvements(),
        vaultApi.contractors(),
        vaultApi.seasonal(season),
      ]);
      setOwnerBudget(budget || []);
      setOwnerImprovements(improvements || []);
      setOwnerContractors(contractors || []);
      setOwnerSeasonal(seasonal || []);
      void Promise.allSettled([
        vaultApi.exportSummary(),
        vaultApi.ownerSuggestions(),
      ]).then(([summaryResult, suggestionsResult]) => {
        if (summaryResult.status === "fulfilled") setOwnerExportSummary(summaryResult.value || null);
        if (suggestionsResult.status === "fulfilled") setOwnerSuggestions(suggestionsResult.value || { improvements: [], contractors: [] });
      });
    } catch (err) {
      setError(err.message || "Could not load owner Home Vault enhancements");
    }
  }, [ownerSeason]);

  useEffect(() => {
    if (propertyType === "owner") loadOwnerEnhancements(ownerSeason);
  }, [propertyType, ownerSeason, loadOwnerEnhancements]);

  useEffect(() => {
    if (!lease) return;
    setLeaseDraft({
      landlord_name: lease.landlord_name || "",
      landlord_phone: lease.landlord_phone || "",
      landlord_email: lease.landlord_email || "",
      property_address: lease.property_address || "",
      monthly_rent: lease.monthly_rent || "",
      deposit_amount: lease.deposit_amount || "",
      lease_start: lease.lease_start || "",
      lease_end: lease.lease_end || "",
      notice_days_required: lease.notice_days_required || "90",
      autopay: !!lease.autopay,
      pets_allowed: !!lease.pets_allowed,
      parking_included: !!lease.parking_included,
      notes: lease.notes || "",
    });
  }, [lease]);

  useEffect(() => {
    let ignore = false;
    vehiclesApi.list()
      .then(data => { if (!ignore) setVehicles(data || []); })
      .catch(() => { if (!ignore) setVehicles([]); });
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const vehicleId = params.get("vehicle_id");
    if (!vehicleId) return;
    setForm(f => ({
      ...f,
      vehicle_id: vehicleId,
      record_type: params.get("record_type") || f.record_type,
      title: params.get("title") || f.title,
      category: "Vehicles",
    }));
    setShowForm(true);
  }, []);

  // Stats
  const expired  = records.filter(r => r.record_type !== "maintenance" && warrantyStatus(r.due_date)?.cls === "is-expired").length;
  const expiring = records.filter(r => r.record_type !== "maintenance" && warrantyStatus(r.due_date)?.cls === "is-expiring").length;
  const warrantyCount = records.filter(r => r.record_type === "warranty").length;
  const applianceCount = records.filter(r => r.record_type === "appliance" || r.category === "Home Systems").length;
  const maintenanceCount = records.filter(r => r.record_type === "maintenance").length;
  const openRecallCount = records.filter(r => r.has_open_recall).length;
  const totalInsuredValue = records.reduce((sum, r) => sum + (Number(r.purchase_price ?? r.amount) || 0), 0);
  const daysToRenewal = lease?.lease_end
    ? Math.ceil((new Date(`${lease.lease_end}T00:00:00`) - new Date()) / 86400000)
    : null;

  const filtered = activeTab === "all"
    ? records
    : records.filter(r => r.record_type === activeTab);
  const groupedVaultRecords = VAULT_GROUPS.map(group => ({
    ...group,
    records: filtered.filter(record => vaultGroupId(record) === group.id),
  }));
  const selectedVaultAiItem = records.find(record => String(record.id) === String(vaultAiItemId)) || records.find(record => record.record_type === "appliance") || records[0] || null;
  const selectedImprovement = ownerImprovements.find(row => String(row.id) === String(selectedImprovementId)) || ownerImprovements[0] || null;
  const vaultActionSheetRecord = records.find(record => String(record.id) === String(vaultActionSheetRecordId)) || null;
  const vaultActionSheetState = vaultActionSheetRecord ? inlineVaultActions[vaultActionSheetRecord.id] : null;
  const renterInsuranceRecord = findRenterInsuranceRecord(records);
  const renterMoveInRecord = records.find(record => /move[-\s]?in|photo log|damage/i.test(`${record.title || ""} ${record.details || ""}`));
  const renterMoveLog = parseMoveInLog(renterMoveInRecord);
  const renterGuideTitle = renterGuideSheet === "insurance" ? "Renter insurance" : "Move-in guidance";
  const vaultAiResultTitle = vaultAiSheetTitle(vaultAiResult?.type || vaultAiBusy, vaultAiResultItem || selectedVaultAiItem);
  const vaultActionSheetTitle = vaultActionSheetState ? `${vaultActionSheetRecord?.title || "Vault item"} · ${({
    score: "Keep / replace",
    claim: "Claim summary",
    warranty: "Warranty coverage",
    quote_check: "Quote check",
  }[vaultActionSheetState.type] || "Vault action")}` : "Vault action";

  function toggleVaultGroup(groupId) {
    setVaultGroupOpen(current => {
      const next = { ...current, [groupId]: !current[groupId] };
      try { localStorage.setItem("hm_vault_group_open", JSON.stringify(next)); } catch {}
      if (current[groupId] && expandedRecordId) {
        const expanded = records.find(record => record.id === expandedRecordId);
        if (expanded && vaultGroupId(expanded) === groupId) setExpandedRecordId(null);
      }
      return next;
    });
  }

  // Preset apply
  function applyPreset(preset) {
    if (!preset) return;
    const p = typeof preset === "string" ? VAULT_PRESETS.find(x => x.name === preset) : preset;
    if (!p) return;
    setForm(f => ({
      ...f,
      title: p.name,
      category: p.category,
      record_type: p.record_type,
      amount: p.amount || f.amount,
      due_date: p.record_type === "maintenance" && p.amount ? nextDateForInterval(p.amount) : f.due_date,
      details: p.details || f.details,
    }));
    setShowForm(true);
    setMobileSheet(null);
  }

  function setField(key, val) { setForm(f => ({ ...f, [key]: val })); }

  // Add
  async function handleAdd(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true); setError("");
    const tid = toast?.addToast?.("Saving record…", "loading", { persist: true });
    try {
      const normalizedDueDate = form.record_type === "maintenance" && form.amount && !form.due_date
        ? nextDateForInterval(form.amount)
        : form.due_date;
      const created = await householdRecordsApi.add({
        record_type:   form.record_type,
        title:         form.title.trim(),
        category:      form.category.trim() || null,
        purchase_date: form.purchase_date || null,
        retailer:      form.retailer.trim() || null,
        model_number:  form.model_number.trim() || null,
        due_date:      normalizedDueDate || null,
        amount:        form.amount.trim() || null,
        details:       form.details.trim() || null,
        vehicle_id:    form.vehicle_id || null,
      });
      let autoCreated = [];
      if (created.record_type !== "maintenance") {
        const autoTasks = automaticMaintenanceForVaultRecord(created, [...records, created]);
        if (autoTasks.length) {
          autoCreated = await Promise.all(autoTasks.map(task => householdRecordsApi.add(task)));
        }
      }
      setRecords(prev => [...autoCreated, created, ...prev]);
      setForm(BLANK_FORM);
      setShowForm(false);
      toast?.updateToast?.(tid, autoCreated.length ? `Record saved with ${autoCreated.length} maintenance reminder${autoCreated.length === 1 ? "" : "s"}` : "Record saved to Home Vault", "success");
      setSuccessMsg(autoCreated.length ? "Record saved with maintenance reminders." : "Record saved.");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      toast?.updateToast?.(tid, "Could not save record", "error");
      setError(err.message || "Could not save record");
    } finally {
      setBusy(false);
    }
  }

  // Edit
  function startEdit(record) {
    const effectiveType = vaultGroupId(record) === "maintenance" ? "maintenance" : record.record_type;
    setEditingId(record.id);
    setEditDraft({
      record_type:   effectiveType,
      title:         record.title,
      category:      effectiveType === "maintenance" ? "maintenance" : (record.category || ""),
      purchase_date: record.purchase_date || "",
      retailer:      record.retailer || "",
      model_number:  record.model_number || "",
      due_date:      record.due_date || "",
      amount:        record.amount || "",
      details:       record.details || "",
      vehicle_id:    record.vehicle_id || "",
    });
  }

  function openEditSheet(record) {
    if (!record?.id) return;
    setMobileSheet(null);
    window.requestAnimationFrame(() => startEdit(record));
  }

  function cancelEdit() { setEditingId(null); setEditDraft({}); }

  function openIntervalSheet(record) {
    if (!record?.id) return;
    setMobileSheet(null);
    window.requestAnimationFrame(() => {
      setIntervalRecordId(record.id);
      setIntervalDraft({
        amount: record.amount || "Every 90 days",
        due_date: record.due_date || nextDateForInterval(record.amount || "Every 90 days"),
      });
    });
  }

  function closeIntervalSheet() {
    setIntervalRecordId(null);
    setIntervalDraft({ amount: "", due_date: "" });
  }

  async function saveInterval(event) {
    event?.preventDefault?.();
    const record = records.find(row => row.id === intervalRecordId);
    if (!record) return;
    setIntervalBusy(true);
    setError("");
    const tid = toast?.addToast?.("Saving interval...", "loading", { persist: true });
    try {
      const normalizedDueDate = intervalDraft.amount && !intervalDraft.due_date
        ? nextDateForInterval(intervalDraft.amount)
        : intervalDraft.due_date;
      const updated = await householdRecordsApi.update(record.id, {
        record_type: "maintenance",
        title: record.title,
        category: "maintenance",
        due_date: normalizedDueDate || null,
        amount: intervalDraft.amount || null,
        details: record.details || null,
        vehicle_id: record.vehicle_id || null,
      });
      setRecords(prev => prev.map(item => item.id === record.id ? updated : item));
      closeIntervalSheet();
      toast?.updateToast?.(tid, "Interval saved", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not save interval", "error");
      setError(err.message || "Could not save interval");
    } finally {
      setIntervalBusy(false);
    }
  }

  async function saveEdit(id) {
    setEditBusy(true); setError("");
    const tid = toast?.addToast?.("Saving changes…", "loading", { persist: true });
    try {
      const normalizedDueDate = editDraft.record_type === "maintenance" && editDraft.amount && !editDraft.due_date
        ? nextDateForInterval(editDraft.amount)
        : editDraft.due_date;
      const updated = await householdRecordsApi.update(id, {
        record_type:   editDraft.record_type,
        title:         editDraft.title.trim(),
        category:      editDraft.record_type === "maintenance" ? "maintenance" : (editDraft.category.trim() || null),
        purchase_date: editDraft.purchase_date || null,
        retailer:      editDraft.retailer.trim() || null,
        model_number:  editDraft.model_number.trim() || null,
        due_date:      normalizedDueDate || null,
        amount:        editDraft.amount.trim() || null,
        details:       editDraft.details.trim() || null,
        vehicle_id:    editDraft.vehicle_id || null,
      });
      setRecords(prev => prev.map(r => r.id === id ? updated : r));
      cancelEdit();
      toast?.updateToast?.(tid, "Record updated", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not update record", "error");
      setError(err.message || "Could not update record");
    } finally {
      setEditBusy(false);
    }
  }

  async function openManualSheet(record) {
    setManualSheet({ open: true, record, docs: [], busy: true, error: "" });
    try {
      const docs = await vaultApi.documents(record.id);
      setManualSheet({ open: true, record, docs: (docs || []).filter(doc => doc.document_type === "manual"), busy: false, error: "" });
    } catch (err) {
      setManualSheet({ open: true, record, docs: [], busy: false, error: err.message || "Could not load manual metadata" });
    }
  }

  async function openRecallSheet(record, forceCheck = false) {
    const existing = parseRecallDetails(record.recall_details);
    setRecallSheet({ open: true, record, result: forceCheck ? null : { has_open_recall: !!record.has_open_recall, recall_details: existing, recall_checked_at: record.recall_checked_at }, busy: forceCheck, error: "" });
    if (!forceCheck) return;
    try {
      const result = await vaultApi.recallCheck(record.id);
      if (result.item) setRecords(prev => prev.map(row => row.id === record.id ? result.item : row));
      setRecallSheet({ open: true, record: result.item || record, result, busy: false, error: "" });
    } catch (err) {
      setRecallSheet({ open: true, record, result: null, busy: false, error: err.message || "Could not check recalls" });
    }
  }

  // Delete
  async function deleteRecord(record = pendingDelete) {
    if (!record?.id) return;
    setDeleteBusyId(record.id);
    setPendingDelete(null);
    setError("");
    const tid = toast?.addToast?.("Removing record…", "loading", { persist: true });
    try {
      await householdRecordsApi.remove(record.id);
      setRecords(prev => prev.filter(r => r.id !== record.id));
      toast?.updateToast?.(tid, "Record removed", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not delete record", "error");
      setError(err.message || "Could not delete record");
    } finally {
      setDeleteBusyId(null);
    }
  }

  const VaultTabs = (
    <div className="hm-vault-tabs">
      {["all", ...VAULT_TYPES].map(tab => (
        <button key={tab} type="button"
          className={`hm-vault-tab${activeTab === tab ? " is-active" : ""}`}
          onClick={() => setActiveTab(tab)}
        >
          {tab === "all" ? "All" : TYPE_CONFIG[tab].label}
          <span className="hm-vault-tab-count">
            {tab === "all" ? records.length : records.filter(r => r.record_type === tab).length}
          </span>
        </button>
      ))}
    </div>
  );

  async function submitAsk(record) {
    if (!askQuestion.trim()) return;
    setAskBusy(true);
    setError("");
    const tid = toast?.addToast?.("Asking Home Vault…", "loading", { persist: true });
    try {
      const result = await householdRecordsApi.ask(record.id, { question: askQuestion.trim() });
      setAskAnswer(result.answer || "I could not find a useful answer for this item.");
      if (result.record) {
        setRecords(prev => prev.map(r => r.id === record.id ? result.record : r));
        setAskAnswer("");
      }
      applyAiUsage?.(result.ai_usage);
      toast?.updateToast?.(tid, "Home Vault answer ready", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not answer this item", "error");
      setError(err.message || "Could not answer this item question");
    } finally {
      setAskBusy(false);
    }
  }

  const askRecord = records.find(record => record.id === askRecordId);
  const detailRecord = records.find(record => record.id === detailRecordId);
  const editingRecord = records.find(record => record.id === editingId);
  const intervalRecord = records.find(record => record.id === intervalRecordId);
  const scanAttachRecord = records.find(record => String(record.id) === String(scanAttachRecordId)) || null;

  async function submitHomeAsk(question = homeQuestion) {
    const q = question.trim();
    if (!q) return;
    setHomeQuestion(q);
    setHomeAskBusy(true);
    setError("");
    const tid = toast?.addToast?.("Asking Home Vault…", "loading", { persist: true });
    try {
      const result = await householdRecordsApi.askHome({ question: q });
      const answer = result.answer || "I could not find an answer from your saved Home Vault records.";
      setHomeAnswer(answer);
      const entry = {
        id: Date.now(),
        question: q,
        answer,
      };
      const nextHistory = [entry, ...homeAskHistory].slice(0, 5);
      setHomeAskHistory(nextHistory);
      localStorage.setItem("hm_vault_home_ai_history", JSON.stringify(nextHistory));
      applyAiUsage?.(result.ai_usage);
      toast?.updateToast?.(tid, "Home Vault answer ready", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not answer Home Vault", "error");
      setError(err.message || "Could not answer this Home Vault question");
    } finally {
      setHomeAskBusy(false);
    }
  }

  async function exportVault() {
    setExportBusy(true);
    setError("");
    const tid = toast?.addToast?.("Preparing Home Vault export…", "loading", { persist: true });
    try {
      await vaultApi.exportSalePdf();
      toast?.updateToast?.(tid, "Home Vault export ready", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not export Home Vault", "error");
      setError(err.message || "Could not export Home Vault");
    } finally {
      setExportBusy(false);
    }
  }

  async function togglePropertyType() {
    const next = propertyType === "owner" ? "renter" : "owner";
    setPropertyType(next);
    const tid = toast?.addToast?.("Switching Home Vault view…", "loading", { persist: true });
    try {
      const result = await workspacesApi.updateCurrent({ property_type: next });
      if (result?.workspace?.property_type) setPropertyType(result.workspace.property_type);
      toast?.updateToast?.(tid, `Home Vault switched to ${next === "owner" ? "Owner" : "Renter"} view`, "success");
    } catch (err) {
      setPropertyType(propertyType);
      toast?.updateToast?.(tid, "Could not switch Home Vault mode", "error");
      setError(err.message || "Could not switch Home Vault mode");
    }
  }

  async function saveLeaseDetails(event) {
    event?.preventDefault?.();
    setLeaseBusy(true);
    setError("");
    const tid = toast?.addToast?.("Saving lease details…", "loading", { persist: true });
    try {
      const payload = {
        ...leaseDraft,
        monthly_rent: leaseDraft.monthly_rent || null,
        deposit_amount: leaseDraft.deposit_amount || null,
        lease_start: leaseDraft.lease_start || null,
        lease_end: leaseDraft.lease_end || null,
        notice_days_required: Number(leaseDraft.notice_days_required || 30),
      };
      const updated = await vaultApi.updateLease(payload);
      setLease(updated);
      const leaseRecord = records.find(record => record.category === "lease" || /lease/i.test(record.title || ""));
      const leaseRecordPayload = {
        record_type: leaseRecord?.record_type || "manual",
        title: leaseRecord?.title || "Lease Agreement",
        category: "lease",
        retailer: leaseDraft.landlord_name || "",
        due_date: leaseDraft.lease_end || null,
        amount: leaseDraft.monthly_rent ? `$${leaseDraft.monthly_rent}/mo` : "",
        details: [
          `Property: ${leaseDraft.property_address || "Rental home"}`,
          `Notice: ${leaseDraft.notice_days_required || 30} days`,
          `Deposit: ${leaseDraft.deposit_amount || "Not saved"}`,
          `Autopay: ${leaseDraft.autopay ? "Yes" : "No"}`,
          `Pets: ${leaseDraft.pets_allowed ? "Allowed" : "Not allowed / unknown"}`,
          `Parking: ${leaseDraft.parking_included ? "Included" : "Not saved"}`,
          `Notes: ${leaseDraft.notes || "No lease notes saved."}`,
        ].join("\n"),
      };
      if (leaseRecord) {
        const saved = await householdRecordsApi.update(leaseRecord.id, leaseRecordPayload);
        setRecords(prev => prev.map(record => record.id === leaseRecord.id ? saved : record));
      } else {
        const created = await householdRecordsApi.add({
          ...leaseRecordPayload,
          record_type: "manual",
          title: "Lease Agreement",
        });
        setRecords(prev => [created, ...prev]);
      }
      setShowLeaseSheet(false);
      toast?.updateToast?.(tid, "Lease details saved", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not save lease details", "error");
      setError(err.message || "Could not save lease details");
    } finally {
      setLeaseBusy(false);
    }
  }

  function openMoveInLog() {
    const existing = records.find(record => /move[-\s]?in|photo log|damage/i.test(`${record.title || ""} ${record.details || ""}`));
    const parsed = parseMoveInLog(existing);
    setMoveInDraft({
      photos: parsed.photos ? String(parsed.photos) : "0",
      damage: parsed.damage ? String(parsed.damage) : "0",
      entries: parsed.entries || "",
      notes: parsed.notes || "",
    });
    setShowMoveInSheet(true);
  }

  function openInsuranceSheet() {
    const existing = findRenterInsuranceRecord(records);
    const parsed = parseInsuranceDetails(existing);
    setInsuranceDraft({
      provider: parsed.provider || "",
      policy_number: parsed.policyNumber || "",
      coverage_amount: parsed.coverage || "",
      liability_amount: parsed.liability || "",
      deductible: parsed.deductible || "",
      agent_contact: parsed.agent || "",
      renewal_date: parsed.renewalDate || "",
      notes: parsed.notes || "",
    });
    setShowInsuranceSheet(true);
  }

  async function saveInsuranceDetails(event) {
    event?.preventDefault?.();
    setInsuranceBusy(true);
    setError("");
    const tid = toast?.addToast?.("Saving insurance…", "loading", { persist: true });
    try {
      const details = formatInsuranceDetails(insuranceDraft);
      const existing = findRenterInsuranceRecord(records);
      const payload = {
        record_type: existing?.record_type || "warranty",
        title: existing?.title || "Renter's Insurance",
        category: "insurance",
        retailer: insuranceDraft.provider || "",
        model_number: insuranceDraft.policy_number || "",
        due_date: insuranceDraft.renewal_date || null,
        amount: insuranceDraft.coverage_amount || "",
        details,
      };
      if (existing) {
        const updated = await householdRecordsApi.update(existing.id, payload);
        setRecords(prev => prev.map(record => record.id === existing.id ? updated : record));
      } else {
        const created = await householdRecordsApi.add(payload);
        setRecords(prev => [created, ...prev]);
      }
      setShowInsuranceSheet(false);
      toast?.updateToast?.(tid, "Renter insurance saved", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not save insurance", "error");
      setError(err.message || "Could not save renter insurance");
    } finally {
      setInsuranceBusy(false);
    }
  }

  async function saveMoveInLog(event) {
    event?.preventDefault?.();
    setMoveInBusy(true);
    setError("");
    const tid = toast?.addToast?.("Saving move-in log…", "loading", { persist: true });
    try {
      const photos = Math.max(0, Number(moveInDraft.photos || 0));
      const damage = Math.max(0, Number(moveInDraft.damage || 0));
      const details = [
        `Photos logged: ${photos}`,
        `Damage items: ${damage}`,
        "Entries:",
        moveInDraft.entries || "No room-by-room entries saved.",
        `Notes: ${moveInDraft.notes || "Metadata only, no raw files stored."}`,
      ].join("\n");
      const existing = records.find(record => /move[-\s]?in|photo log|damage/i.test(`${record.title || ""} ${record.details || ""}`));
      if (existing) {
        const updated = await householdRecordsApi.update(existing.id, {
          record_type: existing.record_type || "receipt",
          title: existing.title || "Move-in Photo Log",
          category: "document",
          details,
        });
        setRecords(prev => prev.map(record => record.id === existing.id ? updated : record));
      } else {
        const created = await householdRecordsApi.add({
          record_type: "receipt",
          title: "Move-in Photo Log",
          category: "document",
          details,
        });
        setRecords(prev => [created, ...prev]);
      }
      setShowMoveInSheet(false);
      toast?.updateToast?.(tid, "Move-in log saved", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not save move-in log", "error");
      setError(err.message || "Could not save move-in photo metadata");
    } finally {
      setMoveInBusy(false);
    }
  }

  async function refreshOwnerBudget() {
    setOwnerEnhancementBusy(true);
    setError("");
    const tid = toast?.addToast?.("Refreshing maintenance plan…", "loading", { persist: true });
    try {
      await vaultApi.generateBudget();
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, "Owner maintenance plan refreshed", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not refresh maintenance plan", "error");
      setError(err.message || "Could not refresh owner maintenance plan");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  async function saveBudgetItem(event) {
    event?.preventDefault?.();
    setOwnerEnhancementBusy(true);
    setError("");
    const isUpdate = !!budgetDraft.id;
    const tid = toast?.addToast?.(isUpdate ? "Updating budget item…" : "Adding budget item…", "loading", { persist: true });
    try {
      if (isUpdate) {
        await vaultApi.updateBudgetItem(budgetDraft.id, budgetDraft);
      } else {
        await vaultApi.addBudgetItem(budgetDraft);
      }
      setShowBudgetSheet(false);
      setBudgetDraft(blankBudgetDraft());
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, isUpdate ? "Budget item updated" : "Budget item added", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not save budget item", "error");
      setError(err.message || "Could not save budget item");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  async function deleteBudgetItem(item = pendingBudgetDelete) {
    if (!item?.id) return;
    setOwnerEnhancementBusy(true);
    setPendingBudgetDelete(null);
    setError("");
    const tid = toast?.addToast?.("Deleting budget item…", "loading", { persist: true });
    try {
      await vaultApi.removeBudgetItem(item.id);
      setShowBudgetSheet(false);
      setBudgetDraft(blankBudgetDraft());
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, "Budget item deleted", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not delete budget item", "error");
      setError(err.message || "Could not delete budget item");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  function openBudgetSheet(item = null) {
    setBudgetDraft(item ? {
      id: item.id,
      name: item.name || "",
      scheduled_month: item.scheduled_month || new Date().toISOString().slice(0, 10),
      estimated_cost_lo: item.estimated_cost_lo ?? "",
      estimated_cost_hi: item.estimated_cost_hi ?? "",
      actual_cost: item.actual_cost ?? "",
      status: item.status || "upcoming",
      notes: item.notes || "",
      vault_item_id: item.vault_item_id || "",
    } : blankBudgetDraft());
    setShowBudgetSheet(true);
  }

  function openBudgetSheetFromSeasonalTask(task) {
    const scheduled = task?.scheduled_month || new Date().toISOString().slice(0, 10);
    setBudgetDraft({
      name: task?.name || "",
      scheduled_month: scheduled,
      estimated_cost_lo: task?.estimated_cost_lo ?? "",
      estimated_cost_hi: task?.estimated_cost_hi ?? "",
      actual_cost: "",
      status: task?.status === "done" ? "done" : "upcoming",
      notes: `${task?.season ? `${task.season} ` : ""}maintenance calendar task`.trim(),
    });
    setShowBudgetSheet(true);
  }

  function closeBudgetSheet() {
    setShowBudgetSheet(false);
    setBudgetDraft(blankBudgetDraft());
  }

  async function markBudgetDone(item) {
    setOwnerEnhancementBusy(true);
    setError("");
    const nextDone = item.status !== "done";
    const tid = toast?.addToast?.(nextDone ? "Logging budget item…" : "Reopening budget item…", "loading", { persist: true });
    try {
      await vaultApi.updateBudgetItem(item.id, { status: nextDone ? "done" : "upcoming", actual_cost: item.actual_cost || item.estimated_cost_hi || item.estimated_cost_lo || "" });
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, nextDone ? "Budget item logged" : "Budget item reopened", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not update budget item", "error");
      setError(err.message || "Could not update budget item");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  async function completeSeasonalTask(task) {
    setOwnerEnhancementBusy(true);
    setError("");
    let tid = null;
    try {
      if (task.source === "budget" && String(task.id || "").startsWith("budget:")) {
        tid = toast?.addToast?.("Logging task…", "loading", { persist: true });
        const budgetId = String(task.id).split(":")[1];
        await vaultApi.updateBudgetItem(budgetId, {
          status: "done",
          actual_cost: task.estimated_cost_hi ?? task.estimated_cost_lo ?? "",
        });
      } else if (task.source === "item" && task.vault_item_id) {
        tid = toast?.addToast?.("Logging task…", "loading", { persist: true });
        await vaultApi.addServiceLog(task.vault_item_id, {
          service_date: new Date().toISOString().slice(0, 10),
          service_type: task.name,
          notes: "Completed from Home Vault maintenance calendar.",
        });
      } else {
        openBudgetSheetFromSeasonalTask(task);
        return;
      }
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, "Seasonal task logged", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not log task", "error");
      setError(err.message || "Could not update seasonal task");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  function openImprovementSheet(row) {
    setImprovementDraft(row ? { ...row } : { name: "", category: "other", completed_date: "", cost: "", contractor: "", roi_pct: "", roi_return_est: "", notes: "", include_in_export: true });
    setShowImprovementSheet(true);
  }

  function closeImprovementSheet() {
    setShowImprovementSheet(false);
    setImprovementDraft(null);
  }

  async function saveImprovement(event) {
    event?.preventDefault?.();
    setOwnerEnhancementBusy(true);
    setError("");
    const tid = toast?.addToast?.("Saving improvement…", "loading", { persist: true });
    try {
      const contractorName = (improvementDraft.contractor || "").trim();
      if (improvementDraft.id) {
        await vaultApi.updateImprovement(improvementDraft.id, improvementDraft);
      } else {
        await vaultApi.addImprovement(improvementDraft);
      }
      if (contractorName && !ownerContractors.some(row => String(row.name || "").trim().toLowerCase() === contractorName.toLowerCase())) {
        await vaultApi.addContractor({
          name: contractorName,
          trade: improvementDraft.category || "general",
          times_used: 1,
          last_used_date: improvementDraft.completed_date || "",
          notes: `Auto-added from improvement: ${improvementDraft.name}`,
        });
      }
      closeImprovementSheet();
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, contractorName ? "Improvement saved and contractor checked" : "Improvement saved", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not save improvement", "error");
      setError(err.message || "Could not save improvement");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  async function deleteImprovement(row = pendingImprovementDelete) {
    if (!row?.id) return;
    setOwnerEnhancementBusy(true);
    setPendingImprovementDelete(null);
    setError("");
    const tid = toast?.addToast?.("Deleting improvement…", "loading", { persist: true });
    try {
      await vaultApi.removeImprovement(row.id);
      closeImprovementSheet();
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, "Improvement deleted", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not delete improvement", "error");
      setError(err.message || "Could not delete improvement");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  function openContractorSheet(row) {
    setContractorDraft(row ? { ...row } : { name: "", trade: "general", phone: "", email: "", rate: "", rating: "", times_used: 0, last_used_date: "", notes: "" });
    setShowContractorSheet(true);
  }

  function closeContractorSheet() {
    setShowContractorSheet(false);
    setContractorDraft(null);
  }

  async function saveContractor(event) {
    event?.preventDefault?.();
    setOwnerEnhancementBusy(true);
    setError("");
    const isUpdate = !!contractorDraft.id;
    const tid = toast?.addToast?.(isUpdate ? "Updating contractor…" : "Saving contractor…", "loading", { persist: true });
    try {
      if (isUpdate) {
        await vaultApi.updateContractor(contractorDraft.id, contractorDraft);
      } else {
        await vaultApi.addContractor(contractorDraft);
      }
      closeContractorSheet();
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, isUpdate ? "Contractor updated" : "Contractor saved", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not save contractor", "error");
      setError(err.message || "Could not save contractor");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  async function deleteContractor(row = pendingContractorDelete) {
    if (!row?.id) return;
    setOwnerEnhancementBusy(true);
    setPendingContractorDelete(null);
    setError("");
    const tid = toast?.addToast?.("Deleting contractor…", "loading", { persist: true });
    try {
      await vaultApi.removeContractor(row.id);
      closeContractorSheet();
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, "Contractor deleted", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not delete contractor", "error");
      setError(err.message || "Could not delete contractor");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  function applyCachedVaultReport(type, report) {
    setVaultAiResult({ type, data: report?.result_json || {} });
    setVaultAiResultSheetOpen(true);
    setVaultAiConfirm(null);
    setVaultAiError("");
  }

  function openQuoteForm(item = null) {
    const target = item || null;
    setQuoteDraft({
      vault_item_id: target?.id || "",
      service_type: target?.category || target?.record_type || "",
      provider: "",
      quote_amount: "",
    });
    setShowQuoteForm(true);
    if (target?.id) setVaultAiItemId(target.id);
  }

  function openAttachScan(record) {
    if (!record?.id) return;
    setMobileSheet(null);
    window.requestAnimationFrame(() => {
      setScanAttachRecordId(record.id);
      setMobileSheet("scan");
    });
  }

  async function logMaintenanceDone(record) {
    if (!record?.id || maintenanceLogBusyId) return;
    setError("");
    setMaintenanceLogBusyId(record.id);
    try {
      const updated = await householdRecordsApi.completeMaintenance(record.id);
      setRecords(prev => prev.map(item => item.id === record.id ? updated : item));
      await loadRecords();
      toast?.addToast?.(updated?.due_date ? `Done ✓ — next due ${updated.due_date}` : "Maintenance logged", "success");
    } catch (err) {
      toast?.addToast?.("Could not log maintenance", "error");
      setError(err.message || "Could not log maintenance");
    } finally {
      setMaintenanceLogBusyId(null);
    }
  }

  function buyPartsForRecord(record) {
    setVaultToolsMode("shops");
    setVaultToolsQuery(`${record.title || ""} ${record.model_number || ""} replacement parts`.trim());
    setMobileSheet(null);
    window.requestAnimationFrame(() => setMobileSheet("tools"));
  }

  async function submitQuoteCheck(event) {
    event.preventDefault();
    if (!quoteDraft.service_type.trim() || !quoteDraft.quote_amount) {
      setVaultAiError("Add the service type and quote amount first.");
      return;
    }
    setShowQuoteForm(false);
    await runVaultAi("quote_check", false, null, quoteDraft);
  }

  async function saveAiBudget(result) {
    setOwnerEnhancementBusy(true);
    setVaultAiError("");
    const tid = toast?.addToast?.("Saving AI budget…", "loading", { persist: true });
    try {
      const saved = await vaultApi.saveAiBudget({ result });
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, `Saved ${saved.saved || 0} budget item${saved.saved === 1 ? "" : "s"}`, "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not save AI budget", "error");
      setVaultAiError(err.message || "Could not save AI budget");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  async function runVaultAi(type, force = false, itemOverride = null, overrides = {}) {
    const item = itemOverride || selectedVaultAiItem;
    const otherClaim = type === "claim" ? overrides.other_item : null;
    if ((type === "score" || type === "ask") && !item) {
      setVaultAiError("Add or select a vault item first.");
      return;
    }
    if (type === "claim" && !item && !otherClaim?.name?.trim()) {
      setVaultAiError("Select a vault item or add an off-vault item name first.");
      return;
    }
    if (type === "improvement_roi" && !selectedImprovement) {
      setVaultAiError("Add an improvement first.");
      return;
    }
    setVaultAiBusy(type);
    setVaultAiResultSheetOpen(true);
    setVaultAiResult(null);
    setVaultAiResultItem(item || (otherClaim ? { title: otherClaim.name, category: otherClaim.category, amount: otherClaim.estimated_value } : null));
    setVaultAiError("");
    setVaultAiConfirm(null);
    try {
      let result;
      if (type === "score") {
        result = await vaultApi.scoreItem(item.id, { force });
      } else if (type === "audit") {
        result = await vaultApi.audit({ force });
      } else if (type === "ask") {
        result = await vaultApi.askItem(item.id, { question: vaultAiQuestion.trim() });
      } else if (type === "claim") {
        if (otherClaim?.name?.trim()) {
          result = await vaultApi.claimOther({
            item_name: otherClaim.name,
            category: otherClaim.category,
            estimated_value: otherClaim.estimated_value,
            damage_description: (overrides.damage_description ?? "").trim(),
          });
        } else {
          result = await vaultApi.claim(item.id, { damage_description: (overrides.damage_description ?? "").trim(), force });
        }
      } else if (type === "lease") {
        result = await vaultApi.negotiateLease({ force });
      } else if (type === "budget_plan") {
        result = await vaultApi.aiBudget({ force });
      } else if (type === "improvement_roi") {
        result = await vaultApi.improvementRoi(selectedImprovement.id, { force });
      } else if (type === "presale_audit") {
        result = await vaultApi.presaleAudit({ force });
      } else if (type === "quote_check") {
        const payload = {
          ...overrides,
          vault_item_id: overrides.vault_item_id || "",
          item_name: overrides.vault_item_id ? records.find(row => String(row.id) === String(overrides.vault_item_id))?.title : overrides.item_name,
        };
        result = await vaultApi.quoteCheck(payload);
      }
      if (result?.needs_confirm) {
        setVaultAiConfirm({ type, report: result.report });
        setVaultAiResult({ type, data: result.report?.result_json || {} });
        setVaultAiResultSheetOpen(true);
        return;
      }
      if (result?.ai_usage) applyAiUsage?.(result.ai_usage);
      setVaultAiResult({ type, data: result || {} });
      setVaultAiResultSheetOpen(true);
      if (result?.condition === "poor" || result?.cross_module_log || ["improvement_roi", "presale_audit"].includes(type)) {
        loadRecords();
        loadOwnerEnhancements(ownerSeason);
      }
    } catch (err) {
      setVaultAiError(err.message || "Could not generate this Home Vault AI card");
    } finally {
      setVaultAiBusy("");
    }
  }

  function setInlineVaultAction(recordId, updater) {
    setInlineVaultActions(current => ({
      ...current,
      [recordId]: typeof updater === "function" ? updater(current[recordId] || {}) : updater,
    }));
  }

  function cacheInlineVaultAction(recordId, action) {
    if (!recordId || !action?.type || !action?.data || action.busy || action.error) return;
    setInlineVaultActions(current => {
      const cachedAction = {
        type: action.type,
        data: action.data,
        report: action.report || null,
        quoteDraft: action.quoteDraft || null,
        showForm: false,
        busy: false,
        error: "",
        confirm: false,
        cachedAt: new Date().toISOString(),
      };
      const next = { ...current, [recordId]: cachedAction };
      try { localStorage.setItem(inlineVaultCacheKey, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  function closeInlineVaultAction(recordId) {
    setVaultActionSheetRecordId(current => String(current) === String(recordId) ? null : current);
  }

  async function runInlineVaultAction(record, type, force = false) {
    const recordId = record.id;
    const current = inlineVaultActions[recordId];
    setVaultActionSheetRecordId(recordId);
    if (!force && current?.type === type && current?.data && !current?.showForm) {
      setVaultActionNotice("");
      return;
    }
    if (type === "quote_check") {
      setVaultActionNotice("Opening quote check...");
      setInlineVaultAction(recordId, {
        type,
        showForm: true,
        quoteDraft: {
          provider: force ? (current?.quoteDraft?.provider || "") : "",
          quote_amount: "",
          service_type: current?.quoteDraft?.service_type || record.category || record.record_type || "Home service",
        },
      });
      window.setTimeout(() => setVaultActionNotice(""), 300);
      return;
    }
    const noticeByType = {
      score: force ? "Regenerating keep/replace card..." : "Checking keep or replace...",
      claim: force ? "Regenerating claim summary..." : "Building claim summary...",
    };
    setVaultActionNotice(noticeByType[type] || "Checking this home record...");
    setInlineVaultAction(recordId, { type, busy: true, error: "", data: force ? current?.data : null, confirm: false });
    try {
      let result;
      if (type === "score") {
        result = await vaultApi.scoreItem(record.id, { force });
      } else if (type === "claim") {
        result = await vaultApi.claim(record.id, { damage_description: "", force });
      }
      if (result?.needs_confirm) {
        cacheInlineVaultAction(recordId, {
          type,
          busy: false,
          error: "",
          data: result.report?.result_json || {},
          report: result.report,
          confirm: false,
        });
        setInlineVaultAction(recordId, {
          type,
          busy: false,
          error: "",
          data: result.report?.result_json || {},
          report: result.report,
          confirm: true,
        });
        return;
      }
      if (result?.ai_usage) applyAiUsage?.(result.ai_usage);
      cacheInlineVaultAction(recordId, { type, busy: false, error: "", data: result || {}, report: result?.report || null, confirm: false });
      if (result?.condition === "poor" || result?.cross_module_log) {
        loadRecords();
        loadOwnerEnhancements(ownerSeason);
      }
    } catch (err) {
      setInlineVaultAction(recordId, currentState => ({ ...currentState, type, busy: false, error: err.message || "Could not generate this Home Vault card" }));
    } finally {
      setVaultActionNotice("");
    }
  }

  function updateInlineQuote(record, field, value) {
    setInlineVaultAction(record.id, current => ({
      ...current,
      quoteDraft: { ...(current.quoteDraft || {}), [field]: value },
    }));
  }

  async function submitInlineQuote(event, record) {
    event.preventDefault();
    const current = inlineVaultActions[record.id] || {};
    const draft = current.quoteDraft || {};
    if (!draft.quote_amount) {
      setInlineVaultAction(record.id, state => ({ ...state, error: "Add the quote amount first." }));
      return;
    }
    setVaultActionNotice("Checking quote fairness...");
    setInlineVaultAction(record.id, state => ({ ...state, busy: true, error: "" }));
    try {
      const payload = {
        vault_item_id: record.id,
        item_name: record.title,
        brand: record.retailer || "",
        service_type: draft.service_type || record.category || record.record_type || "Home service",
        provider: draft.provider || "",
        quote_provider: draft.provider || "",
        quote_amount: draft.quote_amount,
      };
      const result = await vaultApi.quoteCheck(payload);
      if (result?.ai_usage) applyAiUsage?.(result.ai_usage);
      cacheInlineVaultAction(record.id, {
        type: "quote_check",
        busy: false,
        showForm: false,
        error: "",
        quoteDraft: draft,
        data: result || {},
      });
      if (result?.cross_module_log) loadRecords();
    } catch (err) {
      setInlineVaultAction(record.id, state => ({ ...state, busy: false, error: err.message || "Could not check this quote" }));
    } finally {
      setVaultActionNotice("");
    }
  }

  async function addImprovementToExport(improvement) {
    if (!improvement?.id) {
      openImprovementSheet(improvement || null);
      return;
    }
    setOwnerEnhancementBusy(true);
    const tid = toast?.addToast?.("Adding to sale export...", "loading", { persist: true });
    try {
      await vaultApi.updateImprovement(improvement.id, { ...improvement, include_in_export: true });
      await loadOwnerEnhancements(ownerSeason);
      toast?.updateToast?.(tid, "Included in sale export", "success");
    } catch (err) {
      toast?.updateToast?.(tid, "Could not update sale export", "error");
      setError(err.message || "Could not add this improvement to the sale export");
    } finally {
      setOwnerEnhancementBusy(false);
    }
  }

  function openVaultActionTools(item, mode, queryOverride = "") {
    const query = queryOverride || vaultLookupQuery(item || {}) || [item?.title, item?.name, item?.category].filter(Boolean).join(" ");
    setVaultToolsMode(mode);
    setVaultToolsQuery(query);
    setVaultAiResultSheetOpen(false);
    setVaultActionSheetRecordId(null);
    setMobileSheet("tools");
  }

  function promptFromVaultCard(prompt) {
    if (prompt?.type === "find_service") {
      openVaultActionTools(prompt.item, "services", prompt.query);
      return;
    }
    if (prompt?.type === "compare_replacements") {
      openVaultActionTools(prompt.item, "shops", prompt.query);
      return;
    }
    if (prompt?.type === "file_claim") {
      setVaultActionSheetRecordId(null);
      runVaultAi("claim", false, prompt.item);
      return;
    }
    if (prompt?.type === "generate_export") {
      exportVault();
      return;
    }
    if (prompt?.type === "add_improvement_to_export") {
      addImprovementToExport(prompt.improvement);
      return;
    }
    const contextualQuestion = `${prompt} Focus on Home Vault.`;
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: contextualQuestion,
        context: {
          module: "vault",
          module_label: "Home Vault",
          surface: "Home Vault AI card",
        },
      },
    }));
  }

  function askGlobalFromVault(prompt, surface = "Vault details") {
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: `${prompt} Focus on Home Vault.`,
        context: {
          module: "vault",
          module_label: "Home Vault",
          surface,
        },
      },
    }));
  }

  function toggleExpandedRecord(record) {
    setExpandedRecordId(current => current === record.id ? null : record.id);
    setVaultAiItemId(record.id);
  }

  function toggleVaultAiWorkbench() {
    setVaultAiOpen(current => {
      const next = !current;
      try { localStorage.setItem("hm_vault_ai_workbench_open", String(next)); } catch {}
      return next;
    });
  }

  return (
    <div className={`hm-vault-page hm-vault-page--${propertyType}`}>
      {showMaintenanceLogging ? (
        <SavingNotice title="Logging maintenance" message="Updating this maintenance reminder..." />
      ) : showSaving ? (
        <SavingNotice title={exportBusy ? "Exporting" : deleteBusyId ? "Removing" : "Saving"} message={exportBusy ? "Preparing Home Vault PDF..." : deleteBusyId ? "Removing Home Vault record..." : "Updating Home Vault..."} />
      ) : showAsking ? (
        <SavingNotice title="Thinking" message={vaultActionNotice || "Checking this home record..."} />
      ) : null}
      <ModuleHeader
        title="Home Vault"
        subtitle="Warranties, receipts, manuals, appliances, and maintenance reminders — organized for coverage and service."
        image={IMG_VAULT}
        className="hm-vault-module-header"
      >
        {records.length > 0 && (
          <div className="hm-module-stat">{records.length}<span>items</span></div>
        )}
        {expiring > 0 && (
          <div className="hm-module-stat" style={{ background: "rgba(245,166,35,0.35)" }}>
            {expiring}<span>expiring soon</span>
          </div>
        )}
        {expired > 0 && (
          <div className="hm-module-stat" style={{ background: "rgba(255,77,77,0.35)" }}>
            {expired}<span>expired</span>
          </div>
        )}
      </ModuleHeader>

      {error      && <div className="hm-inline-error"   style={{ marginBottom: 12 }}>{error}</div>}
      {successMsg && <div className="hm-inline-success" style={{ marginBottom: 12 }}>{successMsg}</div>}

      <div className="hm-vault-mode-control">
        <div>
          <span>{propertyType === "owner" ? "Owner mode" : "Renter mode"}</span>
          <strong>{propertyType === "owner" ? "Appliances, warranties, recalls, and home health" : "Lease, deposit, insurance, and rental decisions"}</strong>
        </div>
        <button type="button" onClick={togglePropertyType}>
          Switch to {propertyType === "owner" ? "Renter" : "Owner"} view
        </button>
      </div>

      <button type="button" className="hm-vault-contract-shortcut" onClick={() => document.getElementById("contracts")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
        <span>📄</span><span><strong>Contracts & agreements</strong><small>Scan a lease, policy, HOA agreement, or service contract</small></span><b>Open ›</b>
      </button>

      <VaultInsightStrip records={records} expiring={expiring} expired={expired} />

      <div className={`hm-vault-mode-summary hm-vault-mode-summary--${propertyType}`}>
        {propertyType === "owner" ? (
          <>
            <article><span>📦 Items tracked</span><strong>{records.length}</strong></article>
            <article><span>💚 Home health</span><strong>{healthSummary?.score ?? 100}</strong></article>
            <article><span>💰 Total insured value</span><strong>${Math.round(totalInsuredValue).toLocaleString()}</strong></article>
            <article><span>⚠️ Open recalls</span><strong>{openRecallCount}</strong></article>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setShowLeaseSheet(true)}><span>💳 Monthly rent</span><strong>{lease?.monthly_rent ? `$${Math.round(lease.monthly_rent).toLocaleString()}` : "Add lease"}</strong></button>
            <button type="button" onClick={openMoveInLog}><span>🔐 Deposit</span><strong>{lease?.deposit_amount ? `$${Math.round(lease.deposit_amount).toLocaleString()}` : "Unknown"}</strong></button>
            <button type="button" onClick={openInsuranceSheet}><span>🛡️ Property covered</span><strong>{records.some(record => record.category === "insurance") ? "Active" : "Add policy"}</strong></button>
            <button type="button" onClick={() => setShowLeaseSheet(true)}><span>📅 Days to renewal</span><strong>{daysToRenewal !== null ? Math.max(daysToRenewal, 0) : "Set date"}</strong></button>
          </>
        )}
      </div>

      {propertyType === "renter" && (
        <RenterVaultDashboard
          records={records}
          lease={lease}
          daysToRenewal={daysToRenewal}
          onEditLease={() => setShowLeaseSheet(true)}
          onEditInsurance={openInsuranceSheet}
          onMoveInLog={openMoveInLog}
          onLeaseAdvisor={() => runVaultAi("lease")}
          onRenterGuide={setRenterGuideSheet}
          onGlobalAsk={(prompt) => askGlobalFromVault(prompt, "Renter dashboard")}
        />
      )}

      {propertyType === "owner" && (
        <OwnerEnhancementPanel
          records={records}
          budgetItems={ownerBudget}
          improvements={ownerImprovements}
          contractors={ownerContractors}
          suggestions={ownerSuggestions}
          seasonalTasks={ownerSeasonal}
          exportSummary={ownerExportSummary}
          activeSeason={ownerSeason}
          setActiveSeason={setOwnerSeason}
          onGenerateBudget={refreshOwnerBudget}
          onAddBudget={() => openBudgetSheet()}
          onEditBudget={openBudgetSheet}
          onMarkBudgetDone={markBudgetDone}
          onSeasonalTaskDone={completeSeasonalTask}
          onPlanSeasonalTask={openBudgetSheetFromSeasonalTask}
          onEditImprovement={openImprovementSheet}
          onEditContractor={openContractorSheet}
          onExport={exportVault}
          onPresaleAudit={() => runVaultAi("presale_audit")}
          claimItemId={claimItemId}
          setClaimItemId={setClaimItemId}
          claimDamage={claimDamage}
          setClaimDamage={setClaimDamage}
          claimOtherDraft={claimOtherDraft}
          setClaimOtherDraft={setClaimOtherDraft}
          onClaim={(item, damage, otherItem) => runVaultAi("claim", false, item, { damage_description: damage || "", other_item: otherItem })}
        />
      )}

      <section className={`hm-vault-ai-workbench${vaultAiOpen ? " is-open" : " is-collapsed"}`}>
        <button type="button" className="hm-vault-section-toggle hm-vault-ai-workbench-head" onClick={toggleVaultAiWorkbench} aria-expanded={vaultAiOpen}>
          <div>
            <span>{propertyType === "owner" ? "✦ Owner intelligence" : "✦ Renter intelligence"}</span>
            <strong>{propertyType === "owner" ? "Health audit, appliance answers, and quote checks" : "Rental health, item answers, and quote checks"}</strong>
          </div>
          <em>Tools</em>
          <b>{vaultAiOpen ? "⌃" : "⌄"}</b>
        </button>
        {vaultAiOpen && (
          <>
        {propertyType === "owner" ? (
          <div className="hm-vault-ai-owner-grid hm-vault-ai-owner-grid--focused">
            <section className="hm-vault-ai-control-group is-primary">
              <div><span>Whole home</span><strong>Health audit</strong></div>
              <button type="button" className="hm-vault-ai-wide-action is-primary" onClick={() => runVaultAi("audit")} disabled={!!vaultAiBusy || records.length === 0}>
                {vaultAiBusy === "audit" ? "Auditing..." : "Run health audit"}
              </button>
            </section>

            <section className="hm-vault-ai-control-group">
              <div><span>Selected item</span><strong>{selectedVaultAiItem?.title || "Select an item"}</strong></div>
              {records.length > 0 && (
                <select value={selectedVaultAiItem?.id || ""} onChange={event => setVaultAiItemId(event.target.value)} aria-label="Choose Home Vault item for AI">
                  {records.map(record => <option key={record.id} value={record.id}>{record.title}</option>)}
                </select>
              )}
              <div className="hm-vault-ai-controls">
                <button type="button" onClick={() => runVaultAi("score")} disabled={!!vaultAiBusy || !selectedVaultAiItem}>
                  {vaultAiBusy === "score" ? "Scoring..." : "Keep / replace"}
                </button>
                <button type="button" onClick={() => openQuoteForm()} disabled={!!vaultAiBusy}>
                  Quote check
                </button>
              </div>
            </section>
          </div>
        ) : (
          <div className="hm-vault-ai-owner-grid hm-vault-ai-owner-grid--focused">
            <section className="hm-vault-ai-control-group is-primary">
              <div><span>Rental home</span><strong>Health audit</strong></div>
              <button type="button" className="hm-vault-ai-wide-action is-primary" onClick={() => runVaultAi("audit")} disabled={!!vaultAiBusy || records.length === 0}>
                {vaultAiBusy === "audit" ? "Auditing..." : "Run health audit"}
              </button>
            </section>

            <section className="hm-vault-ai-control-group">
              <div><span>Selected item</span><strong>{selectedVaultAiItem?.title || "Select an item"}</strong></div>
              {records.length > 0 && (
                <select value={selectedVaultAiItem?.id || ""} onChange={event => setVaultAiItemId(event.target.value)} aria-label="Choose Home Vault item for AI">
                  {records.map(record => <option key={record.id} value={record.id}>{record.title}</option>)}
                </select>
              )}
              <div className="hm-vault-ai-controls">
                <button type="button" onClick={() => runVaultAi("score")} disabled={!!vaultAiBusy || !selectedVaultAiItem}>
                  {vaultAiBusy === "score" ? "Scoring..." : "Keep / replace"}
                </button>
                <button type="button" onClick={() => openQuoteForm()} disabled={!!vaultAiBusy}>
                  Quote check
                </button>
              </div>
            </section>
          </div>
        )}
        {showQuoteForm && (
          <form className="hm-vault-quote-form" onSubmit={submitQuoteCheck}>
            <label>
              <span>Vault item</span>
              <select value={quoteDraft.vault_item_id || ""} onChange={event => setQuoteDraft(draft => ({ ...draft, vault_item_id: event.target.value }))}>
                <option value="">No item / global quote</option>
                {records.map(record => <option key={record.id} value={record.id}>{record.title}</option>)}
              </select>
            </label>
            <label>
              <span>Service type</span>
              <input value={quoteDraft.service_type} onChange={event => setQuoteDraft(draft => ({ ...draft, service_type: event.target.value }))} placeholder="HVAC tune-up, plumbing repair..." />
            </label>
            <label>
              <span>Provider</span>
              <input value={quoteDraft.provider} onChange={event => setQuoteDraft(draft => ({ ...draft, provider: event.target.value }))} placeholder="Company name" />
            </label>
            <label>
              <span>Quote amount</span>
              <input type="number" min="0" step="1" value={quoteDraft.quote_amount} onChange={event => setQuoteDraft(draft => ({ ...draft, quote_amount: event.target.value }))} placeholder="650" />
            </label>
            <div className="hm-vault-quote-actions">
              <button type="button" onClick={() => setShowQuoteForm(false)}>Close</button>
              <button type="submit" className="is-primary" disabled={!!vaultAiBusy}>{vaultAiBusy === "quote_check" ? "Checking..." : "Check"}</button>
            </div>
          </form>
        )}
        <div className="hm-vault-ai-input-row">
          <label>
            <span>Ask appliance</span>
            <input value={vaultAiQuestion} onChange={event => setVaultAiQuestion(event.target.value)} placeholder="Describe the issue or question..." />
          </label>
          <button type="button" onClick={() => runVaultAi("ask")} disabled={!!vaultAiBusy || !selectedVaultAiItem || !vaultAiQuestion.trim()}>
            {vaultAiBusy === "ask" ? "Asking..." : "Ask item"}
          </button>
        </div>
          </>
        )}
      </section>

      <div className="hm-vault-mobile-command">
        <div className="hm-vault-mobile-stats">
          <button type="button" onClick={() => setActiveTab("warranty")}>
            <strong>{warrantyCount}</strong>
            <span>Warranties</span>
          </button>
          <button type="button" onClick={() => setActiveTab("appliance")}>
            <strong>{applianceCount}</strong>
            <span>Appliances</span>
          </button>
          <button type="button" onClick={() => setActiveTab("maintenance")}>
            <strong>{maintenanceCount}</strong>
            <span>Maintenance</span>
          </button>
        </div>
        <div className="hm-vault-mobile-actions">
          <button type="button" className="is-primary" onClick={() => { setForm(BLANK_FORM); setShowForm(true); }}>+ Add item</button>
          <button type="button" onClick={() => { setScanAttachRecordId(null); setMobileSheet("scan"); }}>📸 Scan doc</button>
          <button type="button" onClick={() => setMobileSheet("ask")}>✦ Ask home</button>
          <button type="button" aria-label="More tools" onClick={() => { setVaultToolsMode("all"); setVaultToolsQuery(""); setMobileSheet("tools"); }}>···</button>
        </div>
      </div>

      {/* ── Contracts & Agreements section ── */}
      <ContractsSection />

      <div className="hm-vault-mobile-tabs">
        {VaultTabs}
      </div>

      <div className="hm-vault-layout">

        {/* ── Left: list ── */}
        <div className="hm-vault-main">
          <div className="hm-vault-desktop-tabs">
            {VaultTabs}
          </div>

          {loading ? (
            <div className="hm-list">
              <article className="hm-item"><span style={{ color: "var(--hm-muted)" }}>Loading…</span></article>
            </div>
          ) : filtered.length === 0 && activeTab !== "all" ? (
            <div className="hm-vault-empty">
              {`No ${TYPE_CONFIG[activeTab]?.label.toLowerCase()} records yet.`}
            </div>
          ) : (
            <div className="hm-vault-grouped-list">
              {groupedVaultRecords.map(group => {
                const isGroupOpen = !!vaultGroupOpen[group.id];
                return (
                  <section key={group.id} className={`hm-vault-group is-${group.id}${isGroupOpen ? " is-open" : " is-collapsed"}`}>
                    <button type="button" className="hm-vault-group-head" onClick={() => toggleVaultGroup(group.id)} aria-expanded={isGroupOpen}>
                      <span>{group.icon}</span>
                      <strong>{group.title}</strong>
                      <em>{group.records.length}</em>
                      <b>{isGroupOpen ? "⌃" : "⌄"}</b>
                    </button>
                    {isGroupOpen && (
                      <div className="hm-list hm-vault-group-list">
                        {group.records.length === 0 ? (
                          <div className="hm-vault-group-empty">{group.empty}</div>
                        ) : group.records.map((record, index) => {
                          const ws = record.record_type === "maintenance" ? dueStatus(record.due_date) : warrantyStatus(record.due_date);
                          const tc = TYPE_CONFIG[record.record_type] || TYPE_CONFIG.warranty;
                          const menuShouldOpenUp = group.records.length - index <= 2;
                          const isExpanded = expandedRecordId === record.id;
                          const canLookup = record.record_type === "appliance" || record.record_type === "manual" || record.record_type === "warranty" || record.category === "Home Systems";
                          const isDocumentRecord = group.id === "documents" || record.record_type === "document";
                          const handleRecordToggle = (event) => {
                            const target = event.target;
                            if (target?.closest?.(".hm-vault-expanded-shell, .hm-vault-mobile-record-menu, .hm-vault-record-menu-popover, button, a, input, select, textarea, form")) return;
                            if (isExpanded && !target?.closest?.(".hm-vault-record-main, .hm-vault-record-badges")) return;
                            if (isCompactSheetViewport()) {
                              setDetailRecordId(record.id);
                              setMobileSheet("recordDetails");
                              return;
                            }
                            toggleExpandedRecord(record);
                          };
                          return (
                            <article
                              className={`hm-vault-record hm-vault-record--compact${ws ? ` ${ws.cls}` : ""}${isExpanded ? " is-expanded" : ""}`}
                              key={record.id}
                              role="button"
                              tabIndex={0}
                              aria-expanded={isExpanded}
                              onClick={handleRecordToggle}
                              onKeyDown={event => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  if (isCompactSheetViewport()) {
                                    setDetailRecordId(record.id);
                                    setMobileSheet("recordDetails");
                                  } else {
                                    toggleExpandedRecord(record);
                                  }
                                }
                              }}
                            >
                              <div className="hm-vault-record-main">
                                <span className="hm-vault-record-icon">{presetIcon(record.title)}</span>
                                <div className="hm-vault-record-copy">
                                  <div className="hm-vault-record-title-row"><strong>{record.title}</strong></div>
                                  <div className="hm-vault-record-meta">
                                    {record.category && <span>{record.category}</span>}
                                    {record.retailer && <span>{record.retailer}</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="hm-vault-record-side">
                                <div className="hm-vault-record-badges">
                                  <span className="hm-vault-type-badge" style={{ color: tc.color, background: tc.bg }}>{tc.label}</span>
                                  {ws ? <span className={`hm-vault-expiry ${ws.cls}`}>{ws.label}</span> : record.due_date ? <span className="hm-vault-expiry-date">{formatDate(record.due_date)}</span> : null}
                                </div>
                                <div className="hm-vault-mobile-record-menu">
                                  <button type="button" onClick={(event) => { event.stopPropagation(); setRecordMenuId(current => current === record.id ? null : record.id); }} aria-label="More record actions">···</button>
                                  {recordMenuId === record.id && (
                                    <div className={`hm-vault-record-menu-popover${menuShouldOpenUp ? " is-up" : ""}`} onClick={event => event.stopPropagation()}>
                                      <button type="button" className="hm-vault-record-menu-close" onClick={() => setRecordMenuId(null)} aria-label="Close menu">×</button>
                                      {!isDocumentRecord && <button type="button" onClick={() => { openAttachScan(record); setRecordMenuId(null); }}><span>⛶</span> Scan document</button>}
                                      {canLookup && <button type="button" onClick={() => { openManualSheet(record); setRecordMenuId(null); }}><span>□</span> Manual details</button>}
                                      {canLookup && <button type="button" onClick={() => { openRecallSheet(record, !record.recall_checked_at); setRecordMenuId(null); }}><span>ⓘ</span> {record.has_open_recall ? "Recall details" : "Check recalls"}</button>}
                                      <button type="button" onClick={() => { openEditSheet(record); setRecordMenuId(null); }}><span>⌕</span> Edit item</button>
                                      <button type="button" className="is-danger" onClick={() => { setPendingDelete(record); setRecordMenuId(null); }}><span>▢</span> Remove</button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="hm-vault-expanded-shell" onClick={event => event.stopPropagation()}>
                                  <VaultExpandedDetails
                                    record={record}
                                    groupId={group.id}
                                    propertyType={propertyType}
                                    lease={lease}
                                    warrantyState={ws}
                                    inlineState={inlineVaultActions[record.id]}
                                    isLoggingMaintenance={String(maintenanceLogBusyId) === String(record.id)}
                                    onScore={() => runInlineVaultAction(record, "score")}
                                    onClaim={() => runInlineVaultAction(record, "claim")}
                                    onQuote={() => runInlineVaultAction(record, "quote_check")}
                                    onLogDone={() => logMaintenanceDone(record)}
                                    onBuyParts={() => buyPartsForRecord(record)}
                                    onAttachReceipt={() => openAttachScan(record)}
                                    onEdit={() => group.id === "maintenance" ? openIntervalSheet(record) : openEditSheet(record)}
                                    onDelete={() => setPendingDelete(record)}
                                    onGlobalAsk={(prompt = "Help me decide what to do with this Home Vault record.") => askGlobalFromVault(prompt, "Vault details")}
                                  />
                                </div>
                              )}
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Right: add panel ── */}
        <aside className="hm-vault-sidebar">
          <div className="hm-panel">
            <div className="hm-panel-head">
              <div>
                <p>Quick add</p>
                <h2>Add to vault</h2>
              </div>
              <button type="button" className="hm-vault-manual-btn" onClick={() => { setForm(BLANK_FORM); setShowForm(true); }}>
                Manual entry
              </button>
            </div>

            <select
              className="hm-auth-input"
              style={{ width: "100%", height: 40, fontSize: 13, padding: "0 10px", marginBottom: 10 }}
              value=""
              onChange={e => applyPreset(e.target.value)}
            >
              <option value="">Choose a common item...</option>
              {["Appliances", "Electronics", "Home Systems", "Maintenance", "Furniture"].map(cat => (
                <optgroup key={cat} label={cat}>
                  {VAULT_PRESETS.filter(p => p.category === cat).map(p => (
                    <option key={p.name} value={p.name}>{p.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p style={{ margin: "0 0 8px", color: "var(--hm-muted)", fontSize: 12 }}>Common items</p>
            <div className="hm-preset-pills">
              {VAULT_PRESETS.slice(0, 10).map(p => (
                <button key={p.name} type="button" onClick={() => applyPreset(p.name)}>{p.name}</button>
              ))}
            </div>
            <p style={{ margin: "12px 0 8px", color: "var(--hm-muted)", fontSize: 12 }}>Maintenance reminders</p>
            <div className="hm-preset-pills">
              {MAINTENANCE_PRESETS.map(p => (
                <button key={p.name} type="button" onClick={() => applyPreset(p.name)}>{p.name}</button>
              ))}
            </div>
          </div>

          {records.length > 0 && (
            <div className="hm-vault-tip">
              <strong>Reminders</strong>
              <p>Warranties and maintenance tasks are flagged automatically when dates are added.</p>
            </div>
          )}

          <div className="hm-vault-tools-card">
            <div className="hm-vault-tools-head">
              <div>
                <strong>Ask your home</strong>
                <p>Ask across saved receipts, warranties, manuals, model numbers, and AI notes.</p>
              </div>
              <button type="button" onClick={() => setShowHomeAsk(open => !open)}>
                {showHomeAsk ? "Close" : "Ask"}
              </button>
            </div>
            {showHomeAsk && (
              <>
                <select
                  className="hm-auth-input hm-vault-question-select"
                  value=""
                  onChange={e => setHomeQuestion(e.target.value)}
                  disabled={homeAskBusy || records.length === 0}
                >
                  <option value="">Use a suggested question...</option>
                  <option value="What warranties expire soon?">What warranties expire soon?</option>
                  <option value="What model numbers do I have saved?">What model numbers do I have saved?</option>
                  <option value="What maintenance should I remember?">What maintenance should I remember?</option>
                </select>
                <textarea
                  className="hm-auth-input"
                  rows={3}
                  value={homeQuestion}
                  onChange={e => setHomeQuestion(e.target.value)}
                  placeholder="Ask: What air filter size do I need? Which warranties expire soon?"
                />
                <button type="button" className="hm-vault-tool-primary" onClick={() => submitHomeAsk()} disabled={homeAskBusy || records.length === 0 || !homeQuestion.trim()}>
                  Ask Home Vault
                </button>
              </>
            )}
            {homeAnswer && (
              <details className="hm-vault-home-answer" open={showHomeAsk}>
                <summary>Latest answer</summary>
                <div>{homeAnswer}</div>
              </details>
            )}
            {homeAskHistory.length > 0 && (
              <details className="hm-vault-home-history">
                <summary>Recent home answers</summary>
                {homeAskHistory.map(item => (
                  <article key={item.id}>
                    <strong>{item.question}</strong>
                    <div>{item.answer}</div>
                  </article>
                ))}
              </details>
            )}
          </div>

          <div className="hm-vault-tools-card">
            <div>
              <strong>Vault tools</strong>
              <p>Download a PDF summary for insurance, service calls, moving, or personal backup.</p>
            </div>
            <button type="button" className="hm-vault-tool-primary" onClick={exportVault} disabled={exportBusy || records.length === 0}>
              Export records PDF
            </button>
          </div>

          <VaultAffiliateCard />
        </aside>
      </div>

      <VaultAddDrawer
        open={showForm}
        form={form}
        busy={busy}
        error={error}
        vehicles={vehicles}
        setField={setField}
        applyPreset={applyPreset}
        onClose={() => { setShowForm(false); setForm(BLANK_FORM); }}
        onSubmit={handleAdd}
      />

      {/* Scan section */}
      <VaultScanSection onSaved={loadRecords} onAiUsage={applyAiUsage} records={records} />

      <VaultMobileSheet open={mobileSheet === "scan"} title={scanAttachRecord ? `Scan for ${scanAttachRecord.title}` : "Scan document"} onClose={() => { setMobileSheet(null); setScanAttachRecordId(null); }}>
        <VaultScanSection compact records={records} attachRecord={scanAttachRecord} onSaved={() => { loadRecords(); setMobileSheet(null); setScanAttachRecordId(null); }} onAiUsage={applyAiUsage} />
      </VaultMobileSheet>

      <VaultMobileSheet open={mobileSheet === "ask"} title="Ask your home" onClose={() => setMobileSheet(null)}>
        <div className="hm-vault-mobile-ask">
          <p>Ask across saved receipts, warranties, manuals, model numbers, and AI notes about your home.</p>
          <input
            className="hm-auth-input"
            value={homeQuestion}
            onChange={e => setHomeQuestion(e.target.value)}
            placeholder="e.g. What model is my refrigerator?"
          />
          <button type="button" className="hm-vault-tool-primary" onClick={() => submitHomeAsk()} disabled={homeAskBusy || records.length === 0 || !homeQuestion.trim()}>
            {homeAskBusy ? "Thinking..." : "Ask your home →"}
          </button>
          {homeAnswer && <div className="hm-vault-ai-answer">{homeAnswer}</div>}
          {homeAskHistory.length > 0 && (
            <div className="hm-vault-mobile-history">
              <strong>Recent questions</strong>
              {homeAskHistory.slice(0, 3).map(item => (
                <button key={item.id} type="button" onClick={() => submitHomeAsk(item.question)}>
                  <span>{item.question}</span>
                  <em>{item.answer}</em>
                </button>
              ))}
            </div>
          )}
        </div>
      </VaultMobileSheet>

      <VaultMobileSheet open={mobileSheet === "recordAsk" && !!askRecord} title={askRecord ? `AI notes · ${askRecord.title}` : "AI notes"} onClose={() => { setMobileSheet(null); setAskRecordId(null); }}>
        {askRecord && (
          <div className="hm-vault-mobile-ask hm-vault-record-ai-sheet">
            <p className="hm-vault-record-ai-meta">
              {[askRecord.model_number, askRecord.category, askRecord.retailer].filter(Boolean).join(" · ") || "Saved Home Vault item"}
            </p>
            {(() => {
              const notes = parseAiNotes(askRecord.ai_notes);
              return notes.length > 0 && (
                <div className="hm-vault-ai-note-list">
                  <strong>{notes.length} saved note{notes.length === 1 ? "" : "s"}</strong>
                  {notes.map((note, index) => (
                    <article key={`${note.date}-${index}`} className="hm-vault-ai-note-card">
                      {note.date && <span>{note.date}</span>}
                      <h3>{note.question}</h3>
                      <div>{renderMarkdownText(note.answer)}</div>
                    </article>
                  ))}
                </div>
              );
            })()}
            <label className="hm-vault-ai-question">
              <span>Ask a new question</span>
            <textarea
              className="hm-auth-input"
              rows={2}
              value={askQuestion}
              onChange={e => setAskQuestion(e.target.value)}
              placeholder="e.g. When does my warranty expire?"
            />
            </label>
            <button type="button" className="hm-primary-action" onClick={() => submitAsk(askRecord)} disabled={askBusy || !askQuestion.trim()}>
              {askBusy ? "Thinking..." : "✦ Ask AI"}
            </button>
            {askAnswer && <div className="hm-vault-ai-answer hm-vault-ai-answer-card">{renderMarkdownText(askAnswer)}</div>}
          </div>
        )}
      </VaultMobileSheet>

      <VaultMobileSheet open={mobileSheet === "recordDetails" && !!detailRecord} title={detailRecord ? detailRecord.title : "Vault details"} onClose={() => { setMobileSheet(null); setDetailRecordId(null); }}>
        {detailRecord && (() => {
          const detailWarranty = detailRecord.record_type === "maintenance" ? dueStatus(detailRecord.due_date) : warrantyStatus(detailRecord.due_date);
          return (
            <VaultExpandedDetails
              record={detailRecord}
              groupId={vaultGroupId(detailRecord)}
              propertyType={propertyType}
              lease={lease}
              warrantyState={detailWarranty}
              inlineState={inlineVaultActions[detailRecord.id]}
              isLoggingMaintenance={String(maintenanceLogBusyId) === String(detailRecord.id)}
              onScore={() => runInlineVaultAction(detailRecord, "score")}
              onClaim={() => runInlineVaultAction(detailRecord, "claim")}
              onQuote={() => runInlineVaultAction(detailRecord, "quote_check")}
              onLogDone={() => {
                const recordToLog = detailRecord;
                logMaintenanceDone(recordToLog);
                setMobileSheet(null);
              }}
              onBuyParts={() => buyPartsForRecord(detailRecord)}
              onAttachReceipt={() => { setMobileSheet(null); openAttachScan(detailRecord); }}
              onEdit={() => vaultGroupId(detailRecord) === "maintenance" ? openIntervalSheet(detailRecord) : openEditSheet(detailRecord)}
              onDelete={() => { setMobileSheet(null); setPendingDelete(detailRecord); }}
              onGlobalAsk={(prompt = "Help me decide what to do with this Home Vault record.") => {
                setMobileSheet(null);
                askGlobalFromVault(prompt, "Vault details");
              }}
            />
          );
        })()}
      </VaultMobileSheet>

      <VaultMobileSheet
        open={vaultAiResultSheetOpen && (!!vaultAiBusy || !!vaultAiResult || !!vaultAiError)}
        title={vaultAiResultTitle}
        onClose={() => setVaultAiResultSheetOpen(false)}
        className="hm-vault-action-sheet hm-vault-ai-result-sheet"
      >
        <section className="hm-vault-action-sheet-result" onClick={event => event.stopPropagation()}>
          {vaultAiBusy && !vaultAiResult?.data && <div className="hm-vault-action-loading">Generating this Home Vault card...</div>}
          {vaultAiError && <div className="hm-inline-error">{vaultAiError}</div>}
          {vaultAiConfirm && (
            <div className="hm-vault-ai-confirm">
              <p>Generating will replace the current report.</p>
              <button type="button" onClick={() => applyCachedVaultReport(vaultAiConfirm.type, vaultAiConfirm.report)}>Keep current</button>
              <button type="button" className="is-primary" onClick={() => runVaultAi(vaultAiConfirm.type, true)}>Replace report</button>
            </div>
          )}
          <VaultAiResultCard
            result={vaultAiResult}
            item={vaultAiResultItem || selectedVaultAiItem}
            question={vaultAiQuestion}
            lease={lease}
            onAction={promptFromVaultCard}
            onSaveBudget={saveAiBudget}
            improvement={selectedImprovement}
          />
        </section>
      </VaultMobileSheet>

      <VaultMobileSheet
        open={!!renterGuideSheet}
        title={renterGuideTitle}
        onClose={() => setRenterGuideSheet("")}
        className="hm-vault-action-sheet hm-vault-renter-guide-sheet"
      >
        <VaultRenterGuideCard
          type={renterGuideSheet}
          lease={lease}
          insuranceRecord={renterInsuranceRecord}
          moveLog={renterMoveLog}
        />
      </VaultMobileSheet>

      <VaultMobileSheet
        open={!!vaultActionSheetRecord && !!vaultActionSheetState}
        title={vaultActionSheetTitle}
        onClose={() => vaultActionSheetRecord ? closeInlineVaultAction(vaultActionSheetRecord.id) : setVaultActionSheetRecordId(null)}
        className="hm-vault-action-sheet"
      >
        {vaultActionSheetRecord && vaultActionSheetState && (
          <VaultActionSheetContent
            state={vaultActionSheetState}
            record={vaultActionSheetRecord}
            warrantyState={vaultActionSheetRecord.record_type === "maintenance" ? dueStatus(vaultActionSheetRecord.due_date) : warrantyStatus(vaultActionSheetRecord.due_date)}
            question={vaultAiQuestion}
            onRegenerate={() => runInlineVaultAction(vaultActionSheetRecord, vaultActionSheetState.type, true)}
            onKeepCurrent={() => setInlineVaultAction(vaultActionSheetRecord.id, state => ({ ...state, confirm: false }))}
            onQuoteChange={(field, value) => updateInlineQuote(vaultActionSheetRecord, field, value)}
            onQuoteSubmit={event => submitInlineQuote(event, vaultActionSheetRecord)}
            onAction={(prompt = "Help me decide what to do with this Home Vault record.") => {
              if (typeof prompt === "object" && prompt?.type) {
                promptFromVaultCard(prompt);
                return;
              }
              askGlobalFromVault(prompt, "Vault action sheet");
            }}
          />
        )}
      </VaultMobileSheet>

      <VaultMobileSheet open={!!intervalRecord} title={intervalRecord ? `Set interval · ${intervalRecord.title}` : "Set interval"} onClose={closeIntervalSheet} className="hm-vault-edit-sheet hm-vault-interval-sheet">
        {intervalRecord && (
          <form className="hm-vault-edit-card" onSubmit={saveInterval}>
            <div className="hm-vault-edit-grid">
              <label className="hm-vault-edit-field hm-vault-edit-span2">
                <span>Service interval</span>
                <select className="hm-auth-input" value={intervalDraft.amount || ""}
                  onChange={event => {
                    const value = event.target.value;
                    setIntervalDraft(draft => ({
                      ...draft,
                      amount: value,
                      due_date: value ? nextDateForInterval(value) : draft.due_date,
                    }));
                  }}>
                  <option value="">One-time / no repeat</option>
                  <option value="Every 30 days">Monthly (30 days)</option>
                  <option value="Every 90 days">Every 3 months</option>
                  <option value="Every 6 months">Every 6 months</option>
                  <option value="Every 12 months">Yearly</option>
                </select>
              </label>
              <label className="hm-vault-edit-field hm-vault-edit-span2">
                <span>Next reminder</span>
                <input type="date" className="hm-auth-input" value={intervalDraft.due_date || ""}
                  onChange={event => setIntervalDraft(draft => ({ ...draft, due_date: event.target.value }))} />
              </label>
            </div>
            <div className="hm-vault-edit-actions">
              <button type="button" className="hm-vault-cancel-btn" onClick={closeIntervalSheet}>Cancel</button>
              <button type="submit" className="hm-vault-save-btn" disabled={intervalBusy}>
                {intervalBusy ? "Saving..." : "Save interval"}
              </button>
            </div>
          </form>
        )}
      </VaultMobileSheet>

      <VaultMobileSheet open={!!editingRecord} title={editingRecord ? `Edit ${editingRecord.title}` : "Edit item"} onClose={cancelEdit} className="hm-vault-edit-sheet">
        {editingRecord && (
          <form className="hm-vault-edit-card" onSubmit={(event) => { event.preventDefault(); saveEdit(editingRecord.id); }}>
            <div className="hm-vault-edit-grid">
              <label className="hm-vault-edit-field hm-vault-edit-span2">
                <span>Item name</span>
                <input className="hm-auth-input" value={editDraft.title || ""}
                  onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))} autoFocus />
              </label>
              <label className="hm-vault-edit-field">
                <span>Type</span>
                <select className="hm-auth-input" value={editDraft.record_type || "warranty"}
                  onChange={e => {
                    const nextType = e.target.value;
                    setEditDraft(d => ({
                      ...d,
                      record_type: nextType,
                      category: nextType === "maintenance" ? "Maintenance" : nextType === "document" ? "Documents" : d.category,
                    }));
                  }}>
                  {VAULT_TYPES.map(t => <option key={t} value={t}>{TYPE_CONFIG[t].label}</option>)}
                </select>
              </label>
              <label className="hm-vault-edit-field">
                <span>Category</span>
                <input className="hm-auth-input" placeholder="Appliances, Electronics..."
                  value={editDraft.category || ""}
                  onChange={e => setEditDraft(d => ({ ...d, category: e.target.value }))} />
              </label>
              <label className="hm-vault-edit-field">
                <span>{editDraft.record_type === "document" ? "Issuer" : editDraft.record_type === "appliance" ? "Brand" : "Retailer"}</span>
                <input className="hm-auth-input" placeholder="Best Buy, Home Depot..."
                  value={editDraft.retailer || ""}
                  onChange={e => setEditDraft(d => ({ ...d, retailer: e.target.value }))} />
              </label>
              <label className="hm-vault-edit-field">
                <span>{editDraft.record_type === "document" ? "Document / ID number" : "Model number"}</span>
                <input className="hm-auth-input" placeholder="Optional"
                  value={editDraft.model_number || ""}
                  onChange={e => setEditDraft(d => ({ ...d, model_number: e.target.value }))} />
              </label>
              <label className="hm-vault-edit-field">
                <span>{editDraft.record_type === "document" ? "Issue date" : "Purchase date"}</span>
                <input type="date" className="hm-auth-input" value={editDraft.purchase_date || ""}
                  onChange={e => setEditDraft(d => ({ ...d, purchase_date: e.target.value }))} />
              </label>
              <label className="hm-vault-edit-field">
                <span>{editDraft.record_type === "maintenance" ? "Next reminder" : editDraft.record_type === "document" ? "Expiry date" : "Warranty end date"}</span>
                <input type="date" className="hm-auth-input" value={editDraft.due_date || ""}
                  onChange={e => setEditDraft(d => ({ ...d, due_date: e.target.value }))} />
              </label>
              {editDraft.record_type === "maintenance" ? (
                <label className="hm-vault-edit-field">
                  <span>Service interval</span>
                  <select className="hm-auth-input" value={editDraft.amount || ""}
                    onChange={e => {
                      const value = e.target.value;
                      setEditDraft(d => ({
                        ...d,
                        amount: value,
                        due_date: value ? nextDateForInterval(value) : d.due_date,
                      }));
                    }}>
                    <option value="">One-time / no repeat</option>
                    <option value="Every 30 days">Monthly (30 days)</option>
                    <option value="Every 90 days">Every 3 months</option>
                    <option value="Every 6 months">Every 6 months</option>
                    <option value="Every 12 months">Yearly</option>
                  </select>
                </label>
              ) : (
                <label className="hm-vault-edit-field">
                  <span>Price paid</span>
                  <input className="hm-auth-input" placeholder="$499"
                    value={editDraft.amount || ""}
                    onChange={e => setEditDraft(d => ({ ...d, amount: e.target.value }))} />
                </label>
              )}
              <label className="hm-vault-edit-field hm-vault-edit-span2">
                <span>Notes</span>
                <textarea className="hm-auth-input" rows={3}
                  placeholder={editDraft.record_type === "maintenance" ? "Filter size, interval, service notes..." : editDraft.record_type === "document" ? "Renewal notes, issuing country/state, restrictions..." : "Serial number, warranty terms..."}
                  value={editDraft.details || ""}
                  onChange={e => setEditDraft(d => ({ ...d, details: e.target.value }))} />
              </label>
            </div>
            <div className="hm-vault-edit-actions">
              <button type="button" className="hm-vault-cancel-btn" onClick={cancelEdit}>Cancel</button>
              <button type="submit" className="hm-vault-save-btn" disabled={editBusy || !editDraft.title?.trim()}>
                {editBusy ? "Saving..." : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </VaultMobileSheet>

      <VaultMobileSheet open={manualSheet.open} title="Manual details" onClose={() => setManualSheet({ open: false, record: null, docs: [], busy: false, error: "" })} className="hm-vault-info-sheet">
        <div className="hm-vault-info-card">
          <p>Saved manual metadata for {manualSheet.record?.title || "this item"}. No raw manual file is stored.</p>
          {manualSheet.busy && <div className="hm-vault-empty">Loading manual metadata...</div>}
          {manualSheet.error && <div className="hm-inline-error">{manualSheet.error}</div>}
          {!manualSheet.busy && manualSheet.docs.length === 0 && (
            <div className="hm-vault-empty">No manual metadata saved for this item yet.</div>
          )}
          {manualSheet.docs.map(doc => (
            <article key={doc.id} className="hm-vault-info-row">
              <strong>{doc.file_name || "Manual metadata"}</strong>
              <span>{doc.uploaded_at ? formatDate(doc.uploaded_at.slice(0, 10)) : "Saved manual"}</span>
              <p>{Object.entries(doc.ocr_extracted_data || {}).filter(([, value]) => value).map(([key, value]) => `${key.replaceAll("_", " ")}: ${value}`).join(" · ") || "No extracted fields saved."}</p>
            </article>
          ))}
          <a className="hm-vault-info-link" href={vaultLookupLinks(manualSheet.record || {}).manual} target="_blank" rel="noopener noreferrer">
            Find manual online ↗
          </a>
        </div>
      </VaultMobileSheet>

      <VaultMobileSheet open={recallSheet.open} title="Recall check" onClose={() => setRecallSheet({ open: false, record: null, result: null, busy: false, error: "" })} className="hm-vault-info-sheet">
        <div className="hm-vault-info-card">
          <p>CPSC recall check for {recallSheet.record?.title || "this item"} using saved brand and product name.</p>
          {recallSheet.busy && <div className="hm-vault-empty">Checking recalls...</div>}
          {recallSheet.error && <div className="hm-inline-error">{recallSheet.error}</div>}
          {!recallSheet.busy && recallSheet.result && (() => {
            const details = parseRecallDetails(recallSheet.result.recall_details);
            return recallSheet.result.has_open_recall ? (
              <article className="hm-vault-info-row is-risk">
                <strong>Possible recall found</strong>
                <span>{details?.recall_date || recallSheet.result.recall_checked_at || "Checked"}</span>
                <p>{details?.hazard || details?.product_description || "Review this recall before use or service."}</p>
                {details?.recall_number && <em>Recall #{details.recall_number}</em>}
              </article>
            ) : (
              <article className="hm-vault-info-row is-good">
                <strong>No recall found</strong>
                <p>No matching CPSC recall was found from the saved item details.</p>
              </article>
            );
          })()}
          <button type="button" className="hm-vault-tool-primary" onClick={() => openRecallSheet(recallSheet.record, true)} disabled={recallSheet.busy || !recallSheet.record}>
            {recallSheet.busy ? "Checking..." : "Check again"}
          </button>
        </div>
      </VaultMobileSheet>

      <VaultMobileSheet open={mobileSheet === "tools"} title={vaultToolsMode === "shops" ? "Parts & shops" : vaultToolsMode === "services" ? "Repair services" : "Tools & services"} onClose={() => setMobileSheet(null)}>
        <div className="hm-vault-mobile-tools">
          <p>{vaultToolsMode === "shops" ? "Choose where to shop for parts or replacement supplies." : vaultToolsMode === "services" ? "Find repair providers for this Home Vault item." : "Find repair help, parts, or extended protection for your home items."}</p>
          <VaultAffiliateCard mode={vaultToolsMode} query={vaultToolsQuery} />
          {vaultToolsMode === "all" && (
            <>
              <strong className="hm-vault-mobile-tool-label">Vault tools</strong>
              <button type="button" className="hm-vault-tool-primary hm-vault-export-card" onClick={exportVault} disabled={exportBusy || records.length === 0}>
                Export records PDF
              </button>
            </>
          )}
        </div>
      </VaultMobileSheet>

      {showLeaseSheet && (
        <VaultMobileSheet open={showLeaseSheet} title="Lease details" onClose={() => setShowLeaseSheet(false)} className="hm-vault-lease-sheet">
          <form className="hm-vault-renter-form hm-vault-lease-form" onSubmit={saveLeaseDetails}>
            <label><span>Property address</span><input className="hm-auth-input" value={leaseDraft.property_address} onChange={e => setLeaseDraft(d => ({ ...d, property_address: e.target.value }))} /></label>
            <label><span>Monthly rent</span><input className="hm-auth-input" value={leaseDraft.monthly_rent} onChange={e => setLeaseDraft(d => ({ ...d, monthly_rent: e.target.value }))} placeholder="1890" /></label>
            <label className="hm-vault-renter-money-field"><span>Deposit</span><input className="hm-auth-input" value={leaseDraft.deposit_amount} onChange={e => setLeaseDraft(d => ({ ...d, deposit_amount: e.target.value }))} placeholder="3780" /></label>
            <label><span>Lease start</span><input type="date" className="hm-auth-input" value={leaseDraft.lease_start} onChange={e => setLeaseDraft(d => ({ ...d, lease_start: e.target.value }))} /></label>
            <label><span>Lease end</span><input type="date" className="hm-auth-input" value={leaseDraft.lease_end} onChange={e => setLeaseDraft(d => ({ ...d, lease_end: e.target.value }))} /></label>
            <label><span>Notice days</span><input className="hm-auth-input" value={leaseDraft.notice_days_required} onChange={e => setLeaseDraft(d => ({ ...d, notice_days_required: e.target.value }))} placeholder="90" /></label>
            <label><span>Landlord</span><input className="hm-auth-input" value={leaseDraft.landlord_name} onChange={e => setLeaseDraft(d => ({ ...d, landlord_name: e.target.value }))} /></label>
            <label><span>Phone</span><input className="hm-auth-input" value={leaseDraft.landlord_phone} onChange={e => setLeaseDraft(d => ({ ...d, landlord_phone: e.target.value }))} /></label>
            <label><span>Email</span><input className="hm-auth-input" value={leaseDraft.landlord_email} onChange={e => setLeaseDraft(d => ({ ...d, landlord_email: e.target.value }))} /></label>
            <div className="hm-vault-renter-toggles">
              <label><input type="checkbox" checked={leaseDraft.autopay} onChange={e => setLeaseDraft(d => ({ ...d, autopay: e.target.checked }))} /> Autopay</label>
              <label><input type="checkbox" checked={leaseDraft.pets_allowed} onChange={e => setLeaseDraft(d => ({ ...d, pets_allowed: e.target.checked }))} /> Pets allowed</label>
              <label><input type="checkbox" checked={leaseDraft.parking_included} onChange={e => setLeaseDraft(d => ({ ...d, parking_included: e.target.checked }))} /> Parking included</label>
            </div>
            <label className="hm-vault-renter-span"><span>Notes</span><textarea className="hm-auth-input" rows={3} value={leaseDraft.notes} onChange={e => setLeaseDraft(d => ({ ...d, notes: e.target.value }))} /></label>
            <div className="hm-vault-renter-form-actions">
              <button type="button" className="hm-vault-cancel-btn" onClick={() => setShowLeaseSheet(false)}>Cancel</button>
              <button type="submit" className="hm-vault-tool-primary" disabled={leaseBusy}>{leaseBusy ? "Saving..." : "Save lease"}</button>
            </div>
          </form>
        </VaultMobileSheet>
      )}

      {showMoveInSheet && (
        <VaultMobileSheet open={showMoveInSheet} title="Move-in inventory log" onClose={() => setShowMoveInSheet(false)} className="hm-vault-inventory-sheet">
          <form className="hm-vault-renter-form hm-vault-inventory-form" onSubmit={saveMoveInLog}>
            <div className="hm-vault-renter-form-note">
              Log inventory and photo metadata only. HouseMind is not storing raw photo files yet.
            </div>
            <label><span>Photos documented</span><input className="hm-auth-input" value={moveInDraft.photos} onChange={e => setMoveInDraft(d => ({ ...d, photos: e.target.value }))} placeholder="5" /></label>
            <label><span>Damage items</span><input className="hm-auth-input" value={moveInDraft.damage} onChange={e => setMoveInDraft(d => ({ ...d, damage: e.target.value }))} placeholder="3" /></label>
            <label className="hm-vault-renter-span hm-vault-inventory-entry-field"><span>Room-by-room entries</span><textarea className="hm-auth-input" rows={4} value={moveInDraft.entries} onChange={e => setMoveInDraft(d => ({ ...d, entries: e.target.value }))} placeholder="Kitchen - cabinet chip - 2 photos&#10;Bedroom - carpet stain - 2 photos&#10;Bathroom - tile crack - 1 photo" /></label>
            <label className="hm-vault-renter-span hm-vault-inventory-notes-field"><span>Notes</span><textarea className="hm-auth-input" rows={3} value={moveInDraft.notes} onChange={e => setMoveInDraft(d => ({ ...d, notes: e.target.value }))} placeholder="Metadata only, no raw files stored." /></label>
            <div className="hm-vault-renter-form-actions">
              <button type="button" className="hm-vault-cancel-btn" onClick={() => setShowMoveInSheet(false)}>Cancel</button>
              <button type="submit" className="hm-vault-tool-primary" disabled={moveInBusy}>{moveInBusy ? "Saving..." : "Save log"}</button>
            </div>
          </form>
        </VaultMobileSheet>
      )}

      {showInsuranceSheet && (
        <VaultMobileSheet open={showInsuranceSheet} title="Renter insurance" onClose={() => setShowInsuranceSheet(false)} className="hm-vault-insurance-sheet">
          <form className="hm-vault-renter-form hm-vault-insurance-form" onSubmit={saveInsuranceDetails}>
            <div className="hm-vault-renter-form-note">
              Save policy metadata for coverage explanations and duplicate device-insurance checks.
            </div>
            <label><span>Provider</span><input className="hm-auth-input" value={insuranceDraft.provider} onChange={e => setInsuranceDraft(d => ({ ...d, provider: e.target.value }))} placeholder="State Farm" /></label>
            <label><span>Policy number</span><input className="hm-auth-input" value={insuranceDraft.policy_number} onChange={e => setInsuranceDraft(d => ({ ...d, policy_number: e.target.value }))} placeholder="POL-12345" /></label>
            <label><span>Property coverage</span><input className="hm-auth-input" value={insuranceDraft.coverage_amount} onChange={e => setInsuranceDraft(d => ({ ...d, coverage_amount: e.target.value }))} placeholder="$45,000" /></label>
            <label><span>Liability</span><input className="hm-auth-input" value={insuranceDraft.liability_amount} onChange={e => setInsuranceDraft(d => ({ ...d, liability_amount: e.target.value }))} placeholder="$100,000" /></label>
            <label><span>Deductible</span><input className="hm-auth-input" value={insuranceDraft.deductible} onChange={e => setInsuranceDraft(d => ({ ...d, deductible: e.target.value }))} placeholder="$500" /></label>
            <label><span>Renewal date</span><input type="date" className="hm-auth-input" value={insuranceDraft.renewal_date} onChange={e => setInsuranceDraft(d => ({ ...d, renewal_date: e.target.value }))} /></label>
            <label className="hm-vault-renter-span"><span>Agent / contact</span><input className="hm-auth-input" value={insuranceDraft.agent_contact} onChange={e => setInsuranceDraft(d => ({ ...d, agent_contact: e.target.value }))} placeholder="Agent name, phone, or email" /></label>
            <label className="hm-vault-renter-span"><span>Notes</span><textarea className="hm-auth-input" rows={3} value={insuranceDraft.notes} onChange={e => setInsuranceDraft(d => ({ ...d, notes: e.target.value }))} placeholder="Coverage exclusions, valuables rider, roommate notes..." /></label>
            <div className="hm-vault-renter-form-actions">
              <button type="button" className="hm-vault-cancel-btn" onClick={() => setShowInsuranceSheet(false)}>Cancel</button>
              <button type="submit" className="hm-vault-tool-primary" disabled={insuranceBusy}>{insuranceBusy ? "Saving..." : "Save policy"}</button>
            </div>
          </form>
        </VaultMobileSheet>
      )}

      {showBudgetSheet && (
        <VaultMobileSheet open={showBudgetSheet} title={budgetDraft.id ? "Edit budget item" : "Add budget item"} onClose={closeBudgetSheet} className="hm-vault-budget-sheet">
          <form className="hm-vault-renter-form hm-vault-budget-form" onSubmit={saveBudgetItem}>
            <label className="hm-vault-renter-span"><span>Name</span><input className="hm-auth-input" value={budgetDraft.name} onChange={e => setBudgetDraft(d => ({ ...d, name: e.target.value }))} placeholder="HVAC tune-up" /></label>
            <label><span>Status</span><select className="hm-auth-input" value={budgetDraft.status || "upcoming"} onChange={e => setBudgetDraft(d => ({ ...d, status: e.target.value }))}>
              <option value="upcoming">Upcoming</option>
              <option value="due">Due</option>
              <option value="done">Done</option>
              <option value="skipped">Skipped</option>
            </select></label>
            <label><span>Scheduled</span><input type="date" className="hm-auth-input" value={budgetDraft.scheduled_month} onChange={e => setBudgetDraft(d => ({ ...d, scheduled_month: e.target.value }))} /></label>
            <label><span>Low</span><input className="hm-auth-input" value={budgetDraft.estimated_cost_lo} onChange={e => setBudgetDraft(d => ({ ...d, estimated_cost_lo: e.target.value }))} placeholder="89" /></label>
            <label><span>High</span><input className="hm-auth-input" value={budgetDraft.estimated_cost_hi} onChange={e => setBudgetDraft(d => ({ ...d, estimated_cost_hi: e.target.value }))} placeholder="150" /></label>
            <label><span>Actual</span><input className="hm-auth-input" value={budgetDraft.actual_cost} onChange={e => setBudgetDraft(d => ({ ...d, actual_cost: e.target.value }))} placeholder="Optional" /></label>
            <label className="hm-vault-renter-span"><span>Notes</span><textarea className="hm-auth-input" rows={3} value={budgetDraft.notes} onChange={e => setBudgetDraft(d => ({ ...d, notes: e.target.value }))} /></label>
            <div className={`hm-vault-renter-form-actions hm-vault-budget-actions${budgetDraft.id ? " is-editing" : ""}`}>
              {budgetDraft.id && <button type="button" className="hm-vault-delete-btn" onClick={() => setPendingBudgetDelete({ id: budgetDraft.id, name: budgetDraft.name })} disabled={ownerEnhancementBusy}>Delete</button>}
              <button type="button" className="hm-vault-cancel-btn" onClick={closeBudgetSheet}>Cancel</button>
              <button type="submit" className="hm-vault-tool-primary" disabled={ownerEnhancementBusy || !budgetDraft.name?.trim()}>{ownerEnhancementBusy ? "Saving..." : budgetDraft.id ? "Save" : "Save item"}</button>
            </div>
          </form>
        </VaultMobileSheet>
      )}

      {showImprovementSheet && improvementDraft && (
        <VaultMobileSheet open={showImprovementSheet} title={improvementDraft.id ? "Edit improvement" : "Add improvement"} onClose={closeImprovementSheet} className="hm-vault-improvement-sheet">
          <form className="hm-vault-renter-form hm-vault-improvement-form" onSubmit={saveImprovement}>
            <label className="hm-vault-renter-span"><span>Project name</span><input className="hm-auth-input" value={improvementDraft.name} onChange={e => setImprovementDraft(d => ({ ...d, name: e.target.value }))} placeholder="Kitchen backsplash" /></label>
            <label><span>Category</span><input className="hm-auth-input" value={improvementDraft.category || ""} onChange={e => setImprovementDraft(d => ({ ...d, category: e.target.value }))} placeholder="kitchen" /></label>
            <label><span>Completed</span><input type="date" className="hm-auth-input" value={improvementDraft.completed_date || ""} onChange={e => setImprovementDraft(d => ({ ...d, completed_date: e.target.value }))} /></label>
            <label><span>Cost</span><input className="hm-auth-input" value={improvementDraft.cost || ""} onChange={e => setImprovementDraft(d => ({ ...d, cost: e.target.value }))} placeholder="2400" /></label>
            <label><span>ROI %</span><input className="hm-auth-input" value={improvementDraft.roi_pct || ""} onChange={e => setImprovementDraft(d => ({ ...d, roi_pct: e.target.value }))} placeholder="72" /></label>
            <label><span>Return est.</span><input className="hm-auth-input" value={improvementDraft.roi_return_est || ""} onChange={e => setImprovementDraft(d => ({ ...d, roi_return_est: e.target.value }))} placeholder="Auto" /></label>
            <label className="hm-vault-renter-span"><span>Contractor</span><input className="hm-auth-input" value={improvementDraft.contractor || ""} onChange={e => setImprovementDraft(d => ({ ...d, contractor: e.target.value }))} placeholder="Company or person" /></label>
            <label className="hm-vault-renter-span"><span>Notes</span><textarea className="hm-auth-input" rows={3} value={improvementDraft.notes || ""} onChange={e => setImprovementDraft(d => ({ ...d, notes: e.target.value }))} /></label>
            <div className="hm-vault-renter-toggles"><label><input type="checkbox" checked={!!improvementDraft.include_in_export} onChange={e => setImprovementDraft(d => ({ ...d, include_in_export: e.target.checked }))} /> Include in sale export</label></div>
            <div className={`hm-vault-renter-form-actions hm-vault-improvement-actions${improvementDraft.id ? " is-editing" : ""}`}>
              {improvementDraft.id && <button type="button" className="hm-vault-delete-btn" onClick={() => setPendingImprovementDelete({ id: improvementDraft.id, name: improvementDraft.name })} disabled={ownerEnhancementBusy}>Delete</button>}
              <button type="button" className="hm-vault-cancel-btn" onClick={closeImprovementSheet}>Cancel</button>
              <button type="submit" className="hm-vault-tool-primary" disabled={ownerEnhancementBusy || !improvementDraft.name?.trim()}>{ownerEnhancementBusy ? "Saving..." : improvementDraft.id ? "Save" : "Save project"}</button>
            </div>
          </form>
        </VaultMobileSheet>
      )}

      {showContractorSheet && contractorDraft && (
        <VaultMobileSheet open={showContractorSheet} title={contractorDraft.id ? "Edit contractor" : "Add contractor"} onClose={closeContractorSheet} className="hm-vault-contractor-sheet">
          <form className="hm-vault-renter-form hm-vault-contractor-form" onSubmit={saveContractor}>
            <label className="hm-vault-renter-span"><span>Name</span><input className="hm-auth-input" value={contractorDraft.name} onChange={e => setContractorDraft(d => ({ ...d, name: e.target.value }))} placeholder="ABC Heating" /></label>
            <label><span>Trade</span><input className="hm-auth-input" value={contractorDraft.trade || ""} onChange={e => setContractorDraft(d => ({ ...d, trade: e.target.value }))} placeholder="hvac" /></label>
            <label><span>Rating</span><input className="hm-auth-input" value={contractorDraft.rating || ""} onChange={e => setContractorDraft(d => ({ ...d, rating: e.target.value }))} placeholder="5" /></label>
            <label><span>Phone</span><input className="hm-auth-input" value={contractorDraft.phone || ""} onChange={e => setContractorDraft(d => ({ ...d, phone: e.target.value }))} /></label>
            <label><span>Email</span><input className="hm-auth-input" value={contractorDraft.email || ""} onChange={e => setContractorDraft(d => ({ ...d, email: e.target.value }))} /></label>
            <label><span>Rate</span><input className="hm-auth-input" value={contractorDraft.rate || ""} onChange={e => setContractorDraft(d => ({ ...d, rate: e.target.value }))} placeholder="$120/hr" /></label>
            <label><span>Last used</span><input type="date" className="hm-auth-input" value={contractorDraft.last_used_date || ""} onChange={e => setContractorDraft(d => ({ ...d, last_used_date: e.target.value }))} /></label>
            <label className="hm-vault-renter-span"><span>Notes</span><textarea className="hm-auth-input" rows={3} value={contractorDraft.notes || ""} onChange={e => setContractorDraft(d => ({ ...d, notes: e.target.value }))} /></label>
            <div className={`hm-vault-renter-form-actions hm-vault-contractor-actions${contractorDraft.id ? " is-editing" : ""}`}>
              {contractorDraft.id && <button type="button" className="hm-vault-delete-btn" onClick={() => setPendingContractorDelete({ id: contractorDraft.id, name: contractorDraft.name })} disabled={ownerEnhancementBusy}>Delete</button>}
              <button type="button" className="hm-vault-cancel-btn" onClick={closeContractorSheet}>Cancel</button>
              <button type="submit" className="hm-vault-tool-primary" disabled={ownerEnhancementBusy || !contractorDraft.name?.trim()}>{ownerEnhancementBusy ? "Saving..." : contractorDraft.id ? "Save" : "Save contractor"}</button>
            </div>
          </form>
        </VaultMobileSheet>
      )}

      {pendingDelete && (
        <ConfirmModal
          tag="⚠ Remove record"
          title="Remove this record?"
          message={`${pendingDelete.title} will be removed from Home Vault.`}
          confirmLabel="Remove"
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => deleteRecord(pendingDelete)}
          loading={deleteBusyId === pendingDelete.id}
        />
      )}

      {pendingBudgetDelete && (
        <ConfirmModal
          tag="Delete budget item"
          title="Delete this budget item?"
          message={`${pendingBudgetDelete.name || "This budget item"} will be removed from the maintenance budget.`}
          confirmLabel="Delete"
          onCancel={() => setPendingBudgetDelete(null)}
          onConfirm={() => deleteBudgetItem(pendingBudgetDelete)}
          loading={ownerEnhancementBusy}
        />
      )}

      {pendingImprovementDelete && (
        <ConfirmModal
          tag="Delete improvement"
          title="Delete this improvement?"
          message={`${pendingImprovementDelete.name || "This improvement"} will be removed from the improvement tracker and sale export.`}
          confirmLabel="Delete"
          onCancel={() => setPendingImprovementDelete(null)}
          onConfirm={() => deleteImprovement(pendingImprovementDelete)}
          loading={ownerEnhancementBusy}
        />
      )}

      {pendingContractorDelete && (
        <ConfirmModal
          tag="Delete contractor"
          title="Delete this contractor?"
          message={`${pendingContractorDelete.name || "This contractor"} will be removed from trusted contractors and the home sale resume.`}
          confirmLabel="Delete"
          onCancel={() => setPendingContractorDelete(null)}
          onConfirm={() => deleteContractor(pendingContractorDelete)}
          loading={ownerEnhancementBusy}
        />
      )}

      {!showForm && !mobileSheet && !vaultAiResultSheetOpen && !renterGuideSheet && !showLeaseSheet && !showInsuranceSheet && !showMoveInSheet && !showBudgetSheet && !showImprovementSheet && !showContractorSheet && !pendingDelete && !pendingBudgetDelete && !pendingImprovementDelete && !pendingContractorDelete && <HomeVaultAskBar />}
    </div>
  );
}
